/**
 * FI Workspace — Unified Financial Institution Operating System v2.0
 * Route: /fi/workspace
 *
 * 9-module lifecycle architecture: Origination → Eligibility → Credit → Portfolio
 *                                  → Climate Risk → Carbon Desk → Reporting → Strategic
 *
 * Design principle: One module, one clear question. No duplication across modules.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import client from "@/api/client";
import { toast } from "sonner";
import OnboardingOverlay from "@/components/OnboardingOverlay";
import { FINoDealsEmptyState } from "@/components/SmartEmptyState";
import * as XLSX from "xlsx";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid
} from "recharts";
import {
  Building2, BarChart2, Shield, GitBranch, DollarSign, Activity,
  RefreshCw, ChevronRight, ArrowRight, CheckCircle2, AlertCircle,
  Layers, Target, TrendingDown, Zap, FileText, Globe,
  LayoutDashboard, Users, Search, SlidersHorizontal, Info,
  Clock, AlertTriangle, PlusCircle, Briefcase, BookOpen,
  Upload, Download, X, Check, ChevronDown
} from "lucide-react";

import FIIncomingRequests from "./FIIncomingRequests";
import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
import { FI_MODULES } from "./fiModules";

const BRAND = "#0F172A";
const TEAL  = "#059669";

const SIGNAL_COLOR = {
  high_priority: { bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500" },
  medium:        { bg: "bg-amber-100",   text: "text-amber-700",   dot: "bg-amber-500"   },
  low:           { bg: "bg-red-50",      text: "text-red-600",     dot: "bg-red-400"     },
};

const STATUS_COLOR = {
  submitted:    "bg-blue-100 text-blue-700",
  under_review: "bg-amber-100 text-amber-700",
  qualified:    "bg-emerald-100 text-emerald-700",
  approved:     "bg-green-100 text-green-700",
  declined:     "bg-red-100 text-red-600",
};

// ── Shared Carbon/Sector Data (used across Dashboard, VCM, Carbon, Strategic) ─
const SECTOR_CARBON = {
  energy:       { tco2e:12000, vcm:"Yes", art6:"Potential",   methodology:"ACM0002, AMS-I.D",     risk:"medium" },
  biogas:       { tco2e:8000,  vcm:"Yes", art6:"Potential",   methodology:"AMS-III.R, Gold Std",   risk:"low"    },
  cement:       { tco2e:15000, vcm:"Possible",art6:"Conditional",methodology:"CDM/VCS fuel switch",risk:"high"   },
  waste:        { tco2e:6000,  vcm:"Yes", art6:"Emerging",    methodology:"AMS-III.G, ACM0022",    risk:"medium" },
  agriculture:  { tco2e:5000,  vcm:"Yes", art6:"Conditional", methodology:"AMS-III.F, VM0017",     risk:"medium" },
  transport:    { tco2e:4000,  vcm:"Possible",art6:"Conditional",methodology:"AMS-III.C",          risk:"high"   },
  manufacturing:{ tco2e:10000, vcm:"Possible",art6:"Conditional",methodology:"CDM SCopE",          risk:"high"   },
};

// ── Shared deal parsers (module-level, used across multiple sections) ──────────
const _parseName  = d => { const n=(d.notes||"").split("\n")[0]; const b=n.split(/\s[—\-]\s/)[0].trim(); return b.length>1&&b.length<55?b:d.deal_id; };
const _parseScore = d => { const m=(d.notes||"").match(/Decision:.*?score[\s:]*(\d+)\/100/i)||(d.notes||"").match(/\bscore[\s:]+(\d+)\/100/i); return m?parseInt(m[1]):null; };
const _parseElig  = d => { const n=(d.notes||"").toLowerCase(); return n.includes("gate 0: eligible")&&!n.includes("cond")?"eligible":n.includes("conditionally")?"conditional":n.includes("blocked")?"blocked":null; };
const _parseSize  = d => { const m=(d.notes||"").match(/USD[\s]*([\d,]+)/i); if(!m)return null; const v=parseFloat(m[1].replace(/,/g,"")); return v>=1e6?`$${(v/1e6).toFixed(1)}M`:v>=1e3?`$${(v/1e3).toFixed(0)}K`:null; };
const _parseDSCR  = d => { const m=(d.notes||"").match(/DSCR[\s:]*([\d.]+)x/i); return m?parseFloat(m[1]):null; };
const _parseCountry = d => { const m=/\(([A-Z]{2})[-,\s\)]/i.exec(d.notes||""); return m&&/^(NG|GH|KE|ZA|ET|SN|SL)$/.test(m[1].toUpperCase())?m[1].toUpperCase():"NG"; };

// ── Decision language (institutional, not technical) ──────────────────────────
const DEC_LANG = {
  screening:        { status:"Eligibility Not Assessed",    action:"Review Eligibility",    section:"eligibility", module:"eligibility", dot:"bg-red-400"     },
  data_request:     { status:"Missing Financial Data",      action:"Request Data",           section:"data_import", module:"credit",       dot:"bg-amber-400"   },
  due_diligence:    { status:"Pending Credit Review",        action:"Prepare Memo",           section:"credit_memo", module:"credit",       dot:"bg-amber-400"   },
  credit_committee: { status:"Ready for Credit Committee",  action:"Submit to Committee",    section:"credit_memo", module:"credit",       dot:"bg-emerald-400" },
  term_sheet:       { status:"Awaiting Term Finalisation",  action:"Negotiate Terms",        section:"financing",   module:"credit",       dot:"bg-teal-400"    },
  financial_close:  { status:"Closed",                      action:"Monitor",                section:"monitoring",  module:"portfolio",    dot:"bg-emerald-500" },
};


// ── Shared Section Layout Component ──────────────────────────────────────
function SectionFrame({ icon: Icon, title, desc, children }) {
  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 bg-teal-50 border border-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon size={16} className="text-teal-600" />
        </div>
        <div>
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          {desc && <p className="text-xs text-slate-400 mt-0.5">{desc}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

// ── MODULE 1: Executive Dashboard ────────────────────────────────────────
function SectionExecutiveDashboard({ onNav }) {
  const [deals,     setDeals]     = useState([]);
  const [stats,     setStats]     = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [velocity,  setVelocity]  = useState(null);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([
      client.get("/fi/decisions/deals"),
      client.get("/fi/workspace/pipeline-stats"),
      client.get("/fi/workspace/portfolio-exposure").catch(() => ({ data: null })),
      client.get("/fi/decisions/pipeline-velocity").catch(() => ({ data: null })),
    ]).then(([dr, sr, pr, vr]) => {
      setDeals(dr.data.deals || []);
      setStats(sr.data);
      setPortfolio(pr.data);
      setVelocity(vr.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // ── Derived data ───────────────────────────────────────────────────────────
  const activeDeals  = deals.filter(d => !["rejected","financial_close"].includes(d.status));
  const nonRejected  = deals.filter(d => d.status !== "rejected");
  const totalVal     = nonRejected.reduce((s, d) => s + (d.deployed_amount_usd || 0), 0);
  const deployed     = deals.filter(d => d.status === "financial_close")
                            .reduce((s, d) => s + (d.deployed_amount_usd || 0), 0);
  const notScored    = activeDeals.filter(d => _parseScore(d) === null).length;
  const scores       = deals.map(_parseScore).filter(s => s !== null);
  const avgScore     = scores.length ? Math.round(scores.reduce((a,b)=>a+b,0)/scores.length) : null;
  const lowestScore  = scores.length ? Math.min(...scores) : null;
  const lowestDeal   = lowestScore !== null ? deals.find(d => _parseScore(d) === lowestScore) : null;
  const belowThresh  = scores.filter(s => s < 45).length;

  // Urgency-sorted decision queue
  const STAGE_URGENCY = {
    credit_committee: 1, term_sheet: 2, due_diligence: 3, data_request: 4, screening: 5,
  };
  const needsAction = deals
    .filter(d => Object.keys(DEC_LANG).includes(d.status) && d.status !== "financial_close")
    .sort((a, b) => (STAGE_URGENCY[a.status] || 9) - (STAGE_URGENCY[b.status] || 9));

  // Sector data (consistent with activeDeals)
  const sectorMap = {};
  activeDeals.forEach(d => { sectorMap[d.sector||"Unknown"]=(sectorMap[d.sector||"Unknown"]||0)+1; });
  const sBase = Math.max(1, Object.values(sectorMap).reduce((a,b)=>a+b,0));
  const topSector = Object.entries(sectorMap).sort((a,b)=>b[1]-a[1])[0];

  // Country map (for risk flags)
  const countryMap = {};
  nonRejected.forEach(d => { const c=_parseCountry(d); countryMap[c]=(countryMap[c]||0)+1; });
  const cBase = Math.max(1, Object.values(countryMap).reduce((a,b)=>a+b,0));

  // Carbon metrics — label clearly, use nonRejected for full portfolio view
  const totalTco2e   = nonRejected.reduce((s,d) => s + (SECTOR_CARBON[d.sector]?.tco2e || 5000), 0);
  const vcmEligible  = nonRejected.filter(d => SECTOR_CARBON[d.sector]?.vcm === "Yes");
  const carbPotential = vcmEligible.reduce((s,d) => s + (SECTOR_CARBON[d.sector]?.tco2e || 5000), 0);

  // Top risks
  const RISKS = [];
  if (topSector && Math.round(topSector[1]/sBase*100) > 40)
    RISKS.push({ sev:"H", label:"Sector Concentration", detail:`${topSector[0]} at ${Math.round(topSector[1]/sBase*100)}% — above 40% threshold` });
  if (Object.entries(countryMap).some(([,c]) => Math.round(c/cBase*100) > 50))
    RISKS.push({ sev:"H", label:"Country Concentration", detail:"Nigeria ≥ 50% — single-country exposure" });
  if (notScored > 0)
    RISKS.push({ sev:"M", label:"Credit Assessment Gap", detail:`${notScored} active deal${notScored>1?"s":""} without Signal Score` });
  if (RISKS.length === 0)
    RISKS.push({ sev:"L", label:"No Active Risk Flags", detail:"Portfolio within concentration and quality thresholds" });
  const SEV_COLOR = { H:"text-red-500", M:"text-amber-500", L:"text-emerald-500" };
  const SEV_BG    = { H:"bg-red-50", M:"bg-amber-50", L:"bg-emerald-50" };

  // Next Best Actions — deal-specific, sorted by urgency
  const committeeDeals = needsAction.filter(d => d.status === "credit_committee");
  const screeningDeals = needsAction.filter(d => d.status === "screening");
  const dataDeals      = needsAction.filter(d => d.status === "data_request");
  const unscoredActive = activeDeals.filter(d => _parseScore(d) === null && !["screening"].includes(d.status));

  const NEXT_ACTIONS = [];
  if (committeeDeals.length > 0) NEXT_ACTIONS.push({
    label: `Prepare Credit Memo — ${_parseName(committeeDeals[0]).split(" ").slice(0,3).join(" ")}`,
    sub: `${committeeDeals.length} deal${committeeDeals.length>1?"s":""} ready for credit committee vote`,
    action: "Open Credit Memo", mod:"credit", sec:"credit_memo",
    bg:"bg-emerald-50 border-emerald-200", color:"text-emerald-700",
  });
  if (unscoredActive.length > 0) NEXT_ACTIONS.push({
    label: `Run Signal Score — ${unscoredActive.length} deal${unscoredActive.length>1?"s":""} unassessed`,
    sub: "Credit quality cannot be confirmed without a scored assessment",
    action: "Run Signal Score", mod:"eligibility", sec:"signal_score",
    bg:"bg-amber-50 border-amber-200", color:"text-amber-700",
  });
  if (screeningDeals.length > 0) NEXT_ACTIONS.push({
    label: `Start Eligibility Assessment — ${screeningDeals.map(d=>_parseName(d).split(" ")[0]).join(", ")}`,
    sub: `${screeningDeals.length} deal${screeningDeals.length>1?"s":""} not through eligibility check`,
    action: "Open Eligibility", mod:"eligibility", sec:"eligibility",
    bg:"bg-red-50 border-red-200", color:"text-red-700",
  });
  if (dataDeals.length > 0) NEXT_ACTIONS.push({
    label: `Request Counterparty Data — ${dataDeals.length} deal${dataDeals.length>1?"s":""} awaiting submission`,
    sub: "Financial and climate metrics cannot be verified without borrower data",
    action: "Data Upload", mod:"origination", sec:"data_import",
    bg:"bg-slate-50 border-slate-200", color:"text-slate-700",
  });

  // Portfolio Insight summary sentence
  const insightParts = [];
  if (committeeDeals.length) insightParts.push(`${committeeDeals.length} ready for committee`);
  if (dataDeals.length)      insightParts.push(`${dataDeals.length} awaiting data`);
  if (unscoredActive.length) insightParts.push(`${unscoredActive.length} unscored`);
  if (screeningDeals.length) insightParts.push(`${screeningDeals.length} not yet screened`);
  const insightText = needsAction.length > 0
    ? `${needsAction.length} deal${needsAction.length!==1?"s":""} require decision — ${insightParts.join(", ")}`
    : "No immediate decisions required. Pipeline is progressing normally.";

  if (loading) return <div className="flex items-center justify-center py-20"><RefreshCw size={16} className="animate-spin text-teal-500"/></div>;

  return (
    <div className="space-y-5" data-testid="fi-executive-dashboard">
      {/* ── Header ── */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Climate & Transition Finance Dashboard</h1>
          <p className="text-xs text-slate-400 mt-0.5">Keystone Bank · Climate & Transition Finance Portfolio</p>
        </div>
        <p className="text-xs text-slate-400">{new Date().toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})}</p>
      </div>

      {/* ── 3. KPI Strip — Decisions Required is clickable ── */}
      <div className="grid grid-cols-5 gap-3">
        {/* Pipeline Value */}
        <div className="bg-white border border-slate-200 rounded-xl p-3.5">
          <p className="text-xs text-slate-400 mb-1">Pipeline Value</p>
          <p className="text-xl font-bold tabular-nums text-slate-900">${(totalVal/1e6).toFixed(1)}M</p>
          <p className="text-xs text-slate-400 mt-1">{nonRejected.length} deals, indicative</p>
        </div>

        {/* Capital Deployed */}
        <div className="bg-white border border-slate-200 rounded-xl p-3.5">
          <p className="text-xs text-slate-400 mb-1">Capital Deployed</p>
          <p className="text-xl font-bold tabular-nums text-teal-600">${(deployed/1e6).toFixed(1)}M</p>
          <p className="text-xs text-slate-400 mt-1">{totalVal>0?`${Math.round(deployed/totalVal*100)}% utilisation`:"—"}</p>
        </div>

        {/* Decisions Required — CLICKABLE CARD */}
        <div
          onClick={() => onNav("origination","pipeline")}
          className="bg-white border border-amber-200 rounded-xl p-3.5 cursor-pointer hover:border-amber-400 hover:shadow-sm transition-all group"
          data-testid="decisions-required-card">
          <p className="text-xs text-slate-400 mb-1">Decisions Required</p>
          <p className="text-xl font-bold tabular-nums text-amber-600">{needsAction.length}</p>
          <p className="text-xs text-slate-400 mt-1">deals in queue</p>
          <p className="text-[10px] text-amber-600 group-hover:text-amber-800 mt-2 font-semibold flex items-center gap-0.5">
            Review Decision Queue <ArrowRight size={9}/>
          </p>
        </div>

        {/* Active Deals */}
        <div className="bg-white border border-slate-200 rounded-xl p-3.5">
          <p className="text-xs text-slate-400 mb-1">Total Deals</p>
          <p className="text-xl font-bold tabular-nums text-teal-600">{nonRejected.length}</p>
          <p className="text-xs text-slate-400 mt-1">{activeDeals.length} in-flight · {nonRejected.length - activeDeals.length} deployed</p>
        </div>

        {/* Deals Below Threshold (replaces Avg Signal — more decision-useful) */}
        <div className="bg-white border border-slate-200 rounded-xl p-3.5" data-testid="below-threshold-card">
          <p className="text-xs text-slate-400 mb-1">Below Threshold</p>
          <p className={`text-xl font-bold tabular-nums leading-none ${
            belowThresh > 0 ? "text-red-600" : "text-emerald-600"
          }`}>{belowThresh}</p>
          <p className="text-xs text-slate-400 mt-1">
            {belowThresh > 0 ? `deal${belowThresh>1?"s":""} with signal < 45` : "all deals above threshold"}
          </p>
          {avgScore !== null && (
            <p className="text-[10px] text-slate-400 mt-1 tabular-nums">Avg: {avgScore}/100</p>
          )}
        </div>
      </div>

      {/* ── 4. Carbon Portfolio — label clarified ── */}
      <div className="bg-[#0F172A] rounded-xl p-4">
        <p className="text-[10px] text-teal-400 font-bold uppercase tracking-[0.15em] mb-3">
          Climate & Carbon Portfolio — Indicative Pre-Verification ({nonRejected.length} deals including 1 closed)
        </p>
        <div className="grid grid-cols-5 gap-4">
          {[
            { label:"Emissions Reduction Potential",  value:`${(totalTco2e/1000).toFixed(0)}K tCO₂e/yr`, sub:"All non-rejected deals",       color:"text-white"       },
            { label:"Carbon Credit Potential",         value:`${(carbPotential/1000).toFixed(0)}K tCO₂e/yr`, sub:`${vcmEligible.length} VCM-eligible`,color:"text-teal-300" },
            { label:"Carbon Value — Low ($8/t)",       value:`$${(carbPotential*8/1000).toFixed(0)}K/yr`,  sub:"Indicative scenario",         color:"text-slate-300"   },
            { label:"Carbon Value — Base ($15/t)",     value:`$${(carbPotential*15/1000).toFixed(0)}K/yr`,  sub:"Indicative scenario",         color:"text-emerald-400" },
            { label:"Carbon Value — High ($35/t)",     value:`$${(carbPotential*35/1000).toFixed(0)}K/yr`,  sub:"Indicative upside",           color:"text-emerald-300" },
          ].map(m => (
            <div key={m.label}>
              <p className="text-[10px] text-slate-500 mb-1">{m.label}</p>
              <p className={`text-sm font-bold tabular-nums ${m.color}`}>{m.value}</p>
              <p className="text-[10px] text-slate-600 mt-0.5">{m.sub}</p>
            </div>
          ))}
        </div>
        <p className="text-[9px] text-slate-600 mt-3 flex items-center gap-1">
          <AlertTriangle size={8}/> Carbon revenue estimates are indicative — not bankable until MRV verified, registry linked, issuance pathway confirmed, buyer integrated.
        </p>
      </div>

      {/* ── 5. Decision Queue (urgency sorted) + Capital Gauge ── */}
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 bg-[#0F172A] rounded-xl overflow-hidden">
          <div className="px-5 pt-4 pb-2 flex items-center gap-3">
            <p className="text-[10px] text-slate-500 uppercase tracking-[0.15em] font-bold">Decision Queue</p>
            <span className="text-[10px] bg-amber-500/20 text-amber-300 font-bold px-2 py-0.5 rounded">{needsAction.length} pending</span>
            <span className="text-[10px] text-slate-600 ml-auto">sorted by urgency</span>
          </div>
          {needsAction.length === 0 ? (
            <p className="px-5 pb-5 text-xs text-slate-500 italic">No deals require immediate action.</p>
          ) : (
            <>
              {/* FIXED financial grid — locked column widths, no auto-sizing */}
              <div className="px-5 pb-1 grid items-center" style={{gridTemplateColumns:"140px 1fr 80px 100px 160px", gap:"0 8px"}}>
                <span className="text-[9px] text-slate-600 uppercase tracking-widest font-bold">Status</span>
                <span className="text-[9px] text-slate-600 uppercase tracking-widest font-bold pl-1">Deal</span>
                <span className="text-[9px] text-slate-600 uppercase tracking-widest font-bold text-right">Signal</span>
                <span className="text-[9px] text-slate-600 uppercase tracking-widest font-bold text-right">Size</span>
                <span className="text-[9px] text-slate-600 uppercase tracking-widest font-bold text-right">Action</span>
              </div>
              {needsAction.slice(0,5).map((deal, idx) => {
                const lang  = DEC_LANG[deal.status];
                const sc    = _parseScore(deal);
                const size  = _parseSize(deal);
                const stMap = { credit_committee:"Ready", term_sheet:"In Progress", due_diligence:"In Progress", data_request:"Awaiting Information", screening:"Awaiting Information" };
                const st    = stMap[deal.status] || "Pending";
                const STATUS_CLS = {
                  Ready:                "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40",
                  "In Progress":        "bg-teal-500/20 text-teal-300 border border-teal-500/40",
                  "Awaiting Information":"bg-amber-500/20 text-amber-300 border border-amber-500/30",
                };
                return (
                  <div key={deal.deal_id}
                    className={`px-5 py-2.5 items-center ${idx<Math.min(needsAction.length,5)-1?"border-b border-white/5":""}`}
                    style={{display:"grid", gridTemplateColumns:"140px 1fr 80px 100px 160px", gap:"0 8px"}}
                    data-testid={`queue-row-${deal.deal_id}`}>
                    {/* Status — fixed 140px, truncate if needed */}
                    <div style={{width:"140px", overflow:"hidden"}}>
                      <span className={`text-[9px] font-bold px-2 py-1 rounded block whitespace-nowrap overflow-hidden text-center truncate ${STATUS_CLS[st]}`}
                        style={{maxWidth:"138px"}}>{st}</span>
                    </div>
                    {/* Deal — flex within bounds, protected with overflow */}
                    <div className="min-w-0 pl-1">
                      <p className="text-xs font-semibold text-white truncate leading-tight">{_parseName(deal)}</p>
                      <p className="text-[10px] text-slate-500 truncate">{deal.stage_label || deal.status.replace(/_/g," ")}</p>
                    </div>
                    {/* Signal — 80px, right-aligned, tabular nums */}
                    <p className={`text-xs font-bold tabular-nums text-right ${sc?sc>=65?"text-emerald-400":sc>=45?"text-amber-400":"text-red-400":"text-slate-600"}`}
                      style={{width:"80px"}}>{sc||"—"}</p>
                    {/* Size — 100px, right-aligned, no wrap */}
                    <p className="text-xs font-bold tabular-nums text-right text-slate-300 whitespace-nowrap"
                      style={{width:"100px"}}>{size||"—"}</p>
                    {/* Action — 160px, right-aligned, isolated, no wrap */}
                    {lang ? (
                      <button onClick={() => onNav(lang.module, lang.section)}
                        className="text-[10px] font-bold text-teal-400 hover:text-white transition-colors flex items-center justify-end gap-1 whitespace-nowrap"
                        style={{width:"160px", overflow:"hidden"}}
                        data-testid={`decision-action-${deal.deal_id}`}>
                        <span className="truncate">{lang.action}</span><ArrowRight size={9} className="flex-shrink-0"/>
                      </button>
                    ) : <span style={{width:"160px"}}/>}
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Capital Gauge */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col">
          <p className="text-[10px] text-slate-400 uppercase tracking-[0.12em] font-bold mb-4">Capital Deployment</p>
          <div className="flex-1 flex flex-col justify-center space-y-3">
            {[
              { label:"Deployed",  val:deployed,       color:"bg-teal-500"  },
              { label:"Pipeline",  val:totalVal-deployed, color:"bg-slate-200" },
            ].map(g => (
              <div key={g.label}>
                <div className="flex justify-between text-xs mb-1.5"><span className="text-slate-500">{g.label}</span><span className="font-bold text-slate-900">${(g.val/1e6).toFixed(1)}M</span></div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full ${g.color} rounded-full`} style={{width:`${totalVal>0?Math.min(g.val/totalVal*100,100):0}%`}}/></div>
              </div>
            ))}
            <div className="pt-2 border-t border-slate-100 grid grid-cols-2 gap-1 text-xs">
              <span className="text-slate-400">Utilisation: <strong className={totalVal>0&&deployed/totalVal>0.5?"text-emerald-600":"text-amber-600"}>{totalVal>0?`${Math.round(deployed/totalVal*100)}%`:"—"}</strong></span>
              <span className="text-slate-400">Pending: <strong>${((totalVal-deployed)/1e6).toFixed(1)}M</strong></span>
            </div>
          </div>
        </div>
      </div>

      {/* ── 6. Sector Exposure + Country Exposure + Top Risks ── */}
      <div className="grid grid-cols-3 gap-4">

        {/* Sector Exposure (replaces Deal Stage Distribution) */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[10px] text-slate-400 uppercase tracking-[0.12em] font-bold mb-3">Sector Exposure</p>          <div className="space-y-2.5">
            {Object.entries(sectorMap).sort((a,b)=>b[1]-a[1]).map(([sec, cnt]) => {
              const pct  = Math.round(cnt/sBase*100);
              const risk = SECTOR_CARBON[sec]?.risk || "medium";
              const alert = pct > 40;
              return (
                <div key={sec}>
                  <div className="flex justify-between text-xs mb-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-600 capitalize">{sec}</span>
                      <span
                        title={`Transition Risk: ${risk==="high"?"High — significant exposure to carbon pricing, technology disruption, and demand shifts":risk==="medium"?"Medium — moderate transition exposure; policy changes may affect viability":"Low — limited transition risk under climate policy scenarios"}`}
                        className={`text-[9px] px-1.5 py-0.5 rounded font-bold leading-none cursor-help ${
                          risk==="high"?"bg-red-100 text-red-600":risk==="medium"?"bg-amber-100 text-amber-600":"bg-emerald-100 text-emerald-600"
                        }`}>
                        {risk==="high"?"High TR":risk==="medium"?"Med TR":"Low TR"}
                      </span>
                    </div>
                    <span className={`font-bold ${alert?"text-amber-600":"text-slate-900"}`}>{pct}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${alert?"bg-amber-400":"bg-teal-500"}`} style={{width:`${pct}%`}}/>
                  </div>
                </div>
              );
            })}
          </div>
          {topSector && Math.round(topSector[1]/sBase*100) > 40 && (
            <p className="text-[9px] text-amber-600 mt-2 flex items-center gap-1">
              <AlertTriangle size={9}/> {topSector[0]} exceeds 40% concentration limit
            </p>
          )}
          <p className="text-[9px] text-slate-400 mt-2">TR = Transition Risk classification. Hover badge for explanation.</p>
        </div>

        {/* Country Exposure */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[10px] text-slate-400 uppercase tracking-[0.12em] font-bold mb-3">Country Exposure</p>
          <div className="space-y-2.5">
            {Object.entries(countryMap).sort((a,b)=>b[1]-a[1]).map(([country, cnt]) => {
              const pct   = Math.round(cnt/cBase*100);
              const alert = pct > 50;
              const CLABELS = { NG:"Nigeria", GH:"Ghana", KE:"Kenya", ZA:"South Africa", ET:"Ethiopia", SN:"Senegal", SL:"Sierra Leone" };
              return (
                <div key={country}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-600">{CLABELS[country]||country}</span>
                    <div className="flex items-center gap-1">{alert&&<AlertTriangle size={9} className="text-amber-500"/>}<span className={`font-bold ${alert?"text-amber-600":"text-slate-900"}`}>{pct}%</span></div>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${alert?"bg-amber-400":"bg-teal-500"}`} style={{width:`${pct}%`}}/></div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Risk Flags */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[10px] text-slate-400 uppercase tracking-[0.12em] font-bold mb-3">Top Risk Flags</p>
          <div className="space-y-2.5">
            {RISKS.slice(0,3).map((r,i) => (
              <div key={i} className={`flex gap-2.5 p-2.5 rounded-lg ${SEV_BG[r.sev]}`}>
                <div className={`mt-0.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${r.sev==="H"?"bg-red-500":r.sev==="M"?"bg-amber-500":"bg-emerald-500"}`}/>
                <div><p className={`text-[10px] font-bold uppercase tracking-wide ${SEV_COLOR[r.sev]}`}>{r.sev==="H"?"HIGH":r.sev==="M"?"MEDIUM":"LOW"} · {r.label}</p><p className="text-[10px] text-slate-600 mt-0.5">{r.detail}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── 7. Next Best Actions (replaces Quick Access) ── */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Next Best Actions</p>
        {NEXT_ACTIONS.length === 0 ? (
          <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-4 text-center">
            <p className="text-sm font-semibold text-emerald-700">No immediate actions required</p>
            <p className="text-xs text-emerald-600 mt-1">Pipeline is progressing. Review Portfolio Summary for performance monitoring.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {NEXT_ACTIONS.slice(0,4).map((a,i) => (
              <button key={i} onClick={() => onNav(a.mod, a.sec)}
                className={`flex items-start gap-3 px-4 py-3.5 border rounded-xl text-left hover:shadow-sm transition-all ${a.bg}`}>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-bold ${a.color} leading-snug`}>{a.label}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5 leading-relaxed">{a.sub}</p>
                </div>
                <span className={`text-[10px] font-bold ${a.color} whitespace-nowrap flex items-center gap-0.5 mt-0.5 flex-shrink-0`}>
                  {a.action} <ArrowRight size={9}/>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── MODULE 2: Deal Intake — 3 Paths ─────────────────────────────────────
const INTAKE_FIELDS = [
  { key:"borrower_name",                    label:"Borrower Name",                required:true,  type:"text",   example:"Acme Solar Ltd" },
  { key:"sector",                           label:"Sector",                       required:true,  type:"enum",   example:"energy",        values:["energy","biogas","cement","waste","agriculture","transport","manufacturing"] },
  { key:"country",                          label:"Country (2-letter code)",      required:true,  type:"text",   example:"NG"             },
  { key:"facility_amount_usd",              label:"Facility Amount (USD)",        required:true,  type:"number", example:"5000000"        },
  { key:"instrument_type",                  label:"Instrument Type",              required:true,  type:"enum",   example:"green_loan",    values:["green_loan","sll","project_finance","blended_finance","carbon_project"] },
  { key:"use_of_funds",                     label:"Use of Funds",                 required:true,  type:"text",   example:"Renewable energy plant construction" },
  { key:"expected_emissions_reduction_tco2e",label:"Expected Reduction (tCO₂e/yr)", required:false, type:"number", example:"12000"          },
  { key:"tenor_months",                     label:"Tenor (months)",               required:true,  type:"number", example:"84"             },
  { key:"contact_name",                     label:"Contact Name",                 required:true,  type:"text",   example:"John Adewale"   },
  { key:"contact_email",                    label:"Contact Email",                required:false, type:"email",  example:"john@acme.com"  },
];

function SectionDealOrigination() {
  const [intake, setIntake]     = useState(null);  // null | "excel" | "form"
  const [file, setFile]         = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [parsed, setParsed]     = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult]     = useState(null);
  const [opportunities, setOpp] = useState([]);
  const [oppLoading, setOppLoading] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState([]);
  const [form, setForm]         = useState({ name:"", sector:"energy", country:"NG", instrument:"green_loan", amount_usd:"", tenor_years:"7", notes:"" });
  const [saving, setSaving]     = useState(false);

  // Load opportunities on mount and after import
  const loadOpportunities = () => {
    setOppLoading(true);
    client.get("/finance-requests/fi/queue?limit=50")
      .then(r => setOpp(r.data?.requests || []))
      .catch(() => {})
      .finally(() => setOppLoading(false));
  };
  useEffect(() => { loadOpportunities(); }, []);

  // ── Intake status labels (pre-FI acceptance only, no pipeline mixing) ──
  const INTAKE_STATUS_MAP = {
    "submitted":     "Submitted to FI",
    "under_review":  "Ready for Intake Review",
    "qualified":     "Ready for Intake Review",
    "approved":      "Ready for Intake Review",
    "declined":      "Not Suitable",
    "deferred":      "Awaiting Information",
    "incomplete":    "Incomplete Submission",
    "pending":       "Awaiting Information",
  };
  const getIntakeStatus = (s) =>
    INTAKE_STATUS_MAP[s] || "Submitted to FI";
  const INTAKE_STATUS_STYLE = {
    "Submitted to FI":      "bg-blue-50 text-blue-700 border border-blue-200",
    "Ready for Intake Review":"bg-emerald-50 text-emerald-700 border border-emerald-200",
    "Awaiting Information": "bg-amber-50 text-amber-600 border border-amber-200",
    "Incomplete Submission": "bg-red-50 text-red-600 border border-red-200",
    "Not Suitable":          "bg-slate-100 text-slate-500 border border-slate-200",
  };

  // ── Excel template download ───────────────────────────────────────────
  const downloadTemplate = () => {
    const headers = INTAKE_FIELDS.map(f => f.key);
    const examples = INTAKE_FIELDS.map(f => f.example);
    const notes    = INTAKE_FIELDS.map(f => `${f.required?"REQUIRED":"optional"} | ${f.type}${f.values?` | ${f.values.join("/")}`:""}`);
    const ws = XLSX.utils.aoa_to_sheet([headers, examples, notes]);
    ws["!cols"] = headers.map(() => ({ wch: 28 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Deal Intake Template");
    XLSX.writeFile(wb, "DeCarbonIQ_FI_Deal_Intake_Template.xlsx");
    toast.success("Template downloaded.");
  };

  // ── Excel parse ───────────────────────────────────────────────────────
  const parseFile = (f) => {
    setFile(f); setParsed(null); setResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type:"array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval:"" });
        const valid = [], invalid = [];
        rows.forEach((row, i) => {
          const errors = [];
          INTAKE_FIELDS.forEach(f => {
            const val = row[f.key];
            if (f.required && (!val || String(val).trim() === "")) { errors.push(`${f.key} required`); return; }
            if (val && f.type === "number" && isNaN(parseFloat(String(val)))) errors.push(`${f.key} must be a number`);
            if (val && f.type === "enum" && f.values && !f.values.includes(String(val).toLowerCase())) errors.push(`${f.key} must be one of: ${f.values.join(", ")}`);
          });
          if (errors.length === 0) valid.push({ ...row, _row: i+2 });
          else invalid.push({ ...row, _row: i+2, _errors: errors });
        });
        setParsed({ valid, invalid, total: rows.length });
      } catch { toast.error("Could not parse file. Use the downloaded template format."); }
    };
    reader.readAsArrayBuffer(f);
  };

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); const f=e.dataTransfer.files[0]; if(f) parseFile(f); };

  const submitBatch = async () => {
    if (!parsed?.valid?.length) return;
    setSubmitting(true); setResult(null);
    let created = 0, failed = 0;
    for (const row of parsed.valid) {
      try {
        await client.post("/fi/decisions/deals/create", {
          counterparty_name: String(row.borrower_name||"").trim(),
          sector:            String(row.sector||"energy").toLowerCase(),
          country:           String(row.country||"NG").toUpperCase(),
          instrument_type:   String(row.instrument_type||"green_loan"),
          amount_usd:        parseFloat(String(row.facility_amount_usd||0)) || 0,
          tenor_years:       Math.round((parseFloat(String(row.tenor_months||84))||84) / 12),
          notes:             [row.use_of_funds, row.contact_name && `Contact: ${row.contact_name}`].filter(Boolean).join(" · "),
        });
        created++;
      } catch { failed++; }
    }
    setResult({ created, failed, invalid: parsed.invalid.length });
    setSubmitting(false);
    if (created > 0) {
      toast.success(`${created} deal${created>1?"s":""} created — Pending Screening`);
      loadOpportunities();
    }
    if (failed > 0) toast.error(`${failed} failed to create`);
    setParsed(null); setFile(null);
  };

  const importSelected = async () => {
    if (!selectedOpp.length) return;
    setSaving(true);
    let created = 0;
    for (const req of selectedOpp) {
      try {
        await client.post("/fi/decisions/deals/create", {
          counterparty_name: req.org_name || req.request_id,
          sector:            req.sector || "energy",
          country:           "NG",
          instrument_type:   req.instrument_type || "green_loan",
          amount_usd:        req.amount_requested || 0,
          tenor_years:       7,
          notes:             `Imported from ${req.workspace||"platform"} — ${req.request_id}. ${req.purpose||""}`.trim(),
        });
        created++;
      } catch {}
    }
    toast.success(`${created} deal${created>1?"s":""} brought into FI pipeline`);
    setSelectedOpp([]);
    setSaving(false);
    loadOpportunities();
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error("Counterparty / project name is required."); return; }
    setSaving(true);
    try {
      await client.post("/fi/decisions/deals/create", {
        counterparty_name: form.name, sector: form.sector, country: form.country,
        instrument_type: form.instrument, amount_usd: parseFloat(form.amount_usd)||0,
        tenor_years: parseInt(form.tenor_years)||7, notes: form.notes,
      });
      toast.success(`${form.name} added to pipeline — Pending Screening`);
      setForm({ name:"", sector:"energy", country:"NG", instrument:"green_loan", amount_usd:"", tenor_years:"7", notes:"" });
      setIntake(null);
    } catch { toast.error("Failed to create deal."); }
    setSaving(false);
  };

  return (
    <div className="space-y-5" data-testid="fi-deal-origination">

      {/* ── Header ── */}
      <div>
        <h2 className="text-base font-bold text-slate-900">Deal Origination</h2>
        <p className="text-xs text-slate-500 mt-0.5">Bring opportunities into the FI assessment pipeline. All paths create deals at Pending Screening.</p>
      </div>

      {/* ── Lifecycle indicator ── */}
      <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl">
        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex-shrink-0">Lifecycle:</p>
        {["Origination", "Pending Screening", "Eligibility & Screening", "Credit Engine"].map((s, i) => (
          <React.Fragment key={s}>
            {i > 0 && <ArrowRight size={9} className="text-slate-300 flex-shrink-0"/>}
            <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold whitespace-nowrap flex-shrink-0 ${
              i === 0 ? "text-teal-700 bg-teal-50 border-teal-200 ring-1 ring-teal-400" :
              i === 1 ? "text-amber-700 bg-amber-50 border-amber-200" :
              "text-slate-400 bg-slate-50 border-slate-200"
            }`}>{s}</span>
          </React.Fragment>
        ))}
      </div>

      {/* ── 1. Deal Intake — 3 paths (PRIMARY) ── */}
      <div>
        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2">Deal Intake</p>
        <div className="grid grid-cols-3 gap-3">
          {[
            { key:"platform", label:"Import from Platform", icon:Download,  badge:"5 available", desc:"Opportunities from SME, Project, Corporate and State workspaces" },
            { key:"excel",    label:"Upload Excel",         icon:Upload,     badge:"Bulk",        desc:"External customers, prospects and off-platform opportunities" },
            { key:"form",     label:"New Deal / Project",   icon:PlusCircle, badge:"Single",      desc:"RM-originated or direct bank pipeline entries" },
          ].map(p => {
            const Icon   = p.icon;
            const active = intake === p.key || (p.key === "platform" && intake === null);
            return (
              <button key={p.key}
                onClick={() => {
                  if (p.key === "platform") { setIntake(null); }
                  else { setIntake(intake === p.key ? null : p.key); }
                }}
                className={`flex items-start gap-3 px-4 py-3.5 rounded-xl border-2 text-left transition-all ${
                  active ? "border-teal-500 bg-teal-50" : "border-slate-200 bg-white hover:border-slate-300"
                }`}
                data-testid={`origination-path-${p.key}`}>
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${active?"bg-teal-500 text-white":"bg-slate-100 text-slate-400"}`}>
                  <Icon size={14}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className={`text-xs font-bold ${active?"text-teal-900":"text-slate-700"}`}>{p.label}</p>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${active?"bg-teal-200 text-teal-800":"bg-slate-100 text-slate-500"}`}>{p.badge}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 leading-snug">{p.desc}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Excel upload panel — only when excel is selected */}
        {intake === "excel" && (
          <div className="mt-3 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">Upload external deals in bulk. Use the template to ensure correct format.</p>
              <button onClick={downloadTemplate}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-xs font-medium text-slate-600 rounded-xl hover:bg-slate-50"
                data-testid="download-template">
                <Download size={11}/> Download Template
              </button>
            </div>
            {!parsed && (
              <div
                onDragOver={e=>{e.preventDefault();setDragOver(true);}}
                onDragLeave={()=>setDragOver(false)}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${dragOver?"border-teal-400 bg-teal-50":"border-slate-200 bg-slate-50 hover:border-teal-300"}`}
                onClick={()=>document.getElementById("fi-excel-input").click()}
                data-testid="excel-drop-zone">
                <input id="fi-excel-input" type="file" accept=".xlsx,.xls,.csv" className="hidden"
                  onChange={e=>{const f=e.target.files[0]; if(f) parseFile(f); e.target.value="";}}/>
                <Upload size={24} className={`mx-auto mb-2 ${dragOver?"text-teal-500":"text-slate-300"}`}/>
                <p className="text-sm font-semibold text-slate-600">Drag & drop or click to select</p>
                <p className="text-[10px] text-slate-400 mt-1">.xlsx, .xls, .csv — required fields: borrower_name, sector, country, facility_amount_usd, use_of_funds, tenor_months</p>
              </div>
            )}
            {file && !parsed && (
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl">
                <FileText size={13} className="text-teal-500"/>
                <p className="text-xs text-slate-700">{file.name}</p>
                <RefreshCw size={11} className="animate-spin text-teal-500 ml-auto"/>
              </div>
            )}
            {parsed && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  {[["Total",parsed.total,"text-slate-900","bg-slate-50 border-slate-200"],["Valid",parsed.valid.length,"text-emerald-700","bg-emerald-50 border-emerald-200"],["Invalid",parsed.invalid.length,"text-red-600","bg-red-50 border-red-200"]].map(([l,v,tc,bc])=>(
                    <div key={l} className={`border rounded-xl p-3 text-center ${bc}`}>
                      <p className={`text-xl font-bold ${tc}`}>{v}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{l}</p>
                    </div>
                  ))}
                </div>
                {parsed.valid.length > 0 && (
                  <div className="flex items-center gap-3">
                    <button onClick={submitBatch} disabled={submitting}
                      className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 disabled:opacity-40"
                      data-testid="submit-batch-upload">
                      {submitting?<RefreshCw size={11} className="animate-spin"/>:<Upload size={11}/>}
                      {submitting?"Creating...": `Create ${parsed.valid.length} Deal${parsed.valid.length>1?"s":""} → Pending Screening`}
                    </button>
                    <button onClick={()=>{setParsed(null);setFile(null);}} className="text-xs text-slate-400 hover:text-slate-600">Clear</button>
                  </div>
                )}
              </div>
            )}
            {result && (
              <div className={`rounded-xl p-3 border text-xs ${result.failed===0?"bg-emerald-50 border-emerald-200 text-emerald-800":"bg-amber-50 border-amber-200 text-amber-800"}`}>
                {result.created} created · {result.failed} failed · {result.invalid} skipped. All created deals are now at Pending Screening.
              </div>
            )}
          </div>
        )}

        {/* New Deal form — only when form is selected */}
        {intake === "form" && (
          <div className="mt-3 max-w-lg space-y-3">
            {[
              { key:"name",       label:"Counterparty / Project Name", type:"text",   placeholder:"e.g. Acme Solar Ltd" },
              { key:"amount_usd", label:"Indicative Amount (USD)",     type:"number", placeholder:"e.g. 5000000" },
              { key:"tenor_years",label:"Tenor (years)",               type:"number", placeholder:"7" },
            ].map(f => (
              <div key={f.key}>
                <label className="block text-xs font-medium text-slate-600 mb-1">{f.label}</label>
                <input type={f.type} value={form[f.key]} placeholder={f.placeholder}
                  onChange={e=>setForm({...form,[f.key]:e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400"
                  data-testid={`new-deal-${f.key}`}/>
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Sector</label>
                <select value={form.sector} onChange={e=>setForm({...form,sector:e.target.value})} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl">
                  {["energy","cement","biogas","waste","agriculture","transport","manufacturing"].map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Instrument</label>
                <select value={form.instrument} onChange={e=>setForm({...form,instrument:e.target.value})} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl">
                  {["green_loan","sll","project_finance","blended_finance","carbon_project"].map(i=><option key={i} value={i}>{i.replace(/_/g," ")}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Context / Notes</label>
              <textarea value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} rows={2}
                placeholder="Use of proceeds, referral, key details..."
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl resize-none focus:outline-none focus:border-teal-400"/>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={handleSubmit} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700 disabled:opacity-40"
                data-testid="create-deal-submit">
                {saving?<RefreshCw size={13} className="animate-spin"/>:<PlusCircle size={13}/>}
                {saving?"Creating...":"Add to Pipeline → Pending Screening"}
              </button>
              <button onClick={()=>setIntake(null)} className="text-xs text-slate-400 hover:text-slate-600">Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* ── 2. Platform Opportunities (always visible) ── */}
      <div id="platform-opportunities-table">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Platform Opportunities</p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Opportunities from other workspaces awaiting FI intake review.
              Select and import to bring into the FI assessment pipeline.
            </p>
          </div>
          <button onClick={loadOpportunities}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-teal-700">
            <RefreshCw size={11} className={oppLoading?"animate-spin":""}/> Refresh
          </button>
        </div>

        <div className="border border-slate-200 rounded-xl overflow-hidden">
          {oppLoading ? (
            <div className="flex items-center justify-center py-8"><RefreshCw size={14} className="animate-spin text-teal-500"/></div>
          ) : opportunities.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm font-semibold text-slate-400">No opportunities in queue</p>
              <p className="text-xs text-slate-300 mt-1">Submissions from other platform users will appear here for FI review.</p>
            </div>
          ) : (
            <>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-slate-500">
                    <th className="w-8 px-4 py-2.5 text-left">
                      <input type="checkbox"
                        onChange={e=>setSelectedOpp(e.target.checked?opportunities:[])}
                        className="rounded"/>
                    </th>
                    <th className="text-left px-4 py-2.5 font-semibold">Organisation</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Source</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Amount</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Intake Status</th>
                  </tr>
                </thead>
                <tbody>
                  {opportunities.map((req, i) => {
                    const statusLabel = getIntakeStatus(req.status);
                    const statusStyle = INTAKE_STATUS_STYLE[statusLabel] || "bg-slate-100 text-slate-500 border border-slate-200";
                    const hasAmount   = req.amount_requested && req.amount_requested > 0;
                    return (
                      <tr key={req.request_id}
                        className={`border-b border-slate-100 last:border-0 ${i%2===1?"bg-slate-50/30":""}`}>
                        <td className="px-4 py-3">
                          <input type="checkbox"
                            checked={selectedOpp.some(r=>r.request_id===req.request_id)}
                            onChange={e=>setSelectedOpp(e.target.checked?[...selectedOpp,req]:selectedOpp.filter(r=>r.request_id!==req.request_id))}
                            className="rounded"/>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-slate-900">{req.org_name || req.request_id}</p>
                          <p className="text-[10px] text-slate-400 font-mono mt-0.5">{req.request_id}</p>
                        </td>
                        <td className="px-4 py-3 text-slate-500 capitalize">
                          {req.workspace || req.source || "Platform"}
                        </td>
                        <td className={`px-4 py-3 text-right tabular-nums font-medium ${hasAmount?"text-slate-900":"text-slate-400 italic text-[11px]"}`}>
                          {hasAmount ? `$${(req.amount_requested/1e6).toFixed(1)}M` : "To be defined"}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${statusStyle}`}>
                            {statusLabel}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {selectedOpp.length > 0 && (
                <div className="px-5 py-3 bg-teal-50 border-t border-teal-200 flex items-center justify-between">
                  <p className="text-xs text-teal-700 font-medium">{selectedOpp.length} selected</p>
                  <button onClick={importSelected} disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 disabled:opacity-40"
                    data-testid="import-selected">
                    {saving?<RefreshCw size={11} className="animate-spin"/>:<Download size={11}/>}
                    Import → Pending Screening
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionCreditMemo() {
  const [deals, setDeals]   = useState([]);
  const [selected, setSelected] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [memo, setMemo]     = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [form, setForm]     = useState({
    baseline_tco2e:50000, annual_reduction_tco2e:16000, confidence_pct:70,
    amount_usd:8000000, annual_dscr:1.35, sector:"cement", country:"NG",
    org_type:"corporate", instrument_type:"green_loan", credit_risk_tier:"B",
    carbon_annual_tco2e:0, annual_revenue_usd:0, annual_debt_service_usd:0,
    carbon_revenue_share_pct:0, grant_component_pct:0,
    ghg_protocol_compliant:true, taxonomy_eligible:"conditional",
    evidence_coverage_pct:60, periods_tracked:4, periods_locked:2,
    verification_status:"none",
  });

  useEffect(() => {
    client.get("/fi/decisions/deals").then(r => {
      const ds = r.data.deals || [];
      setDeals(ds);
      if (ds.length > 0) setSelected(ds[0]);
    }).catch(() => {});
  }, []);

  // Load compliance gate for selected deal
  useEffect(() => {
    if (!selected?.deal_id) return;
    client.get(`/fi/compliance/assessment/${selected.deal_id}`)
      .then(r => setCompliance(r.data))
      .catch(() => setCompliance(null));
  }, [selected?.deal_id]);

  const generate = async () => {
    if (!selected) return;
    // Compliance gate check
    if (compliance?.compliance_gate === "blocked") {
      try {
        await client.post(`/fi/compliance/log-attempt/${selected.deal_id}`, {
          attempted_action: "generate_credit_memo",
          gate_status: "blocked",
          blocking_rule_ids: compliance.blocking_rules.map(b => b.id),
        });
      } catch (err) { /* silent */ }
      toast.error(`Credit memo generation blocked — ${compliance.blocking_rules.length} compliance rule(s) must be resolved first.`);
      return;
    }
    setGenerating(true); setMemo(null);
    try {
      const res = await client.post(`/fi/decisions/deals/${selected.deal_id}/credit-committee-pack`, { deal_id: selected.deal_id, ...form });
      setMemo(res.data);
      toast.success("Credit Memo generated from deal data.");
    } catch { toast.error("Generation failed — check deal data."); }
    setGenerating(false);
  };

  const exportPDF = async () => {
    if (!selected || !memo) return;
    try {
      toast.info("Generating PDF...");
      const resp = await client.post(`/fi/pdf/credit-committee/${selected.deal_id}`, { deal_id: selected.deal_id, ...form }, { responseType:"blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data],{type:"application/pdf"}));
      const a = document.createElement("a");
      a.href = url; a.download = `CreditMemo_${selected.deal_id}_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success("Credit Memo PDF exported.");
    } catch { toast.error("PDF export failed."); }
  };

  const s1  = memo?.section_1_executive_summary || {};
  const s3  = memo?.section_3_signal_score || {};
  const s4  = memo?.section_4_eligibility || {};
  const s5  = memo?.section_5_risk_analysis || {};
  const s6  = memo?.section_6_pricing || {};
  const s7  = memo?.section_7_carbon_analysis || {};
  const s8  = memo?.section_8_term_sheet || {};
  const s9  = memo?.section_9_decision_trail || {};
  const s11 = memo?.section_11_conditions_precedent || {};
  const s13 = memo?.section_13_recommendation || {};
  const summ = memo?.summary || {};

  const MemoSection = ({ num, title, children }) => (
    <div className="border-b border-slate-100 last:border-0">
      <div className="px-5 py-3 bg-slate-50 flex items-center gap-3 border-b border-slate-100">
        <span className="text-[9px] font-bold text-slate-400 bg-slate-200 px-1.5 py-0.5 rounded font-mono">S{num}</span>
        <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">{title}</p>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );

  return (
    <SectionFrame icon={BookOpen} title="Credit Memo" desc="Auto-generated from structured deal data. Deterministic, auditable, institutional-grade.">
      {/* Compliance gate indicator */}
      {compliance && (
        <div className={`rounded-xl border px-4 py-3 mb-4 flex items-start gap-3 ${
          compliance.compliance_gate === "blocked" ? "border-red-300 bg-red-50" :
          compliance.compliance_gate === "conditional" ? "border-amber-200 bg-amber-50" :
          "border-emerald-200 bg-emerald-50"
        }`} data-testid="credit-memo-compliance-gate">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${
            compliance.compliance_gate === "blocked" ? "bg-red-500" :
            compliance.compliance_gate === "conditional" ? "bg-amber-500" : "bg-emerald-500"
          }`}/>
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-bold ${
              compliance.compliance_gate === "blocked" ? "text-red-700" :
              compliance.compliance_gate === "conditional" ? "text-amber-700" : "text-emerald-700"
            }`}>
              Compliance Gate: {compliance.compliance_gate === "blocked" ? "BLOCKED" :
                compliance.compliance_gate === "conditional" ? "CONDITIONAL" : "CLEAR"} — {compliance.risk_level} Risk
            </p>
            <p className="text-[10px] text-slate-500 mt-0.5">{compliance.gate_label}</p>
            {compliance.compliance_gate === "blocked" && (
              <div className="mt-2 space-y-1">
                {compliance.blocking_rules.map(b => (
                  <p key={b.id} className="text-[10px] text-red-700">
                    <span className="font-bold">{b.framework}: </span>{b.action_required}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Deal selector */}
      <div className="flex items-center gap-3 mb-5">
        <select value={selected?.deal_id || ""}
          onChange={e => { const d=deals.find(x=>x.deal_id===e.target.value)||null; setSelected(d); setMemo(null); }}
          className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none"
          data-testid="credit-memo-deal-select">
          {deals.map(d=><option key={d.deal_id} value={d.deal_id}>{d.deal_id} — {d.stage_label||d.status}</option>)}
        </select>
        <button onClick={generate} disabled={generating||!selected}
          className={`flex items-center gap-2 px-4 py-2 text-white text-sm font-bold rounded-xl disabled:opacity-40 transition-colors whitespace-nowrap ${compliance?.compliance_gate === "blocked" ? "bg-red-600 hover:bg-red-700" : "bg-[#0F172A] hover:bg-slate-800"}`}
          data-testid="generate-credit-memo">
          {generating ? <RefreshCw size={13} className="animate-spin"/> : <BookOpen size={13}/>}
          {generating ? "Generating..." : compliance?.compliance_gate === "blocked" ? "Blocked — Resolve Compliance" : "Generate Memo"}
        </button>
        {memo && (
          <button onClick={exportPDF} className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 text-sm font-medium rounded-xl hover:bg-slate-50 transition-colors whitespace-nowrap"
            data-testid="export-credit-memo-pdf">
            <FileText size={13}/> Export PDF
          </button>
        )}
      </div>

      {!memo && !generating && (
        <div className="flex flex-col items-center justify-center py-16 border border-dashed border-slate-200 rounded-xl bg-slate-50 text-center">
          <BookOpen size={32} className="text-slate-200 mb-3"/>
          <p className="text-sm font-semibold text-slate-500 mb-1">Select a deal and generate</p>
          <p className="text-xs text-slate-400">Credit Memo is auto-generated from structured deal data — no manual upload required.</p>
          <div className="mt-4 grid grid-cols-2 gap-2 text-left max-w-xs">
            {["Signal Score breakdown","Eligibility gate results","Pricing waterfall","Risk assessment","Carbon analysis","Recommendation & audit trail"].map(f=>(
              <div key={f} className="flex items-center gap-1.5 text-xs text-slate-400">
                <CheckCircle2 size={10} className="text-teal-400 flex-shrink-0"/>{f}
              </div>
            ))}
          </div>
        </div>
      )}

      {memo && (
        <div className="border border-slate-200 rounded-xl overflow-hidden text-sm" data-testid="credit-memo-document">

          {/* Cover */}
          <div className="bg-[#0F172A] text-white p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[9px] text-slate-400 uppercase tracking-[0.15em] font-bold mb-1">CREDIT MEMORANDUM · CONFIDENTIAL · DECARBONIQ</p>
                <p className="text-xl font-bold">{s1.counterparty || selected?.deal_id}</p>
                <p className="text-sm text-slate-300 mt-0.5">{s1.instrument||"Green Loan"} · {(s1.sector||"").toUpperCase()} · {s1.country||"NG"}</p>
              </div>
              <div className={`px-3 py-1.5 rounded text-xs font-bold ${summ.recommendation_status==="approve_with_conditions"?"bg-emerald-500/20 text-emerald-300":summ.recommendation_status==="conditional_approve"?"bg-amber-500/20 text-amber-300":"bg-red-500/20 text-red-300"}`}>
                {summ.recommendation?.split(" — ")[0] || "Pending"}
              </div>
            </div>
            <div className="grid grid-cols-5 gap-4 pt-4 border-t border-white/10">
              {[["Facility Amount",s1.finance_amount||"—"],["Proposed Rate",s1.proposed_rate||"—"],["Signal Score",`${summ.signal_score||"—"}/100`],["DSCR Base",`${summ.dscr_base||"—"}x`],["DSCR w/ Carbon",`${summ.dscr_with_carbon||"—"}x`]].map(([l,v])=>(
                <div key={l}><p className="text-[9px] text-slate-400 uppercase tracking-widest mb-1">{l}</p><p className="text-sm font-bold text-white">{v}</p></div>
              ))}
            </div>
          </div>

          {/* S1: Executive Summary */}
          <MemoSection num="1" title="Executive Summary">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {[["Prepared","by "+s1.prepared_by+" on "+s1.prepared_date],["Counterparty",s1.counterparty],["Deal ID",selected?.deal_id],["Instrument",s1.instrument],["Tenor",s1.tenor],["Eligibility",summ.eligibility||"—"]].map(([l,v])=>(
                <div key={l} className="flex gap-2 text-xs py-1 border-b border-slate-50">
                  <span className="text-slate-400 w-24 flex-shrink-0">{l}</span>
                  <span className="font-medium text-slate-900">{v||"—"}</span>
                </div>
              ))}
            </div>
          </MemoSection>

          {/* S3: Signal Score */}
          <MemoSection num="3" title="Signal Score — Component Breakdown">
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[["Total Score",`${s3.total_score||"—"}/100`,s3.signal_label||"—"],["Action",s3.action||"—",""]].map(([l,v,s])=>(
                <div key={l} className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-400 mb-0.5">{l}</p><p className="text-base font-bold text-slate-900">{v}</p>{s&&<p className="text-xs text-slate-500">{s}</p>}</div>
              ))}
            </div>
            {(s3.components||[]).map(c => (
              <div key={c.name} className="flex items-center gap-3 py-1.5 border-b border-slate-50 last:border-0">
                <span className="text-xs text-slate-600 w-32 flex-shrink-0">{c.name}</span>
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${c.pct>=70?"bg-emerald-500":c.pct>=50?"bg-teal-500":c.pct>=30?"bg-amber-400":"bg-red-400"}`} style={{width:`${Math.min(c.pct||0,100)}%`}}/>
                </div>
                <span className="text-xs font-bold text-slate-900 w-12 text-right tabular-nums">{c.score}/{c.max}</span>
                <span className="text-xs text-slate-400 w-8 text-right">{(c.pct||0).toFixed(0)}%</span>
              </div>
            ))}
          </MemoSection>

          {/* S4: Eligibility */}
          <MemoSection num="4" title="Eligibility Review — 4-Gate Framework">
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(s4.gates||{}).map(([gk, g]) => (
                <div key={gk} className={`flex items-start gap-2.5 p-2.5 rounded-lg ${g.advance?"bg-emerald-50":"bg-red-50"}`}>
                  <span className={`mt-0.5 flex-shrink-0 text-sm ${g.advance?"text-emerald-600":"text-red-500"}`}>{g.advance?"✓":"✗"}</span>
                  <div>
                    <p className="text-xs font-bold text-slate-800">
                      {gk === "gate_0" ? "Climate Finance Eligibility Check" :
                       gk === "gate_1" ? "Climate Taxonomy Alignment" :
                       gk === "gate_2" ? "Additionality Assessment" :
                       gk === "gate_3" ? "MRV Readiness Assessment" :
                       gk === "gate_4" ? "Financing Fit Assessment" :
                       (g.gate_label||gk).replace(/Gate\s*0\s*[—\-]*/gi,"Eligibility —").replace(/\bGate\s*\d\b/gi,"Check")}
                    </p>
                    <p className={`text-[10px] font-semibold ${g.advance?"text-emerald-700":"text-red-600"}`}>{g.result||"—"}</p>
                  </div>
                </div>
              ))}
            </div>
          </MemoSection>

          {/* S5: Risk */}
          <MemoSection num="5" title="Risk Analysis">
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(s5).filter(([k])=>k.endsWith("_risk")).map(([cat, data]) => {
                const factors = Array.isArray(data?.factors) ? data.factors.slice(0,2).join(", ") : typeof data==="string" ? data : "";
                return (
                  <div key={cat} className="border border-slate-100 rounded-lg p-3">
                    <p className="text-xs font-bold text-slate-700 mb-1">{cat.replace("_risk","").replace("_"," ").toUpperCase()} RISK</p>
                    <p className="text-xs text-slate-500">{factors||"Review required"}</p>
                  </div>
                );
              })}
            </div>
          </MemoSection>

          {/* S6: Pricing */}
          <MemoSection num="6" title="Pricing Waterfall">
            <div className="space-y-0.5 mb-3">
              {(s6.waterfall||[]).map((item,i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0 text-xs">
                  <span className="text-slate-600">{item.component}</span>
                  <span className={`font-semibold w-14 text-right ${item.adjustment>0?"text-red-500":item.adjustment<0?"text-emerald-600":"text-slate-400"}`}>{item.adjustment>0?"+":""}{item.adjustment?.toFixed(2)}%</span>
                  <span className="font-bold text-slate-900 w-14 text-right tabular-nums">{item.value?.toFixed(2)}%</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between px-3 py-2.5 bg-[#0F172A] rounded-lg">
              <span className="text-xs font-bold text-white">All-in Rate</span>
              <span className="text-base font-bold text-teal-400">{summ.pricing_rate||"—"}%</span>
            </div>
            {s6.rate_note && <p className="text-xs text-slate-400 mt-2 italic">{s6.rate_note}</p>}
          </MemoSection>

          {/* S8: Repayment Structure + DSCR Sensitivity */}
          {(() => {
            const rate = (s8.rate_pct || summ.pricing_rate || 9) / 100;
            const amount = s8.amount_usd || 0;
            const tenor = s8.tenor_years || 7;
            const grace = s8.grace_period_months || 12;
            const amortYears = Math.max(0, tenor - grace / 12);
            const monthlyRate = rate / 12;
            const amortMonths = amortYears * 12;
            const monthlyPayment = (amortMonths > 0 && monthlyRate > 0)
              ? amount * (monthlyRate * Math.pow(1 + monthlyRate, amortMonths)) / (Math.pow(1 + monthlyRate, amortMonths) - 1)
              : 0;
            const annualInterest = amount * rate;
            const annualRepayment = monthlyPayment * 12;
            const annualDebtService = annualRepayment + (grace > 0 ? annualInterest : 0);

            const baseDSCR = summ.dscr_base || 1.2;
            const minCovenant = s8.dscr_covenant || 1.3;
            const dscrScenarios = [
              { label:"Base Case (projected)", dscr: baseDSCR, note:"From financial model" },
              { label:"Downside (–10% revenue)", dscr: parseFloat((baseDSCR * 0.85).toFixed(2)), note:"1.5× operating leverage" },
              { label:"Stress (–20% revenue)", dscr: parseFloat((baseDSCR * 0.70).toFixed(2)), note:"1.5× operating leverage" },
              { label:"Min Covenant", dscr: minCovenant, note:"Breach triggers remediation", isCovenant: true },
            ];
            return (
              <MemoSection num="8" title="Repayment Structure & DSCR Sensitivity">
                <div className="grid grid-cols-2 gap-5">
                  {/* Repayment schedule */}
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-2">Repayment Structure</p>
                    {amount > 0 ? (
                      <div className="space-y-1.5">
                        {[
                          ["Facility Amount", `USD ${amount.toLocaleString()}`],
                          ["Interest Rate", `${s8.rate_pct||summ.pricing_rate||"—"}% p.a. fixed`],
                          ["Tenor", `${tenor} years`],
                          ["Grace Period", `${grace} months interest-only`],
                          ["Amortization Period", `${amortYears.toFixed(1)} years`],
                          ["Annual Interest (IO)", `USD ${Math.round(annualInterest).toLocaleString()}`],
                          ["Annual Debt Service (amort)", `USD ${Math.round(annualRepayment).toLocaleString()}`],
                          ["Repayment Method", "Semi-annual principal + quarterly interest"],
                        ].map(([l,v]) => (
                          <div key={l} className="flex justify-between text-xs py-1 border-b border-slate-50 last:border-0">
                            <span className="text-slate-400">{l}</span>
                            <span className="font-medium text-slate-900">{v}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic">Set facility amount in the pack parameters to compute repayment schedule.</p>
                    )}
                  </div>

                  {/* DSCR sensitivity */}
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-2">DSCR Sensitivity Analysis</p>
                    <div className="space-y-2">
                      {dscrScenarios.map(sc => {
                        const breaches = sc.dscr < minCovenant && !sc.isCovenant;
                        return (
                          <div key={sc.label} className={`rounded-lg px-3 py-2 ${sc.isCovenant?"bg-red-50 border border-red-200":breaches?"bg-amber-50 border border-amber-200":sc.dscr>=1.3?"bg-emerald-50 border border-emerald-100":"bg-slate-50 border border-slate-200"}`}>
                            <div className="flex justify-between items-start gap-2">
                              <span className="text-[10px] text-slate-600 flex-1">{sc.label}</span>
                              <span className={`text-sm font-bold tabular-nums flex-shrink-0 ${sc.isCovenant?"text-red-600":breaches?"text-amber-600":sc.dscr>=1.3?"text-emerald-600":"text-slate-700"}`}>{sc.dscr}x</span>
                            </div>
                            <p className="text-[9px] text-slate-400 mt-0.5">{sc.note}{breaches?" — COVENANT BREACH":""}</p>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[9px] text-slate-400 mt-2 italic">Sensitivity assumes 1.5× operating leverage. Lender stress test required before credit committee.</p>
                  </div>
                </div>

                {/* Cash flow waterfall */}
                <div className="mt-4">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-2">Cash Flow Waterfall (Illustrative)</p>
                  <div className="space-y-0.5">
                    {[
                      { label:"Operating Revenue", note:"Per borrower financial model", color:"text-slate-900", indent:0 },
                      { label:"Operating Costs (excl. D&A)", note:"Estimated ~55–65% of revenue (sector-dependent)", color:"text-red-500", indent:1, prefix:"−" },
                      { label:"EBITDA", note:"", color:"text-teal-700 font-semibold", indent:0, divider:true },
                      { label:"Tax & Duties", note:"Local corporate rate applies", color:"text-red-500", indent:1, prefix:"−" },
                      { label:"Annual Debt Service", note:amount>0?`~USD ${Math.round(annualDebtService).toLocaleString()} (interest + principal)`:"Set amount to compute", color:"text-red-500", indent:1, prefix:"−" },
                      { label:"Free Cash Flow to Equity", note:"Basis for DSCR test — excludes carbon upside", color:"text-emerald-700 font-semibold", indent:0, divider:true },
                      { label:"Carbon Revenue (supplementary)", note:"Indicative — not in base DSCR. Pending MRV verification.", color:"text-amber-600", indent:1, prefix:"+" },
                    ].map((row,i)=>(
                      <div key={i} className={`flex justify-between items-center py-1.5 text-xs ${row.divider?"border-y border-slate-200 bg-slate-50 px-2":""} ${row.indent?"pl-5":""}`}>
                        <span className={row.color}>{row.prefix?<span className="text-slate-400 mr-1">{row.prefix}</span>:null}{row.label}</span>
                        <span className="text-[10px] text-slate-400 text-right ml-4 max-w-[220px]">{row.note}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </MemoSection>
            );
          })()}

          {/* S7: Carbon */}
          {s7.scenarios && (
            <MemoSection num="7" title="Carbon Revenue Analysis">
              <div className="grid grid-cols-3 gap-3 mb-3">
                {Object.entries(s7.scenarios||{}).slice(0,3).map(([label,sc])=>(
                  <div key={label} className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-slate-400 uppercase mb-1">{label}</p>
                    <p className="text-sm font-bold text-teal-600">${(sc.annual_revenue_usd||0).toLocaleString()}/yr</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">DSCR +{(sc.dscr_uplift||0).toFixed(2)}x</p>
                  </div>
                ))}
              </div>
              {s7.interpretation && <p className="text-xs text-slate-500 italic">{s7.interpretation}</p>}
            </MemoSection>
          )}

          {/* S11: Conditions */}
          <MemoSection num="11" title="Conditions Precedent">
            <div className="space-y-1.5">
              {[...(s11.from_eligibility||[]).slice(0,3), ...(s11.standard||[]).slice(0,3)].map((cp,i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <span className="text-slate-400 font-mono flex-shrink-0">{String(i+1).padStart(2,"0")}.</span>
                  <span className="text-slate-600">{cp}</span>
                </div>
              ))}
              {((s11.total_count||0) > 6) && <p className="text-xs text-slate-400 italic">+{(s11.total_count||0)-6} additional conditions — see full PDF</p>}
            </div>
          </MemoSection>

          {/* S12: Covenant Logic (explicit triggers) */}
          <MemoSection num="12" title="Covenant Logic & Trigger Framework">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-2">Financial Covenants</p>
                <div className="space-y-2">
                  {[
                    { covenant:`Min DSCR ≥ ${s8.dscr_covenant||"1.30"}x`, trigger:"Breach → waiver required within 30 days. Sustained breach → rate step-up +1.0%. DSCR < 1.0x → Event of Default." },
                    { covenant:"Max Leverage (Debt/EBITDA)", trigger:"Tested annually. Breach → restrict additional indebtedness. Lender consent required for new facilities." },
                    { covenant:"Dividend Restriction", trigger:"No dividends if DSCR < 1.30x at test date." },
                  ].map(c=>(
                    <div key={c.covenant} className="bg-slate-50 rounded-lg p-2.5">
                      <p className="text-xs font-bold text-slate-800 mb-1">{c.covenant}</p>
                      <p className="text-[10px] text-slate-500">{c.trigger}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-2">Climate & Carbon Covenants</p>
                <div className="space-y-2">
                  {[
                    { covenant:"Climate Score ≥ 60/100", trigger:"Tested annually. Breach → +0.50% rate step-up applies until score restored. Borrower must submit improvement plan within 60 days." },
                    { covenant:"Quarterly MRV Reporting", trigger:"Overdue → formal notice. 30-day cure period. Persistent breach → event of default under reporting covenant." },
                    { covenant:"Annual GHG Inventory", trigger:"Submitted via DeCarbonIQ platform within 120 days of year-end. Failure → restrict drawdown." },
                  ].map(c=>(
                    <div key={c.covenant} className="bg-emerald-50 rounded-lg p-2.5">
                      <p className="text-xs font-bold text-slate-800 mb-1">{c.covenant}</p>
                      <p className="text-[10px] text-slate-500">{c.trigger}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-[10px] font-bold text-red-700 uppercase tracking-wide mb-1">Events of Default</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                {["DSCR < 1.00x (tested at year-end)", "Payment past due > 30 days", "Climate Score < 40/100 for 2+ consecutive periods", "MRV reporting overdue > 90 days", "Change of control without lender consent", "Cross-default on material indebtedness"].map(e => (
                  <p key={e} className="text-[10px] text-red-600 flex items-center gap-1"><span className="flex-shrink-0">→</span>{e}</p>
                ))}
              </div>
            </div>
          </MemoSection>

          {/* S13: Recommendation + Audit */}
          <MemoSection num="13" title="Recommendation & Decision Logic">
            {/* Explicit decision thresholds */}
            <div className="mb-4">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-2">Credit Decision Boundaries — Explicit Logic</p>
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead><tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-3 py-2 text-slate-500 font-semibold">Threshold</th>
                    <th className="text-left px-3 py-2 text-slate-500 font-semibold">Condition</th>
                    <th className="text-left px-3 py-2 text-slate-500 font-semibold">Outcome</th>
                    <th className="text-center px-3 py-2 text-slate-500 font-semibold">This Deal</th>
                  </tr></thead>
                  <tbody>
                    {[
                      { label:"Signal Score",  cond:"< 35/100",         outcome:"Decline",                    triggered: (summ.signal_score||0) < 35 },
                      { label:"Signal Score",  cond:"35–44/100",         outcome:"Defer — Remediation",        triggered: (summ.signal_score||0) >= 35 && (summ.signal_score||0) < 45 },
                      { label:"Signal Score",  cond:"45–64/100",         outcome:"Conditional Approval",       triggered: (summ.signal_score||0) >= 45 && (summ.signal_score||0) < 65 },
                      { label:"Signal Score",  cond:"≥ 65/100",          outcome:"Recommend Approval",         triggered: (summ.signal_score||0) >= 65 },
                      { label:"DSCR",          cond:"< 1.0x",            outcome:"Automatic Decline",          triggered: (summ.dscr_base||0) > 0 && (summ.dscr_base||0) < 1.0 },
                      { label:"DSCR",          cond:"1.0x–1.29x",        outcome:"Downgrade to Conditional",   triggered: (summ.dscr_base||0) >= 1.0 && (summ.dscr_base||0) < 1.3 },
                      { label:"DSCR",          cond:"≥ 1.30x + Score ≥65",outcome:"Recommend Approval",         triggered: (summ.dscr_base||0) >= 1.3 && (summ.signal_score||0) >= 65 },
                      { label:"DSCR Covenant", cond:"Min 1.30x test",      outcome:"Covenant breach if below",   triggered: (summ.dscr_base||0) < 1.3 && (summ.dscr_base||0) > 0 },
                    ].map(t => (
                      <tr key={t.label+t.cond} className={`border-b border-slate-50 last:border-0 ${t.triggered?"bg-amber-50":""}`}>
                        <td className="px-3 py-2 font-medium text-slate-700">{t.label}</td>
                        <td className="px-3 py-2 text-slate-600 font-mono text-[10px]">{t.cond}</td>
                        <td className="px-3 py-2 text-slate-600">{t.outcome}</td>
                        <td className="px-3 py-2 text-center">
                          {t.triggered
                            ? <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded">TRIGGERED</span>
                            : <span className="text-[10px] text-slate-400">—</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recommendation output */}
            <div className={`p-4 rounded-xl mb-4 ${s13.status==="approve_with_conditions"?"bg-emerald-50 border border-emerald-200":s13.status==="conditional_approve"?"bg-amber-50 border border-amber-200":"bg-red-50 border border-red-200"}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-900 mb-2">{s13.label||"—"}</p>
                  <div className="space-y-1">
                    {(s13.proposed_next_actions||[]).map((a,i)=><p key={i} className="text-xs text-slate-600">→ {a}</p>)}
                  </div>
                </div>
                <div className="text-right ml-4 flex-shrink-0">
                  <p className="text-[10px] text-slate-400 mb-0.5">Driven by</p>
                  <p className="text-xs font-bold text-slate-700">Score {summ.signal_score||"—"}/100</p>
                  <p className="text-xs font-bold text-slate-700">DSCR {summ.dscr_base||"—"}x</p>
                </div>
              </div>
            </div>

            {/* Full audit trail */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wide mb-3">Audit Trail & Assumptions Snapshot</p>
              <div className="grid grid-cols-2 gap-3 text-xs mb-3">
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Document Metadata</p>
                  <div className="space-y-1">
                    <div className="flex justify-between"><span className="text-slate-500">Generated by:</span><span className="font-medium">{s1.prepared_by||"—"}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Generated at:</span><span className="font-medium">{s1.prepared_date||"—"}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Pack schema:</span><span className="font-medium font-mono">{memo?.schema||"v1"}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Deal ID:</span><span className="font-medium font-mono">{selected?.deal_id}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Methodology:</span><span className="font-medium">Signal Score v2.0 | Eligibility v2.0</span></div>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Key Assumptions Used</p>
                  <div className="space-y-1">
                    <div className="flex justify-between"><span className="text-slate-500">Sector:</span><span className="font-medium">{s1.sector||selected?.sector||"—"}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Country:</span><span className="font-medium">{s1.country||"NG"}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Instrument:</span><span className="font-medium">{s1.instrument||"—"}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Tenor:</span><span className="font-medium">{s1.tenor||"—"}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">DSCR (input):</span><span className="font-medium">{summ.dscr_base||"—"}x</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Pricing rate:</span><span className="font-medium">{summ.pricing_rate||"—"}%</span></div>
                  </div>
                </div>
              </div>
              <div className="border-t border-slate-200 pt-2 mt-1">
                <p className="text-[10px] text-slate-400 italic">Carbon revenue is excluded from all DSCR calculations. This document was generated deterministically from structured deal inputs. No AI-generated narrative. All assumptions are traceable to inputs above. Tracing path: inputs → Signal Score engine → Eligibility engine → Pricing engine → Recommendation logic → this document.</p>
              </div>
              {s13.disclaimer && <p className="mt-2 italic text-[10px] text-slate-400 border-t border-slate-200 pt-2">{s13.disclaimer}</p>}
            </div>
          </MemoSection>
        </div>
      )}
    </SectionFrame>
  );
}

// ── MODULE 7: VCM Desk (Carbon Desk → VCM / Article 6) ───────────────────
// ── CARBON DESK: VCM / Article 6 — Realistic Pricing + Platform Connected ──
function SectionVCMDesk() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScenario, setSelectedScenario] = useState("base");

  // Realistic pricing by sector and scenario
  const SECTOR_PRICING = {
    energy:       { low:10, base:18, high:30, art6_low:20, art6_base:40, art6_high:60, vcm_suitable:true,  art6_suitable:true,  methodology:"ACM0002, AMS-I.D",     standard:"Gold Standard / Verra VCS", eligibility:"Yes",      notes:"Suitable for solar, wind, grid displacement" },
    biogas:       { low:10, base:18, high:30, art6_low:20, art6_base:40, art6_high:60, vcm_suitable:true,  art6_suitable:true,  methodology:"AMS-III.R",            standard:"Gold Standard",             eligibility:"Yes",      notes:"Organic waste-to-energy, methane avoidance" },
    cement:       { low:8,  base:15, high:25, art6_low:15, art6_base:30, art6_high:50, vcm_suitable:true,  art6_suitable:true,  methodology:"CDM/VCS fuel switch",  standard:"Verra VCS",                 eligibility:"Possible", notes:"Requires additionality validation; fuel switch methodology" },
    waste:        { low:8,  base:15, high:25, art6_low:15, art6_base:35, art6_high:50, vcm_suitable:true,  art6_suitable:true,  methodology:"AMS-III.G, ACM0022",  standard:"Gold Standard / Verra",     eligibility:"Yes",      notes:"Landfill gas or composting; strong methodology coverage" },
    agriculture:  { low:6,  base:12, high:20, art6_low:12, art6_base:25, art6_high:40, vcm_suitable:true,  art6_suitable:false, methodology:"AMS-III.F, VM0017",   standard:"Verra VCS",                 eligibility:"Possible", notes:"Soil carbon limited Art6 suitability; VCM pathway preferred" },
    transport:    { low:5,  base:10, high:18, art6_low:10, art6_base:20, art6_high:35, vcm_suitable:false, art6_suitable:false, methodology:"AMS-III.C",           standard:"Gold Standard",             eligibility:"Possible", notes:"Limited VCM coverage; focus on transition finance" },
    manufacturing:{ low:6,  base:12, high:20, art6_low:12, art6_base:25, art6_high:40, vcm_suitable:false, art6_suitable:false, methodology:"CDM SCopE",           standard:"Verra VCS",                 eligibility:"Possible", notes:"Industrial scope 1 — complex additionality" },
  };

  useEffect(() => {
    client.get("/fi/decisions/deals").then(r => setDeals(r.data.deals||[])).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const nonRejected = deals.filter(d => d.status !== "rejected");
  const inPortfolioSectors = new Set(nonRejected.map(d => d.sector));

  // Portfolio totals by scenario
  const getPortfolioRevenue = (scenario) => {
    return nonRejected.reduce((sum, deal) => {
      const pr = SECTOR_PRICING[deal.sector];
      if (!pr) return sum;
      const tco2e = SECTOR_CARBON[deal.sector]?.tco2e || 5000;
      const price  = scenario === "low" ? pr.low : scenario === "high" ? pr.high : pr.base;
      return sum + (tco2e * price);
    }, 0);
  };

  if (loading) return <div className="flex items-center justify-center py-12"><RefreshCw size={14} className="animate-spin text-teal-500"/></div>;

  return (
    <div className="space-y-5" data-testid="section-vcm-desk">
      {/* Concise disclaimer */}
      <div className="flex items-start gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl">
        <AlertTriangle size={11} className="text-amber-500 flex-shrink-0 mt-0.5"/>
        <p className="text-[11px] text-amber-700">
          <strong>Indicative market-informed ranges — not guaranteed prices.</strong> Carbon revenues excluded from DSCR. Not bankable until MRV verified, registry linked, and buyer integrated.
        </p>
      </div>

      {/* Portfolio Carbon Revenue Summary */}
      <div className="bg-[#0F172A] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] text-teal-400 font-bold uppercase tracking-[0.15em]">Portfolio Carbon Revenue — Scenario Analysis</p>
          <div className="flex rounded-lg border border-white/10 overflow-hidden">
            {[["low","Low"],["base","Base"],["high","High"]].map(([k,l])=>(
              <button key={k} onClick={()=>setSelectedScenario(k)}
                className={`px-3 py-1 text-[10px] font-bold transition-all ${selectedScenario===k?"bg-teal-600 text-white":"text-slate-500 hover:bg-white/5"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {[["low","Conservative"],["base","Base Case"],["high","Upside"]].map(([s,l])=>(
            <div key={s} className={`${selectedScenario===s?"bg-teal-600/20":"bg-white/5"} rounded-lg p-3 transition-all`}>
              <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-1">{l}</p>
              <p className={`text-xl font-bold tabular-nums ${selectedScenario===s?"text-teal-300":"text-slate-400"}`}>${(getPortfolioRevenue(s)/1000).toFixed(0)}K/yr</p>
              <p className="text-[10px] text-slate-600 mt-0.5">
                {s==="low"?"Conservative pricing, limited verification":s==="base"?"Market benchmark, base scenario":"Premium verified credits, Art6 pathway"}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing by sector — realistic ranges */}
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Carbon Pricing by Sector & Pathway</p>
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-2.5 text-slate-500 font-semibold">Sector</th>
                <th className="text-center px-3 py-2.5 text-teal-600 font-semibold" colSpan={3}>VCM Price ($/tCO₂e)</th>
                <th className="text-center px-3 py-2.5 text-blue-600 font-semibold" colSpan={3}>Article 6 ITMO ($/tCO₂e)</th>
                <th className="text-center px-3 py-2.5 text-slate-500 font-semibold">VCM Eligible</th>
              </tr>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] text-slate-400">
                <th className="px-4 py-1"></th>
                <th className="px-2 py-1 text-center">Low</th><th className="px-2 py-1 text-center font-bold text-teal-600">Base</th><th className="px-2 py-1 text-center">High</th>
                <th className="px-2 py-1 text-center">Low</th><th className="px-2 py-1 text-center font-bold text-blue-600">Base</th><th className="px-2 py-1 text-center">High</th>
                <th className="px-2 py-1 text-center"></th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(SECTOR_PRICING).map(([sector, pr], i) => {
                const inPortfolio = inPortfolioSectors.has(sector);
                return (
                  <tr key={sector} className={`border-b border-slate-100 last:border-0 ${i%2===1?"bg-slate-50/30":""}${inPortfolio?" ring-1 ring-inset ring-teal-200 bg-teal-50/20":""}`}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {inPortfolio && <div className="w-1.5 h-1.5 bg-teal-500 rounded-full flex-shrink-0"/>}
                        <span className="font-medium text-slate-800 capitalize">{sector}</span>
                        {inPortfolio && <span className="text-[9px] bg-teal-100 text-teal-700 px-1.5 rounded font-bold">In Portfolio</span>}
                      </div>
                    </td>
                    <td className="px-2 py-2.5 text-center text-slate-500">${pr.low}</td>
                    <td className="px-2 py-2.5 text-center font-bold text-teal-700">${pr.base}</td>
                    <td className="px-2 py-2.5 text-center text-slate-500">${pr.high}+</td>
                    <td className="px-2 py-2.5 text-center text-slate-400">{pr.art6_suitable?`$${pr.art6_low}`:"—"}</td>
                    <td className="px-2 py-2.5 text-center font-bold text-blue-700">{pr.art6_suitable?`$${pr.art6_base}`:"—"}</td>
                    <td className="px-2 py-2.5 text-center text-slate-400">{pr.art6_suitable?`$${pr.art6_high}+`:"—"}</td>
                    <td className="px-2 py-2.5 text-center">
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${pr.eligibility==="Yes"?"bg-emerald-100 text-emerald-700":pr.eligibility==="Possible"?"bg-amber-100 text-amber-700":"bg-red-100 text-red-600"}`}>
                        {pr.eligibility}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-400 italic mt-1.5">Prices are indicative market-informed ranges — not guaranteed. Actual pricing depends on project quality, verification, and buyer demand at time of issuance.</p>
      </div>

      {/* Deal-Level View — connected to platform */}
      {/* Pathway Methodology — Clean & Standardized */}
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Pathway Methodology by Sector</p>
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold">
                <th className="text-left px-4 py-2.5">Sector</th>
                <th className="text-left px-4 py-2.5">Methodology</th>
                <th className="text-left px-4 py-2.5">Standard</th>
                <th className="text-center px-3 py-2.5">VCM Eligible</th>
                <th className="text-left px-4 py-2.5">Notes</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(SECTOR_PRICING).map(([sector, pr], i) => (
                <tr key={sector} className={`border-b border-slate-100 last:border-0 ${i%2===1?"bg-slate-50/30":""}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-800 capitalize">{sector}</td>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-slate-600">{pr.methodology}</td>
                  <td className="px-4 py-2.5 text-slate-600">{pr.standard}</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${pr.eligibility==="Yes"?"bg-emerald-100 text-emerald-700":pr.eligibility==="Possible"?"bg-amber-100 text-amber-700":"bg-slate-100 text-slate-500"}`}>{pr.eligibility}</span>
                  </td>
                  <td className="px-4 py-2.5 text-[11px] text-slate-500 italic">{pr.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── STRATEGIC LAYER: Deal Syndication ───────────────────────────────────────
// Execution layer for capital structuring — not a static list
// Answers: Which deals can be structured? How? Who participates? What output?
function SectionSyndication() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panel, setPanel] = useState(null);   // { deal, type: "tranche"|"note" }
  const [tranche, setTranche] = useState({ senior:50, dfi:30, concessional:20 });
  const [exporting, setExporting] = useState(null);
  const [exportedDeals, setExportedDeals] = useState({});

  useEffect(() => {
    client.get("/fi/decisions/deals").then(r => setDeals(r.data.deals||[])).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  // Eligibility filter: size >= 3M AND (signal >= 50 OR at credit_committee+)
  const eligible = deals.filter(d => {
    const amount = d.deployed_amount_usd || 0;
    const score  = _parseScore(d) || 0;
    const stage  = d.status;
    return amount >= 3000000 || ["credit_committee","term_sheet","financial_close"].includes(stage) ||
           (score >= 50 && !["rejected","screening","data_request"].includes(stage));
  }).filter(d => d.status !== "rejected");

  // Syndication Readiness Score (0-100)
  const getReadiness = (deal) => {
    const score = _parseScore(deal) || 0;
    const elig  = _parseElig(deal);
    const ev    = deal.evidence_readiness_pct || 0;
    const amount = deal.deployed_amount_usd || 0;
    const stage  = deal.status;
    const pts = {
      financial: score >= 65 ? 25 : score >= 50 ? 15 : 5,
      esg:       elig === "eligible" ? 25 : elig === "conditional" ? 15 : 5,
      data:      ev >= 70 ? 25 : ev >= 40 ? 15 : 5,
      structure: ["credit_committee","term_sheet","financial_close"].includes(stage) ? 25 : ["due_diligence"].includes(stage) ? 15 : 5,
    };
    const total = Object.values(pts).reduce((a,b)=>a+b,0);
    return { total, pts, label: total >= 70 ? "Highly Ready" : total >= 45 ? "Ready" : "Not Ready",
      color: total >= 70 ? "text-emerald-700" : total >= 45 ? "text-teal-700" : "text-red-600",
      bg: total >= 70 ? "bg-emerald-50 border-emerald-200" : total >= 45 ? "bg-teal-50 border-teal-200" : "bg-red-50 border-red-200",
    };
  };

  // Action state
  const getActionState = (deal) => {
    if (exportedDeals[deal.deal_id]) return "In Syndication";
    const r = getReadiness(deal);
    if (deal.status === "financial_close") return "Closed";
    if (r.total >= 70) return "Ready for Syndication";
    if (r.total >= 45) return "Ready";
    return "Not Ready";
  };

  const exportNote = async (deal) => {
    setExporting(deal.deal_id);
    try {
      const payload = {
        senior_pct: tranche.senior,
        dfi_pct: tranche.dfi,
        concessional_pct: tranche.concessional,
        dscr: (deal.notes||"").match(/DSCR base case[:\s]*([\d.]+)x/i)?.[1] ? parseFloat((deal.notes||"").match(/DSCR base case[:\s]*([\d.]+)x/i)[1]) : 1.3,
        signal_score: _parseScore(deal) || 60,
        amount_usd: deal.deployed_amount_usd || 5000000,
      };
      const resp = await client.post(`/fi/pdf/syndication-note/${deal.deal_id}`, payload, { responseType:"blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data],{type:"application/pdf"}));
      const a = document.createElement("a");
      a.href=url; a.download=`SyndicationNote_${deal.deal_id}_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success(`Syndication Note for ${_parseName(deal).split(" ")[0]} exported.`);
      setExportedDeals(e => ({...e, [deal.deal_id]: true}));
    } catch { toast.error("PDF export failed."); }
    setExporting(null);
    setPanel(null);
  };

  // Dynamic insights
  const readyCount = eligible.filter(d => getReadiness(d).total >= 45).length;
  const highlyReadyCount = eligible.filter(d => getReadiness(d).total >= 70).length;
  const totalSyndicable = eligible.reduce((s,d)=>s+(d.deployed_amount_usd||0),0);

  if (loading) return <div className="flex items-center justify-center py-10"><RefreshCw size={14} className="animate-spin text-teal-500"/></div>;

  return (
    <div className="space-y-4" data-testid="section-syndication">
      {/* Dynamic Insights */}
      <div className="bg-[#0F172A] rounded-xl p-4">
        <p className="text-[10px] text-teal-400 font-bold uppercase tracking-[0.15em] mb-3">Syndication Portfolio Insights</p>
        <div className="grid grid-cols-3 gap-4">
          <div><p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Eligible for Syndication</p><p className="text-xl font-bold text-white">{eligible.length}</p><p className="text-[10px] text-slate-600">deals in pipeline</p></div>
          <div><p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Ready (Score ≥ 45)</p><p className="text-xl font-bold text-teal-400">{readyCount}</p><p className="text-[10px] text-slate-600">can proceed to structuring</p></div>
          <div><p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Total Syndication Volume</p><p className="text-xl font-bold text-white">${(totalSyndicable/1e6).toFixed(1)}M</p><p className="text-[10px] text-slate-600">across {eligible.length} deals</p></div>
        </div>
        <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5">
          {[
            `${highlyReadyCount} deal${highlyReadyCount!==1?"s":""} highly ready for syndication (score ≥ 70)`,
            readyCount > 0 ? `${readyCount} deal${readyCount!==1?"s":""} eligible for structured financing` : "No deals ready yet — complete credit assessment first",
            eligible.some(d=>["cement","transport"].includes(d.sector)) ? "High transition-risk deals will require ESG-compliant DFI participation" : "Portfolio well-positioned for green finance syndication",
          ].map((ins,i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-slate-400"><span className="text-teal-500 flex-shrink-0">—</span>{ins}</div>
          ))}
        </div>
      </div>

      {/* Eligible Deals */}
      {eligible.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-xl p-8 text-center">
          <p className="text-sm text-slate-400">No deals eligible for syndication yet</p>
          <p className="text-xs text-slate-300 mt-1">Deals with facility size ≥ $3M or at Credit Committee stage will appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {eligible.map(deal => {
            const r = getReadiness(deal);
            const actionState = getActionState(deal);
            const dscr = (deal.notes||"").match(/DSCR base case[:\s]*([\d.]+)x/i)?.[1] ? parseFloat((deal.notes||"").match(/DSCR base case[:\s]*([\d.]+)x/i)[1]) : null;
            const score = _parseScore(deal);
            const amount = deal.deployed_amount_usd || 0;
            const isExp = exporting === deal.deal_id;

            return (
              <div key={deal.deal_id} className={`border rounded-xl p-4 ${r.bg}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${actionState==="Highly Ready"||actionState==="Ready for Syndication"?"bg-emerald-100 text-emerald-700":actionState==="Ready"?"bg-teal-100 text-teal-700":actionState==="In Syndication"?"bg-blue-100 text-blue-700":actionState==="Closed"?"bg-slate-100 text-slate-500":"bg-red-100 text-red-600"}`}>
                        {actionState}
                      </span>
                      <span className="text-[10px] text-slate-500 capitalize">{deal.sector}</span>
                      {amount > 0 && <span className="text-[10px] font-bold text-slate-700">${(amount/1e6).toFixed(1)}M</span>}
                    </div>
                    <p className="text-sm font-bold text-slate-900 truncate">{_parseName(deal)}</p>
                    <p className="text-[10px] text-slate-400 font-mono">{deal.deal_id}</p>
                    {/* Readiness Score */}
                    <div className="mt-2 flex items-center gap-2">
                      <p className="text-[10px] text-slate-500">Syndication Readiness:</p>
                      <span className={`text-[10px] font-bold ${r.color}`}>{r.total}/100 — {r.label}</span>
                    </div>
                    <div className="h-1.5 bg-white/60 rounded-full overflow-hidden mt-1 w-40">
                      <div className={`h-full rounded-full ${r.total>=70?"bg-emerald-500":r.total>=45?"bg-teal-500":"bg-red-400"}`} style={{width:`${r.total}%`}}/>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    <button onClick={() => setPanel({ deal, type:"note" })}
                      disabled={isExp}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold rounded-lg transition-colors ${r.total >= 45 ? "bg-teal-600 text-white hover:bg-teal-700" : "bg-slate-100 text-slate-500"} disabled:opacity-40`}
                      data-testid={`syn-note-${deal.deal_id}`}>
                      {isExp?<RefreshCw size={10} className="animate-spin"/>:null} Prepare Note
                    </button>
                    <button onClick={() => setPanel({ deal, type:"tranche" })}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold border border-teal-300 text-teal-700 rounded-lg hover:bg-teal-50">
                      Allocate DFI Tranche
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* DFI Allocation / Note Panel */}
      {panel && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/30" onClick={()=>setPanel(null)}/>
          <div className="w-96 bg-white shadow-2xl flex flex-col h-full overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b bg-slate-50">
              <div>
                <p className="text-sm font-bold text-slate-900">{panel.type==="tranche"?"DFI Tranche Allocation":"Prepare Syndication Note"}</p>
                <p className="text-[10px] text-slate-400">{_parseName(panel.deal).split(" ").slice(0,3).join(" ")} · ${((panel.deal.deployed_amount_usd||5000000)/1e6).toFixed(1)}M</p>
              </div>
              <button onClick={()=>setPanel(null)}><X size={16} className="text-slate-400"/></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {panel.type === "tranche" ? (
                <div className="space-y-4">
                  <p className="text-xs text-slate-500">Define the capital stack structure for this deal. Percentages must sum to 100%.</p>
                  {[["Senior (Commercial)", "senior", "bg-slate-700"], ["DFI / A-Loan", "dfi", "bg-blue-600"], ["Concessional / First Loss", "concessional", "bg-amber-500"]].map(([label, key, color]) => (
                    <div key={key}>
                      <div className="flex justify-between text-xs mb-1"><label className="font-medium text-slate-700">{label}</label><span className="font-bold text-slate-900">{tranche[key]}% · ${((panel.deal.deployed_amount_usd||5000000) * tranche[key]/100/1e6).toFixed(1)}M</span></div>
                      <input type="range" min={0} max={100} value={tranche[key]} onChange={e => setTranche(t => ({...t, [key]: parseInt(e.target.value)}))}
                        className="w-full accent-teal-600"/>
                      <div className={`h-1.5 rounded-full ${color} mt-1`} style={{width:`${tranche[key]}%`}}/>
                    </div>
                  ))}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs">
                    <div className="flex justify-between"><span className="text-slate-500">Total</span><span className={`font-bold ${tranche.senior+tranche.dfi+tranche.concessional===100?"text-emerald-600":"text-red-600"}`}>{tranche.senior+tranche.dfi+tranche.concessional}%</span></div>
                    {tranche.senior+tranche.dfi+tranche.concessional !== 100 && <p className="text-red-600 mt-1">Must sum to 100%</p>}
                  </div>
                  <button onClick={() => { toast.success("Capital stack saved."); setPanel(null); }}
                    disabled={tranche.senior+tranche.dfi+tranche.concessional !== 100}
                    className="w-full py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 disabled:opacity-40">
                    Confirm Capital Stack
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-xs text-slate-500">The Syndication Note will be generated using the current capital stack and deal data.</p>
                  <div className="border border-slate-200 rounded-lg p-3 space-y-1.5 text-xs">
                    {[["Deal", _parseName(panel.deal).split(" ").slice(0,3).join(" ")],["Amount", `$${((panel.deal.deployed_amount_usd||5000000)/1e6).toFixed(1)}M`],["Signal Score", `${_parseScore(panel.deal)||"—"}/100`],["DSCR", `${(panel.deal.notes||"").match(/DSCR base case[:\s]*([\d.]+)x/i)?.[1]||"—"}x`],["Capital Stack",`${tranche.senior}% senior / ${tranche.dfi}% DFI / ${tranche.concessional}% concessional`]].map(([l,v])=>(
                      <div key={l} className="flex justify-between"><span className="text-slate-400">{l}</span><span className="font-medium text-slate-700">{v}</span></div>
                    ))}
                  </div>
                  <div className="border border-slate-200 rounded-lg p-3 text-xs">
                    <p className="font-bold text-slate-700 mb-2">Note Contents</p>
                    {["Deal summary + sector overview","Financial analysis (DSCR, signal score)","Proposed capital stack","ESG + climate metrics","Carbon upside (indicative)","Risk profile","Verification trail (hash + timestamp)"].map((s,i)=>(
                      <div key={i} className="flex items-center gap-2 text-slate-600 py-0.5"><span className="text-teal-500">✓</span>{s}</div>
                    ))}
                  </div>
                  <button onClick={() => exportNote(panel.deal)}
                    disabled={exporting === panel.deal.deal_id}
                    className="w-full py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 disabled:opacity-40">
                    {exporting === panel.deal.deal_id ? <RefreshCw size={13} className="animate-spin mx-auto"/> : "Generate & Export Syndication Note (PDF)"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── STRATEGIC LAYER: Co-Investment Structuring ───────────────────────────────
function SectionCoInvestment() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelected] = useState(null);
  const [stack, setStack] = useState({ first_loss:15, dfi:35, commercial:50, guarantee:0 });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    client.get("/fi/decisions/deals").then(r => {
      const d = (r.data.deals||[]).filter(x => !["rejected"].includes(x.status));
      setDeals(d);
      if (d.length > 0) setSelected(d[0]);
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const COINV_ELIGIBLE = deals.filter(d => !["rejected","screening"].includes(d.status));

  // DSCR improvement from blended structure
  const getImpact = (deal) => {
    const baseDSCR = (deal.notes||"").match(/DSCR base case[:\s]*([\d.]+)x/i)?.[1] ? parseFloat((deal.notes||"").match(/DSCR base case[:\s]*([\d.]+)x/i)[1]) : 1.2;
    const marketRate = 0.105;
    const blendedRate = marketRate * (stack.commercial/100) + 0.05 * (stack.dfi/100) + 0.02 * (stack.first_loss/100);
    const dscrImproved = Math.min(baseDSCR * (marketRate / Math.max(blendedRate, 0.01)), 3.0);
    const rwWithout = 100;
    const rwWith = Math.max(20, Math.round(100 - stack.first_loss - stack.guarantee * 0.5));
    return { baseDSCR, blendedRate: blendedRate*100, dscrImproved, rwWithout, rwWith, improvement: dscrImproved - baseDSCR };
  };

  const exportMemo = async () => {
    if (!selectedDeal) return;
    setExporting(true);
    try {
      const imp = getImpact(selectedDeal);
      const resp = await client.post(`/fi/pdf/co-investment-memo/${selectedDeal.deal_id}`, {
        first_loss_pct: stack.first_loss, dfi_pct: stack.dfi,
        commercial_pct: stack.commercial, guarantee_pct: stack.guarantee,
        base_dscr: imp.baseDSCR,
      }, { responseType:"blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data],{type:"application/pdf"}));
      const a = document.createElement("a");
      a.href=url; a.download=`CoInvestmentMemo_${selectedDeal.deal_id}_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success("Co-Investment Memo exported.");
    } catch { toast.error("Export failed."); }
    setExporting(false);
  };

  if (loading) return <div className="flex items-center justify-center py-10"><RefreshCw size={14} className="animate-spin text-teal-500"/></div>;

  const impact = selectedDeal ? getImpact(selectedDeal) : null;
  const totalPct = stack.first_loss + stack.dfi + stack.commercial;
  const amount = selectedDeal?.deployed_amount_usd || 5000000;

  return (
    <div className="space-y-4" data-testid="section-co-investment">
      {/* Insights */}
      <div className="bg-[#0F172A] rounded-xl p-4">
        <p className="text-[10px] text-teal-400 font-bold uppercase tracking-[0.15em] mb-2">Co-Investment Opportunities</p>
        <div className="space-y-1.5">
          {[
            `${COINV_ELIGIBLE.length} deal${COINV_ELIGIBLE.length!==1?"s":""} eligible for blended finance structuring`,
            "DFI participation reduces blended interest rate and improves DSCR",
            "First loss tranche unlocks commercial bank participation in higher-risk climate deals",
            COINV_ELIGIBLE.some(d=>d.sector==="biogas"||d.sector==="waste") ? "Biogas and waste deals particularly suitable for concessional co-financing" : "Energy sector deals suitable for A/B loan DFI structure",
          ].map((ins,i)=>(
            <div key={i} className="flex items-start gap-2 text-xs text-slate-400"><span className="text-teal-500 flex-shrink-0">—</span>{ins}</div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Capital Stack Builder */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Select Deal</label>
            <select value={selectedDeal?.deal_id||""} onChange={e=>setSelected(COINV_ELIGIBLE.find(d=>d.deal_id===e.target.value)||null)}
              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none">
              {COINV_ELIGIBLE.map(d=><option key={d.deal_id} value={d.deal_id}>{d.deal_id} — {_parseName(d).split(" ").slice(0,3).join(" ")}</option>)}
            </select>
          </div>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Capital Stack Builder</p>
          {[
            { key:"first_loss",  label:"First Loss / Grant", color:"bg-red-400",    hint:"Absorbs initial losses — unlocks DFI and commercial participation" },
            { key:"dfi",         label:"DFI Senior Tranche", color:"bg-blue-500",   hint:"DFI / MDB participation at concessional rate (5–7%)" },
            { key:"commercial",  label:"Commercial Tranche", color:"bg-slate-700",  hint:"Commercial bank at market rate (10–12%)" },
            { key:"guarantee",   label:"Guarantee Cover",    color:"bg-emerald-500",hint:"Partial credit guarantee — reduces RWA" },
          ].map(t=>(
            <div key={t.key}>
              <div className="flex justify-between text-xs mb-1"><span className="font-medium text-slate-700">{t.label}</span><span className="font-bold">{stack[t.key]}% · ${(amount*stack[t.key]/100/1e6).toFixed(1)}M</span></div>
              <input type="range" min={0} max={100} value={stack[t.key]}
                onChange={e=>setStack(s=>({...s,[t.key]:parseInt(e.target.value)}))}
                className="w-full accent-teal-600"/>
              <div className={`h-1 rounded-full ${t.color} mt-0.5`} style={{width:`${stack[t.key]}%`}}/>
              <p className="text-[9px] text-slate-400 mt-0.5">{t.hint}</p>
            </div>
          ))}
          <div className={`flex justify-between text-xs p-2 rounded-lg ${totalPct===100?"bg-emerald-50 border border-emerald-200":"bg-red-50 border border-red-200"}`}>
            <span className="font-bold">Capital Stack Total</span>
            <span className={`font-bold ${totalPct===100?"text-emerald-700":"text-red-600"}`}>{totalPct}% {totalPct!==100?"⚠ Must = 100%":""}</span>
          </div>
        </div>

        {/* DSCR Impact + Actions */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Impact of Blended Structure</p>
          {impact ? (
            <>
              <div className="bg-[#0F172A] rounded-xl p-4 text-white space-y-3">
                <div>
                  <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">DSCR — Without Blending</p>
                  <p className="text-2xl font-bold text-white">{impact.baseDSCR.toFixed(2)}x</p>
                </div>
                <div className="flex items-center gap-2">
                  <ArrowRight size={14} className="text-teal-400 flex-shrink-0"/>
                  <div>
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">DSCR — With DFI Blending</p>
                    <p className="text-2xl font-bold text-emerald-400">{impact.dscrImproved.toFixed(2)}x</p>
                    <p className="text-[10px] text-emerald-500">↑ +{Math.max(0,impact.improvement).toFixed(2)}x improvement</p>
                  </div>
                </div>
                <div className="border-t border-white/10 pt-3 grid grid-cols-2 gap-3 text-xs">
                  <div><p className="text-[9px] text-slate-500">Blended Rate</p><p className="font-bold text-teal-400">{impact.blendedRate.toFixed(1)}%</p></div>
                  <div><p className="text-[9px] text-slate-500">Risk Weight</p><p className="font-bold text-emerald-400">{impact.rwWith}% (was {impact.rwWithout}%)</p></div>
                </div>
              </div>
              <button onClick={exportMemo} disabled={exporting || totalPct !== 100}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 disabled:opacity-40"
                data-testid="co-invest-export">
                {exporting?<RefreshCw size={13} className="animate-spin"/>:null}
                {exporting?"Generating...":"Prepare Co-Investment Memo (PDF)"}
              </button>
              <div className="text-[10px] text-slate-400 space-y-0.5">
                <p>Memo includes: capital stack, risk sharing, DSCR impact, ESG alignment, climate metrics, verification trail</p>
              </div>
            </>
          ) : <p className="text-xs text-slate-400">Select a deal to see impact</p>}
        </div>
      </div>
    </div>
  );
}

// ── STRATEGIC LAYER: PoA Aggregation ─────────────────────────────────────────
function SectionPoADesk() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [programme, setProgramme] = useState({ name:"Keystone Climate PoA", entity:"Keystone Bank", deals:[] });
  const [progId, setProgId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const SECTOR_CO2 = { cement:50000, biogas:35000, energy:25000, waste:18000, agriculture:15000, transport:12000, manufacturing:20000 };
  const METHODOLOGIES = { biogas:"AMS-III.R (Gold Standard)", energy:"ACM0002 (Verra VCS)", waste:"AMS-III.G (Gold Standard)", cement:"CDM Fuel Switch (Verra VCS)", agriculture:"AMS-III.F (Verra VCS)" };
  const POA_SECTORS = ["biogas","energy","waste","agriculture","cement"];

  useEffect(() => {
    client.get("/fi/decisions/deals").then(r => setDeals(r.data.deals||[])).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const poaEligible = deals.filter(d => !["rejected"].includes(d.status) && POA_SECTORS.includes(d.sector));

  const toggleDeal = (deal_id) => {
    setProgramme(p => ({
      ...p,
      deals: p.deals.includes(deal_id) ? p.deals.filter(d=>d!==deal_id) : [...p.deals, deal_id]
    }));
  };

  const selectedDeals = deals.filter(d => programme.deals.includes(d.deal_id));
  const totalTco2e     = selectedDeals.reduce((s,d)=>(SECTOR_CO2[d.sector]||5000)*0.30+s, 0);
  const totalAmt       = selectedDeals.reduce((s,d)=>(d.deployed_amount_usd||0)+s, 0);
  const baseCarbonRev  = totalTco2e * 15;
  const mrvSavings     = selectedDeals.length > 1 ? Math.round(selectedDeals.length * 150000 * 0.72) : 0;

  const createProgramme = () => {
    const ts = new Date().toISOString().slice(2,10).replace(/-/g,"");
    setProgId(`POA-${ts}-KBK`);
    toast.success(`Programme ${`POA-${ts}-KBK`} created with ${programme.deals.length} CPA${programme.deals.length!==1?"s":""}.`);
  };

  const exportPDD = async () => {
    setExporting(true);
    try {
      const resp = await client.post("/fi/pdf/poa-programme", {
        programme_name: programme.name,
        deal_ids: programme.deals,
        coordinating_entity: programme.entity,
      }, { responseType:"blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data],{type:"application/pdf"}));
      const a = document.createElement("a");
      a.href=url; a.download=`PoA_PDD_${progId||"draft"}_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success("Programme Design Document (PDD-lite) exported.");
    } catch { toast.error("PDD export failed."); }
    setExporting(false);
  };

  if (loading) return <div className="flex items-center justify-center py-10"><RefreshCw size={14} className="animate-spin text-teal-500"/></div>;

  return (
    <div className="space-y-4" data-testid="section-poa-desk">
      {/* Key Insight */}
      {programme.deals.length >= 2 && (
        <div className="bg-emerald-50 border border-emerald-300 rounded-xl px-5 py-3.5">
          <p className="text-sm font-bold text-emerald-800">
            This PoA unlocks ${(baseCarbonRev/1000).toFixed(0)}K/yr carbon revenue and ${(mrvSavings/1000).toFixed(0)}K/yr MRV cost savings (72% reduction vs individual projects)
          </p>
          <p className="text-xs text-emerald-600 mt-1">{programme.deals.length} CPAs · {totalTco2e.toLocaleString()} tCO₂e/yr aggregated reduction · 7-year crediting horizon</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {/* Programme Builder */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Programme Configuration</p>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Programme Name</label>
            <input value={programme.name} onChange={e=>setProgramme(p=>({...p,name:e.target.value}))}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Coordinating Entity</label>
            <input value={programme.entity} onChange={e=>setProgramme(p=>({...p,entity:e.target.value}))}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none"/>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-600 mb-2">Select CPAs (Sub-Projects)</p>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {poaEligible.map(deal => {
                const tco2e = (SECTOR_CO2[deal.sector]||5000)*0.30;
                const selected = programme.deals.includes(deal.deal_id);
                return (
                  <label key={deal.deal_id} className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-all ${selected?"bg-teal-50 border border-teal-300":"bg-slate-50 border border-slate-200 hover:border-teal-300"}`}>
                    <input type="checkbox" checked={selected} onChange={()=>toggleDeal(deal.deal_id)} className="rounded text-teal-600"/>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-900 truncate">{_parseName(deal).split(" ").slice(0,3).join(" ")}</p>
                      <p className="text-[10px] text-slate-400 capitalize">{deal.sector} · {tco2e.toLocaleString()} tCO₂e/yr · {METHODOLOGIES[deal.sector]||"VCM"}</p>
                    </div>
                  </label>
                );
              })}
              {poaEligible.length === 0 && <p className="text-xs text-slate-400 text-center py-3">No PoA-eligible deals yet</p>}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={createProgramme} disabled={programme.deals.length === 0}
              className="flex-1 py-2 bg-slate-900 text-white text-xs font-bold rounded-xl hover:bg-slate-800 disabled:opacity-40"
              data-testid="create-programme">
              {progId ? "Update Programme" : "Create Programme"}
            </button>
          </div>
          {progId && <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-xs"><span className="font-bold text-emerald-700">Programme ID:</span> <span className="font-mono text-emerald-800">{progId}</span></div>}
        </div>

        {/* Aggregated Outputs */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Aggregated Outputs</p>
          {programme.deals.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-slate-400">
              <div className="text-center">
                <Layers size={24} className="mx-auto mb-2 text-slate-200"/>
                <p className="text-xs">Select CPAs to see aggregated metrics</p>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label:"Total Reduction", value:`${totalTco2e.toLocaleString()} tCO₂e/yr`, color:"text-emerald-700", bg:"bg-emerald-50 border-emerald-200" },
                  { label:"Carbon Revenue (Base)", value:`$${(baseCarbonRev/1000).toFixed(0)}K/yr`, color:"text-teal-700", bg:"bg-teal-50 border-teal-200" },
                  { label:"MRV Cost Saving", value:mrvSavings > 0 ? `$${(mrvSavings/1000).toFixed(0)}K/yr` : "Add ≥2 CPAs", color:"text-slate-700", bg:"bg-slate-50 border-slate-200" },
                  { label:"Total Financing", value:`$${(totalAmt/1e6).toFixed(1)}M`, color:"text-slate-900", bg:"bg-slate-50 border-slate-200" },
                ].map(m=>(
                  <div key={m.label} className={`border rounded-xl p-3 ${m.bg}`}>
                    <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-0.5">{m.label}</p>
                    <p className={`text-sm font-bold ${m.color}`}>{m.value}</p>
                  </div>
                ))}
              </div>
              {selectedDeals.length > 0 && (
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider px-3 py-2 bg-slate-50 border-b">CPAs in Programme</p>
                  {selectedDeals.map(d=>(
                    <div key={d.deal_id} className="flex items-center gap-2 px-3 py-2 border-b border-slate-50 last:border-0 text-xs">
                      <span className="text-slate-400 font-mono text-[9px] w-24 flex-shrink-0 truncate">{d.deal_id}</span>
                      <span className="text-slate-600 capitalize flex-1">{d.sector}</span>
                      <span className="text-emerald-600 font-bold tabular-nums">{((SECTOR_CO2[d.sector]||5000)*0.30).toLocaleString()}</span>
                      <span className="text-slate-400">tCO₂e</span>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={exportPDD} disabled={exporting || !progId}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 disabled:opacity-40"
                data-testid="export-pdd">
                {exporting?<RefreshCw size={13} className="animate-spin"/>:null}
                {exporting?"Generating...":"Bundle into Programme (PDD-Lite PDF)"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ── PIPELINE: Deal Action Board ───────────────────────────────────────────
// Shows all deals with their current disposition, key blocker, and next action.
// For declined/deferred/conditional: explicitly shows why and what needs to happen.
// This is deal management — not origination and not the executive dashboard.
function SectionPipelineMain({ onSection }) {
  const [deals, setDeals]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active"); // active | all | closed

  useEffect(() => {
    setLoading(true);
    client.get("/fi/decisions/deals")
      .then(r => setDeals(r.data.deals || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // ── Disposition logic (stage + history + signal) ────────────────────────
  const getDisposition = (deal) => {
    const sc   = _parseScore(deal);
    const elig = _parseElig(deal);
    const size = _parseSize(deal);
    const history = deal.stage_history || [];

    if (deal.status === "rejected") {
      // Find the rejection event in history
      const ev = [...history].reverse().find(h =>
        ["not_suitable","not_eligible","decline","reject"].includes(h.action)
      );
      const REJECT_LABELS = {
        not_suitable: "Not Suitable",
        not_eligible: "Not Eligible",
        decline:      "Declined — Credit Committee",
        reject:       "Rejected",
      };
      const label  = ev ? (REJECT_LABELS[ev.action] || "Rejected") : "Closed";
      const reason = ev?.reason || ev?.notes || null;
      return {
        type: "closed",
        label,
        reason,
        color: "bg-red-50 border-red-200",
        badge: "bg-red-100 text-red-700",
        badgeLabel: "Closed",
        nextAction: reason?.toLowerCase().includes("dscr")
          ? "Restructure facility or close file"
          : reason?.toLowerCase().includes("data")
          ? "Request missing data before reconsidering"
          : "Review decline rationale — close file or queue for resubmission",
        actionSection: "decision",
        actionModule: "credit",
      };
    }

    if (deal.status === "financial_close") {
      return {
        type: "closed",
        label: "Disbursed — Financial Close",
        reason: null,
        color: "bg-emerald-50 border-emerald-200",
        badge: "bg-emerald-100 text-emerald-700",
        badgeLabel: "Closed",
        nextAction: "Monitor covenant compliance and carbon performance",
        actionSection: "monitoring",
        actionModule: "portfolio",
      };
    }

    if (deal.status === "screening") {
      return {
        type: "blocked",
        label: "Eligibility not assessed",
        reason: "Deal has entered pipeline but has not been through eligibility assessment",
        color: "bg-slate-50 border-slate-200",
        badge: "bg-slate-100 text-slate-600",
        badgeLabel: "Screening",
        nextAction: "Proceed to Eligibility Assessment",
        actionSection: "eligibility",
        actionModule: "eligibility",
      };
    }

    if (deal.status === "data_request") {
      const lastRequest = [...history].reverse().find(h => h.action === "request_more_info");
      const dataNeeded = lastRequest?.reason || "Financial and climate metrics not yet received";
      return {
        type: "blocked",
        label: "Awaiting counterparty data",
        reason: dataNeeded,
        color: "bg-blue-50 border-blue-200",
        badge: "bg-blue-100 text-blue-700",
        badgeLabel: "Data Requested",
        nextAction: "Chase counterparty — specific data gaps logged",
        actionSection: "data_import",
        actionModule: "credit",
      };
    }

    if (deal.status === "due_diligence") {
      if (!sc) {
        return {
          type: "at_risk",
          label: "Signal Score not computed",
          reason: "Credit quality cannot be confirmed — signal score assessment must be run",
          color: "bg-amber-50 border-amber-200",
          badge: "bg-amber-100 text-amber-700",
          badgeLabel: "Due Diligence",
          nextAction: "Run Signal Score to establish credit quality",
          actionSection: "signal_score",
          actionModule: "eligibility",
        };
      }
      if (sc < 45) {
        return {
          type: "at_risk",
          label: `Signal ${sc}/100 — below credit threshold`,
          reason: `Score ${sc}/100 is below the 45/100 minimum for conditional approval. Eligibility or financial data remediation needed.`,
          color: "bg-amber-50 border-amber-200",
          badge: "bg-amber-100 text-amber-700",
          badgeLabel: "Due Diligence",
          nextAction: "Identify and resolve score gaps before committee submission",
          actionSection: "signal_score",
          actionModule: "eligibility",
        };
      }
      return {
        type: "active",
        label: `Signal ${sc}/100 — in due diligence`,
        reason: sc >= 65 ? "Strong signal — ready for committee submission" : "Adequate signal — conditional path available",
        color: "bg-amber-50 border-amber-200",
        badge: "bg-amber-100 text-amber-700",
        badgeLabel: "Due Diligence",
        nextAction: sc >= 65 ? "Submit to Credit Committee" : "Review conditions before submission",
        actionSection: "decision",
        actionModule: "credit",
      };
    }

    if (deal.status === "credit_committee") {
      if (!sc) {
        return {
          type: "at_risk",
          label: "Signal Score missing — committee pack incomplete",
          reason: "Credit memo and signal score must be generated before committee deliberation",
          color: "bg-orange-50 border-orange-200",
          badge: "bg-orange-100 text-orange-700",
          badgeLabel: "Credit Committee",
          nextAction: "Generate Credit Memo with full analysis",
          actionSection: "credit_memo",
          actionModule: "credit",
        };
      }
      const deferEvent = [...history].reverse().find(h => h.action === "defer_to_next_committee");
      if (deferEvent) {
        return {
          type: "conditional",
          label: "Deferred — conditions must be met",
          reason: deferEvent.reason || "Committee has set conditions for resubmission",
          color: "bg-orange-50 border-orange-200",
          badge: "bg-orange-100 text-orange-700",
          badgeLabel: "Deferred",
          nextAction: "Resolve deferral conditions and resubmit to committee",
          actionSection: "decision",
          actionModule: "credit",
        };
      }
      return {
        type: "active",
        label: "Ready for credit committee deliberation",
        reason: `Signal ${sc}/100 — full analysis package required for committee`,
        color: "bg-orange-50 border-orange-200",
        badge: "bg-orange-100 text-orange-700",
        badgeLabel: "Credit Committee",
        nextAction: "Present to Credit Committee — generate Credit Memo",
        actionSection: "credit_memo",
        actionModule: "credit",
      };
    }

    if (deal.status === "term_sheet") {
      return {
        type: "active",
        label: "Commercial terms under negotiation",
        reason: "Committee approved — term sheet execution in progress",
        color: "bg-teal-50 border-teal-200",
        badge: "bg-teal-100 text-teal-700",
        badgeLabel: "Term Sheet",
        nextAction: "Finalise and execute term sheet",
        actionSection: "financing",
        actionModule: "credit",
      };
    }

    return {
      type: "active",
      label: deal.stage_label || deal.status,
      reason: null,
      color: "bg-slate-50 border-slate-200",
      badge: "bg-slate-100 text-slate-600",
      badgeLabel: deal.stage_label || deal.status,
      nextAction: "Review deal status",
      actionSection: "decision",
      actionModule: "credit",
    };
  };

  const activeDeals  = deals.filter(d => !["rejected","financial_close"].includes(d.status));
  const closedDeals  = deals.filter(d => ["rejected","financial_close"].includes(d.status));
  const shownDeals   = filter === "active" ? activeDeals : filter === "closed" ? closedDeals : deals;

  // Sort: active by urgency, closed at bottom
  const STAGE_URGENCY = { credit_committee:1, term_sheet:2, due_diligence:3, data_request:4, screening:5, financial_close:6, rejected:7 };
  const sorted = [...shownDeals].sort((a,b) => (STAGE_URGENCY[a.status]||9) - (STAGE_URGENCY[b.status]||9));

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <RefreshCw size={16} className="animate-spin text-teal-500"/>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="fi-pipeline-board">
      {/* ── Header ── */}
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-900">Pipeline Action Board</h2>
          <p className="text-xs text-slate-500 mt-0.5">All deals currently inside FI assessment. Each shows current disposition and required next action.</p>
        </div>
        <div className="flex gap-1 p-1 bg-slate-100 rounded-xl">
          {[["active",`Active (${activeDeals.length})`],["all",`All (${deals.length})`],["closed",`Closed (${closedDeals.length})`]].map(([k,l]) => (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${filter===k?"bg-white shadow-sm text-slate-900":"text-slate-500"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-xl p-10 text-center">
          <Layers size={28} className="mx-auto mb-3 text-slate-200"/>
          <p className="text-sm font-semibold text-slate-400">No deals in this view</p>
          <p className="text-xs text-slate-300 mt-1">Add deals via Deal Origination to start the pipeline</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sorted.map(deal => {
            const disp = getDisposition(deal);
            const sc   = _parseScore(deal);
            const size = _parseSize(deal);
            const name = _parseName(deal);
            const sector = deal.sector || "—";

            return (
              <div key={deal.deal_id}
                className={`border rounded-xl p-4 ${disp.color}`}
                data-testid={`pipeline-deal-${deal.deal_id}`}>

                <div className="flex items-start gap-3">
                  {/* Status badge */}
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded flex-shrink-0 mt-0.5 ${disp.badge}`}>
                    {disp.badgeLabel}
                  </span>

                  {/* Main content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold text-slate-900 leading-tight">{name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          <span className="capitalize">{sector}</span>
                          {size && <span className="ml-2 font-semibold text-slate-700">{size}</span>}
                          <span className="mx-1.5 text-slate-300">·</span>
                          <span className="font-mono text-[10px] text-slate-400">{deal.deal_id}</span>
                        </p>
                      </div>
                      {/* Signal */}
                      {sc !== null ? (
                        <div className="text-right flex-shrink-0">
                          <p className={`text-base font-bold tabular-nums leading-none ${sc>=65?"text-emerald-600":sc>=45?"text-amber-500":"text-red-500"}`}>
                            {sc}<span className="text-xs font-normal text-slate-400">/100</span>
                          </p>
                          <p className="text-[10px] text-slate-400 mt-0.5">signal</p>
                        </div>
                      ) : (
                        <span className="text-[10px] text-slate-400 italic flex-shrink-0">not scored</span>
                      )}
                    </div>

                    {/* Disposition + reason */}
                    <div className="mt-2">
                      <p className="text-xs font-semibold text-slate-700">{disp.label}</p>
                      {disp.reason && (
                        <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">{disp.reason}</p>
                      )}
                    </div>

                    {/* Next action */}
                    <div className="flex items-center justify-between mt-2.5 gap-4">
                      <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                        <span className="font-semibold text-slate-500">Next:</span>
                        {disp.nextAction}
                      </div>
                      {disp.type !== "closed" || deal.status === "rejected" ? (
                        <button
                          onClick={() => onSection && onSection(disp.actionSection)}
                          className={`flex items-center gap-1 text-[11px] font-bold transition-colors flex-shrink-0 ${
                            disp.type==="closed"?"text-slate-500 hover:text-slate-700":
                            disp.type==="at_risk"?"text-amber-600 hover:text-amber-800":
                            "text-teal-600 hover:text-teal-800"
                          }`}
                          data-testid={`pipeline-next-${deal.deal_id}`}>
                          {deal.status === "rejected" ? "View Audit" : disp.nextAction.split(" ").slice(0,3).join(" ")} <ArrowRight size={10}/>
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── MODULE 1: Executive Dashboard ────────────────────────────────────────

// ── STEP 1: DEAL SCREENING — Triage Layer ────────────────────────────────
// Purpose: Quickly decide whether a deal should proceed, needs data, or is unsuitable.
// NO scoring. NO analytics. Human + light rules only.
// ── STEP 1: DEAL SCREENING — Triage Layer ────────────────────────────────
function SectionScreening({ onSection }) {
  const [deals,      setDeals]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [acting,     setActing]     = useState(null);
  const [panel,      setPanel]      = useState(null);  // deal open in right panel
  const [rejectDeal, setRejectDeal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [filter,     setFilter]     = useState("screening");

  const load = () => {
    setLoading(true);
    client.get("/fi/decisions/deals")
      .then(r => setDeals(r.data.deals || []))
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const advance = async (dealId, action, reason) => {
    setActing(dealId);
    try {
      await client.post(`/fi/decisions/deals/${dealId}/action`, {
        action, reason: reason || "Actioned at triage stage",
      });
      toast.success(
        action === "advance_to_data_request" ? "Proceeding to Eligibility" :
        action === "request_more_info"        ? "Data request recorded" : "Marked as not suitable"
      );
      setPanel(null);
      load();
    } catch { toast.error("Action failed."); }
    setActing(null);
  };

  // ── MRV Readiness classification (NOT auto-pass) ──────────────────────
  const getMRV = (deal) => {
    const notes = (deal.notes || "").toLowerCase();
    const ev = deal.evidence_readiness_pct || 0;
    const hasVerification = notes.includes("verified") && !notes.includes("not verified");
    const hasMRVPlan = notes.includes("mrv") || notes.includes("monitoring") || notes.includes("evidence");
    // MRV Ready: explicitly verified with high evidence quality
    if (hasVerification && ev >= 80)
      return { label:"MRV Ready", color:"text-emerald-700", bg:"bg-emerald-100 border-emerald-300", dot:"bg-emerald-500",
               tooltip:"Evidence verified — meets institutional MRV standards. Issuance pathway available." };
    // MRV Not Ready: truly critical — no notes, no evidence, no MRV indication
    if ((deal.notes || "").length < 20 && ev === 0)
      return { label:"MRV Not Ready", color:"text-red-700", bg:"bg-red-100 border-red-300", dot:"bg-red-400",
               tooltip:"Critical MRV gaps — no evidence of monitoring plan. Not eligible for carbon revenue claims." };
    // MRV Conditional: platform default — partial evidence, not yet verified
    return { label:"MRV Conditional", color:"text-amber-700", bg:"bg-amber-100 border-amber-300", dot:"bg-amber-400",
             tooltip:"Platform-calculated estimates. Partial evidence — not yet independently verified. Cannot be used for issuance or credit claims." };
  };

  // ── Missing data flags ─────────────────────────────────────────────────
  const getFlags = (deal) => {
    const flags = [];
    if (!deal.deployed_amount_usd) flags.push("Facility size not provided");
    if (!deal.notes || deal.notes.length < 30) flags.push("Insufficient context or notes");
    if (!deal.sector) flags.push("Sector not classified");
    return flags;
  };

  // ── Climate signal (indicative from platform data) ────────────────────
  const getClimateSignal = (deal) => {
    const sc = SECTOR_CARBON[deal.sector] || { tco2e: 5000 };
    const notes = deal.notes || "";
    // Try to parse from notes first
    const baseM  = notes.match(/baseline[:\s]+([\d,]+)\s*tCO2e/i);
    const redM   = notes.match(/Reduction[:\s]+([\d,]+)\s*tCO2e/i);
    const baseline = baseM ? parseFloat(baseM[1].replace(/,/g,"")) : sc.tco2e;
    const reduction = redM ? parseFloat(redM[1].replace(/,/g,"")) : Math.round(baseline * 0.3);
    const pct = baseline > 0 ? Math.round(reduction/baseline*100) : 30;
    const confidence = deal.evidence_readiness_pct || 50;
    return { baseline, reduction, pct, confidence,
      scope_1: Math.round(baseline * 0.60),
      scope_2: Math.round(baseline * 0.25),
      scope_3: Math.round(baseline * 0.15),
      source: baseM ? "Notes" : "Sector benchmark",
    };
  };

  const SOURCE_LABELS = {
    corporate_workspace_intake:"Corporate", sme_workspace:"SME",
    project_workspace:"Project", states_workspace:"States",
    fi_workspace_new_deal:"FI Direct", fi_excel_upload:"Excel Upload",
    fi_platform_import:"Platform Import",
  };

  const filtered = filter === "all"          ? deals.filter(d=>["screening","data_request"].includes(d.status)) :
                   filter === "data_request" ? deals.filter(d=>d.status==="data_request") :
                                               deals.filter(d=>d.status==="screening");
  const pendingCount = deals.filter(d=>d.status==="screening").length;
  const dataCount    = deals.filter(d=>d.status==="data_request").length;

  if (loading) return <div className="flex items-center justify-center py-10"><RefreshCw size={14} className="animate-spin text-teal-500"/></div>;

  return (
    <div className="space-y-4" data-testid="section-screening">
      {/* Step indicator */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl">
        <span className="text-[10px] font-bold bg-slate-900 text-white px-2 py-0.5 rounded">Step 1 of 3</span>
        <div>
          <p className="text-xs font-bold text-slate-800">Triage — Should we proceed with this deal?</p>
          <p className="text-[10px] text-slate-500">Click any deal to open Decision Panel. Route without leaving this page.</p>
        </div>
        <div className="ml-auto flex gap-1">
          {[["screening",`Pending (${pendingCount})`],["data_request",`Data Requested (${dataCount})`],["all","All"]].map(([k,l])=>(
            <button key={k} onClick={()=>setFilter(k)}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold ${filter===k?"bg-white border border-slate-200 text-slate-800 shadow-sm":"text-slate-500 hover:text-slate-700"}`}>{l}</button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-xl p-8 text-center">
          <Search size={24} className="mx-auto mb-2 text-slate-200"/>
          <p className="text-sm font-semibold text-slate-400">No deals at this stage</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(deal => {
            const flags = getFlags(deal);
            const mrv   = getMRV(deal);
            const source = deal.stage_history?.[0]?.source || "";
            const srcLabel = SOURCE_LABELS[source] || "Platform";
            const size   = _parseSize(deal);
            const isActing = acting === deal.deal_id;
            const isOpen   = panel?.deal_id === deal.deal_id;
            return (
              <div key={deal.deal_id}
                onClick={() => setPanel(deal)}
                className={`border rounded-xl p-4 cursor-pointer transition-all ${
                  isOpen ? "border-teal-400 bg-teal-50/30 shadow-sm" :
                  flags.length > 0 ? "border-amber-200 bg-amber-50/20 hover:border-amber-300" :
                  "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
                }`}
                data-testid={`screening-deal-${deal.deal_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${deal.status==="screening"?"bg-slate-100 text-slate-600":"bg-blue-100 text-blue-700"}`}>
                        {deal.status==="screening"?"Screening":"Data Requested"}
                      </span>
                      {deal.sector && <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded capitalize">{deal.sector}</span>}
                      {size && <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium">{size}</span>}
                      <span className="text-[10px] text-slate-400">{srcLabel}</span>
                      {/* MRV badge */}
                      <span title={mrv.tooltip} className={`text-[9px] font-bold px-1.5 py-0.5 rounded border cursor-help ${mrv.bg} ${mrv.color}`}>{mrv.label}</span>
                    </div>
                    <p className="text-sm font-bold text-slate-900">{_parseName(deal)}</p>
                    <p className="text-[10px] text-slate-400 font-mono">{deal.deal_id}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-[10px] text-teal-600 font-medium hidden sm:block">View Panel →</span>
                  </div>
                </div>
                {flags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {flags.map(f => (
                      <span key={f} className="flex items-center gap-1 text-[10px] text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                        <AlertTriangle size={8}/> {f}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── RIGHT-SIDE DECISION PANEL ── */}
      {panel && (
        <div className="fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div className="flex-1 bg-black/30" onClick={() => setPanel(null)}/>
          {/* Panel */}
          <div className="w-96 bg-white shadow-2xl flex flex-col h-full overflow-hidden" data-testid="screening-panel">
            {/* Panel header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-slate-50">
              <div>
                <p className="text-xs font-bold text-slate-900">Deal Triage Panel</p>
                <p className="text-[10px] text-slate-400">Review and route without leaving the page</p>
              </div>
              <button onClick={() => setPanel(null)} className="text-slate-400 hover:text-slate-700"><X size={16}/></button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto">
              {/* A. Deal Snapshot */}
              <div className="px-5 py-4 border-b border-slate-100">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">A. Deal Snapshot</p>
                <p className="text-sm font-bold text-slate-900 mb-1">{_parseName(panel)}</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                  {[
                    ["Sector",    panel.sector || "—"],
                    ["Size",      _parseSize(panel) || "Not provided"],
                    ["Source",    SOURCE_LABELS[panel.stage_history?.[0]?.source||""] || "Platform"],
                    ["Stage",     panel.stage_label || panel.status],
                    ["Deal ID",   panel.deal_id],
                  ].map(([l,v]) => (
                    <div key={l}><span className="text-slate-400">{l}: </span><span className="font-medium text-slate-700">{v}</span></div>
                  ))}
                </div>
                {/* MRV status */}
                <div className="mt-3">
                  {(() => { const mrv = getMRV(panel); return (
                    <div title={mrv.tooltip} className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded border cursor-help ${mrv.bg} ${mrv.color}`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${mrv.dot}`}/>
                      {mrv.label}
                    </div>
                  ); })()}
                  <p className="text-[10px] text-slate-400 mt-1">{getMRV(panel).tooltip}</p>
                </div>
              </div>

              {/* B. Climate Signal */}
              <div className="px-5 py-4 border-b border-slate-100">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">B. Climate Signal</p>
                {(() => {
                  const cs = getClimateSignal(panel);
                  return (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-slate-50 rounded-lg p-2.5">
                          <p className="text-[9px] text-slate-400 uppercase tracking-wider">Baseline</p>
                          <p className="text-sm font-bold text-slate-900">{cs.baseline.toLocaleString()}</p>
                          <p className="text-[10px] text-slate-400">tCO₂e/yr</p>
                        </div>
                        <div className="bg-teal-50 rounded-lg p-2.5">
                          <p className="text-[9px] text-slate-400 uppercase tracking-wider">Reduction</p>
                          <p className="text-sm font-bold text-teal-700">{cs.reduction.toLocaleString()}</p>
                          <p className="text-[10px] text-teal-600">{cs.pct}% reduction</p>
                        </div>
                      </div>
                      <div className="text-xs">
                        <p className="text-slate-400 mb-1">Scope breakdown (indicative)</p>
                        {[["Scope 1",cs.scope_1],["Scope 2",cs.scope_2],["Scope 3",cs.scope_3]].map(([s,v])=>(
                          <div key={s} className="flex justify-between py-0.5">
                            <span className="text-slate-500">{s}</span>
                            <span className="font-medium text-slate-700 tabular-nums">{v.toLocaleString()} tCO₂e</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-400">Data confidence</span>
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${cs.confidence>=70?"bg-emerald-500":cs.confidence>=40?"bg-amber-500":"bg-red-400"}`} style={{width:`${cs.confidence}%`}}/>
                          </div>
                          <span className={`font-bold ${cs.confidence>=70?"text-emerald-600":cs.confidence>=40?"text-amber-600":"text-red-500"}`}>{cs.confidence}%</span>
                        </div>
                      </div>
                      <p className="text-[9px] text-slate-400 italic">Source: {cs.source}. Indicative — not verified.</p>
                    </div>
                  );
                })()}
              </div>

              {/* C. Key Risks / Flags */}
              <div className="px-5 py-4">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">C. Key Risks & Flags</p>
                {(() => {
                  const flags = getFlags(panel);
                  const mrv   = getMRV(panel);
                  const allFlags = [
                    ...flags.map(f => ({ text: f, severity: "warning" })),
                    mrv.label !== "MRV Ready" ? { text: mrv.label + " — " + mrv.tooltip.split(".")[0], severity: "info" } : null,
                    !_parseScore(panel) ? { text: "Signal Score not computed — credit quality unverified", severity: "warning" } : null,
                  ].filter(Boolean);

                  return allFlags.length === 0 ? (
                    <div className="flex items-center gap-2 text-xs text-emerald-600">
                      <CheckCircle2 size={12}/> No critical flags identified
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {allFlags.map((f, i) => (
                        <div key={i} className={`flex items-start gap-2 text-xs px-2.5 py-1.5 rounded ${f.severity==="warning"?"bg-amber-50 text-amber-700":"bg-blue-50 text-blue-700"}`}>
                          <AlertTriangle size={10} className="flex-shrink-0 mt-0.5"/>
                          <span>{f.text}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* D. Sticky actions */}
            <div className="border-t border-slate-200 px-5 py-4 bg-white space-y-2">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">D. Routing Decision</p>
              {panel.status === "screening" ? (
                <>
                  <button onClick={() => advance(panel.deal_id, "advance_to_data_request")}
                    disabled={acting === panel.deal_id}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 disabled:opacity-40"
                    data-testid="panel-proceed">
                    <ArrowRight size={12}/> Proceed to Eligibility
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => advance(panel.deal_id, "request_more_info")}
                      disabled={acting === panel.deal_id}
                      className="py-2 border border-amber-300 text-amber-700 text-[11px] font-bold rounded-xl hover:bg-amber-50 disabled:opacity-40"
                      data-testid="panel-request">Request Data</button>
                    <button onClick={() => { setRejectDeal(panel.deal_id); setRejectReason(""); }}
                      className="py-2 border border-red-200 text-red-600 text-[11px] font-bold rounded-xl hover:bg-red-50"
                      data-testid="panel-reject">Reject</button>
                  </div>
                </>
              ) : (
                <button onClick={() => advance(panel.deal_id, "advance_to_due_diligence")}
                  disabled={acting === panel.deal_id}
                  className="w-full py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 disabled:opacity-40">
                  Data Received — Proceed to DD
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject confirmation modal */}
      {rejectDeal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl p-5 w-full max-w-sm shadow-xl">
            <p className="text-sm font-bold text-slate-900 mb-3">Confirm Rejection</p>
            <textarea value={rejectReason} onChange={e=>setRejectReason(e.target.value)} rows={3}
              placeholder="Reason for rejection (recorded in audit trail)..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl resize-none focus:outline-none mb-3"/>
            <div className="flex gap-2">
              <button disabled={!rejectReason.trim()} onClick={()=>{advance(rejectDeal,"not_suitable",rejectReason);setRejectDeal(null);}}
                className="flex-1 py-2 bg-red-600 text-white text-xs font-bold rounded-xl hover:bg-red-700 disabled:opacity-40">Confirm</button>
              <button onClick={()=>setRejectDeal(null)} className="px-4 py-2 border text-xs text-slate-600 rounded-xl">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── STEP 2: ELIGIBILITY ENGINE — Qualification Layer ─────────────────────
// Purpose: Does this deal qualify under climate finance frameworks?
// Rule-based policy check. NO scoring. NO ranking.
function SectionEligibility({ onSection }) {
  const [deals, setDeals]       = useState([]);
  const [selected, setSelected] = useState(null);
  const [running, setRunning]   = useState(false);
  const [result, setResult]     = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    client.get("/fi/decisions/deals").then(r => {
      const ds = (r.data.deals||[]).filter(d => !["rejected","financial_close"].includes(d.status));
      setDeals(ds);
      if (ds.length > 0) setSelected(ds[0]);
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const runEligibility = async () => {
    if (!selected) return;
    setRunning(true); setResult(null);
    try {
      const r = await client.post(`/fi/eligibility/signal-score/from-deal/${selected.deal_id}`, {});
      setResult({ data: r.data, deal: selected });
    } catch {
      try {
        const sc = SECTOR_CARBON[selected.sector] || { tco2e: 5000 };
        const r2 = await client.post("/fi/eligibility/signal-score", {
          annual_reduction_tco2e: sc.tco2e * 0.3,
          baseline_tco2e: sc.tco2e,
          confidence_pct: 60,
          evidence_coverage_pct: 50,
          periods_tracked: 4, periods_locked: 2,
          verification_status: "none",
          amount_usd: selected.deployed_amount_usd || 5000000,
          instrument_type: "green_loan",
          org_type: "project",
          sector: selected.sector || "energy",
          country: "NG",
          ghg_protocol_compliant: true,
          taxonomy_eligible: "conditional",
        });
        setResult({ data: r2.data, deal: selected });
      } catch { toast.error("Eligibility assessment failed."); }
    }
    setRunning(false);
  };

  // Financing pathways for eligible deals (policy-based, not score-based)
  const PATHWAYS = {
    energy:       ["Green Loan", "Project Finance"],
    biogas:       ["Green Loan", "Carbon Project Finance"],
    cement:       ["Transition Finance", "Sustainability-Linked Loan"],
    waste:        ["Green Loan", "Carbon Project Finance"],
    agriculture:  ["Green Loan", "Blended Finance"],
    transport:    ["Transition Finance", "Sustainability-Linked Loan"],
    manufacturing:["Transition Finance"],
  };

  const sig = result?.data;
  const totalScore = sig?.total_score || 0;

  // Qualification verdict — policy outcome, not a score
  const getVerdict = () => {
    const elig = _parseElig(selected);
    if (totalScore >= 55 || elig === "eligible") return {
      label: "Eligible",
      sub: "Qualifies for climate finance under applicable frameworks",
      color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-300",
      badge: "bg-emerald-100 text-emerald-700 border-emerald-300",
    };
    if (totalScore >= 35 || elig === "conditional") return {
      label: "Conditionally Eligible",
      sub: "Qualifies with conditions — specific gaps must be addressed first",
      color: "text-amber-700", bg: "bg-amber-50 border-amber-300",
      badge: "bg-amber-100 text-amber-700 border-amber-300",
    };
    return {
      label: "Not Eligible",
      sub: "Does not qualify under current frameworks — remediation or rejection required",
      color: "text-red-700", bg: "bg-red-50 border-red-200",
      badge: "bg-red-100 text-red-700 border-red-200",
    };
  };

  if (loading) return <div className="flex items-center justify-center py-10"><RefreshCw size={14} className="animate-spin text-teal-500"/></div>;

  const verdict = result ? getVerdict() : null;
  const pathways = selected ? (PATHWAYS[selected.sector] || ["Green Loan"]) : [];

  return (
    <div className="space-y-4" data-testid="section-eligibility">
      {/* Step indicator */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl">
        <span className="text-[10px] font-bold bg-slate-900 text-white px-2 py-0.5 rounded">Step 2 of 3</span>
        <div>
          <p className="text-xs font-bold text-slate-800">Qualification — Does this deal qualify for climate finance?</p>
          <p className="text-[10px] text-slate-500">Policy-based rules: taxonomy alignment, additionality, data quality. No scoring. Outputs: Eligible / Conditional / Not Eligible.</p>
        </div>
      </div>

      {/* Deal selector */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <label className="block text-[10px] text-slate-500 font-semibold uppercase tracking-wider mb-1">Select Deal to Assess</label>
          <select value={selected?.deal_id||""}
            onChange={e => { setSelected(deals.find(d=>d.deal_id===e.target.value)||null); setResult(null); }}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none"
            data-testid="eligibility-deal-select">
            {deals.map(d => <option key={d.deal_id} value={d.deal_id}>{d.deal_id} — {d.stage_label||d.status}</option>)}
          </select>
        </div>
        <div className="flex-shrink-0 mt-5">
          <button onClick={runEligibility} disabled={running||!selected}
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-xl hover:bg-slate-800 disabled:opacity-40"
            data-testid="run-eligibility-btn">
            {running?<RefreshCw size={12} className="animate-spin"/>:<CheckCircle2 size={12}/>}
            {running?"Assessing...":"Run Eligibility Check"}
          </button>
        </div>
      </div>

      {/* Current eligibility status from notes */}
      {selected && (
        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs">
          <span className="text-slate-400">Current record:</span>
          {_parseElig(selected) ? (
            <span className={`font-bold px-2 py-0.5 rounded ${
              _parseElig(selected)==="eligible"?"bg-emerald-100 text-emerald-700":
              _parseElig(selected)==="conditional"?"bg-amber-100 text-amber-700":
              "bg-red-100 text-red-600"}`}>
              {_parseElig(selected)==="eligible"?"Eligibility Cleared":_parseElig(selected)==="conditional"?"Conditionally Eligible":"Eligibility Blocked"}
            </span>
          ) : <span className="text-slate-400 italic">No eligibility record — run assessment below</span>}
        </div>
      )}

      {!result && !running && (
        <div className="border border-dashed border-slate-200 rounded-xl p-8 text-center">
          <CheckCircle2 size={24} className="mx-auto mb-2 text-slate-200"/>
          <p className="text-sm text-slate-400">Select a deal and run the eligibility check</p>
          <p className="text-[11px] text-slate-300 mt-1">Assessment applies climate finance policy rules — 4-gate framework</p>
        </div>
      )}

      {result && verdict && (
        <div className="space-y-3">
          {/* VERDICT — the primary output */}
          <div className={`rounded-xl border-2 p-5 ${verdict.bg}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1">Qualification Verdict</p>
                <p className={`text-2xl font-bold ${verdict.color}`}>{verdict.label}</p>
                <p className="text-xs text-slate-600 mt-1">{verdict.sub}</p>
              </div>
              <span className={`text-xs font-bold px-3 py-1.5 rounded-lg border flex-shrink-0 mt-1 ${verdict.badge}`}>
                {verdict.label}
              </span>
            </div>
          </div>

          {/* Gate breakdown */}
          {(sig?.score_table||[]).length > 0 && (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider px-4 py-2 bg-slate-50 border-b">Assessment Breakdown (4-Gate Framework)</p>
              {(sig.score_table||[]).map(c => {
                const pass = c.pct >= 50;
                const cond = c.pct >= 30 && c.pct < 50;
                return (
                  <div key={c.component} className={`flex items-center gap-3 px-4 py-2.5 border-b border-slate-50 last:border-0`}>
                    <span className={`flex-shrink-0 text-sm ${pass?"text-emerald-500":cond?"text-amber-500":"text-red-400"}`}>
                      {pass?"✓":cond?"~":"✗"}
                    </span>
                    <p className="text-xs text-slate-700 flex-1">{c.component}</p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${pass?"bg-emerald-50 text-emerald-700":cond?"bg-amber-50 text-amber-700":"bg-red-50 text-red-600"}`}>
                      {pass?"Pass":cond?"Conditional":"Fail"}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Financing pathways (for eligible deals) */}
          {verdict.label !== "Not Eligible" && (
            <div className="border border-slate-200 rounded-xl p-4">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Applicable Financing Pathways</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {pathways.map(p => (
                  <span key={p} className="text-[11px] font-bold px-2.5 py-1 bg-teal-50 border border-teal-200 text-teal-700 rounded-lg">{p}</span>
                ))}
              </div>
              <button onClick={() => onSection?.("signal_score")}
                className="flex items-center gap-1.5 text-xs font-bold text-teal-600 hover:text-teal-800"
                data-testid="proceed-to-signal">
                <ArrowRight size={11}/> Next: Assess Investment Attractiveness — Signal Score
              </button>
            </div>
          )}

          {/* Required actions for non-eligible */}
          {verdict.label === "Not Eligible" && sig?.flags && (
            <div className="border border-red-200 rounded-xl p-4 bg-red-50">
              <p className="text-[10px] font-bold text-red-700 uppercase tracking-wider mb-2">Required Actions to Qualify</p>
              <div className="space-y-1.5">
                {sig.flags.filter(f=>f.severity==="critical"||f.urgency==="high").slice(0,4).map((f,i)=>(
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <span className="text-red-500 font-bold flex-shrink-0">→</span>
                    <span className="text-red-700">{f.key_issue||f.component}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── STEP 3: SIGNAL SCORE — Prioritization Layer ───────────────────────────
function SectionSignalScore() {
  const [deals, setDeals]   = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [manualOverrides, setManualOverrides] = useState({});
  const [form, setForm]     = useState({
    annual_reduction_tco2e:"", baseline_tco2e:"", confidence_pct:65,
    evidence_coverage_pct:50, periods_tracked:6, periods_locked:3,
    verification_status:"none", amount_usd:"", instrument_type:"green_loan",
    org_type:"project", sector:"energy", country:"NG",
    ghg_protocol_compliant:true, taxonomy_eligible:"conditional",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dealsLoading, setDealsLoading] = useState(true);
  const [portfolioAvg, setPortfolioAvg] = useState(null);

  useEffect(() => {
    client.get("/fi/decisions/deals").then(r => {
      const ds = r.data.deals || [];
      setDeals(ds);
      const scores = ds.map(_parseScore).filter(s => s !== null);
      if (scores.length) setPortfolioAvg(Math.round(scores.reduce((a,b)=>a+b,0)/scores.length));
    }).catch(()=>{}).finally(()=>setDealsLoading(false));
  }, []);

  const autoPopulate = (deal) => {
    if (!deal) return;
    const sc = SECTOR_CARBON[deal.sector] || { tco2e: 5000 };
    const sizeM = _parseSize(deal);
    setForm({
      annual_reduction_tco2e: String(Math.round(sc.tco2e * 0.3)),
      baseline_tco2e:         String(sc.tco2e),
      confidence_pct:         Math.max(50, deal.evidence_readiness_pct || 60),
      evidence_coverage_pct:  Math.max(40, deal.evidence_readiness_pct || 50),
      periods_tracked:        4, periods_locked: 2,
      verification_status:    "none",
      amount_usd:             sizeM ? String(parseFloat(sizeM.replace(/[$MK]/g,"")) * (sizeM.includes("M")?1e6:1e3)) : (deal.deployed_amount_usd ? String(deal.deployed_amount_usd) : ""),
      instrument_type:        "green_loan",
      org_type:               "project",
      sector:                 deal.sector || "energy",
      country:                _parseCountry(deal) || "NG",
      ghg_protocol_compliant: true,
      taxonomy_eligible:      _parseElig(deal) === "eligible" ? "eligible" : "conditional",
    });
    setManualOverrides({}); setResult(null);
  };

  const handleDealSelect = (dealId) => {
    const deal = deals.find(d=>d.deal_id===dealId)||null;
    setSelectedDeal(deal);
    autoPopulate(deal);
  };

  const setField = (key, val) => {
    setForm(f=>({...f,[key]:val}));
    setManualOverrides(m=>({...m,[key]:true}));
  };

  const run = async () => {
    setLoading(true);
    try {
      const res = await client.post("/fi/eligibility/signal-score", {
        ...form,
        annual_reduction_tco2e: parseFloat(form.annual_reduction_tco2e)||0,
        baseline_tco2e:         parseFloat(form.baseline_tco2e)||0,
        amount_usd:             parseFloat(form.amount_usd)||0,
      });
      setResult(res.data);
    } catch { toast.error("Score calculation failed."); }
    setLoading(false);
  };

  // ── Decision bands (non-negotiable routing logic) ──────────────────────
  const BANDS = [
    { min:75, max:100, key:"fast_track",  label:"Strong — Fast-Track to Credit",        rec:"Score qualifies for fast-track. Proceed directly to Credit Engine.",   color:"text-emerald-700", bg:"bg-emerald-50 border-emerald-300", ctaColor:"bg-emerald-600 hover:bg-emerald-700" },
    { min:55, max:74,  key:"conditional", label:"Conditional — Proceed with Flags",      rec:"Score is adequate. Proceed to Credit Engine with noted conditions.",   color:"text-teal-700",    bg:"bg-teal-50 border-teal-200",       ctaColor:"bg-teal-600 hover:bg-teal-700" },
    { min:35, max:54,  key:"improve",     label:"Weak — Improve Before Proceeding",      rec:"Score below conditional threshold. Address flagged gaps and re-score.", color:"text-amber-700",   bg:"bg-amber-50 border-amber-200",     ctaColor:"bg-amber-600 hover:bg-amber-700" },
    { min:0,  max:34,  key:"park",        label:"Not Viable — Park or Reject",           rec:"Score is insufficient. Reject or park for resubmission if data improves.", color:"text-red-700", bg:"bg-red-50 border-red-200",         ctaColor:"bg-red-600 hover:bg-red-700" },
  ];
  const getBand = (score) => BANDS.find(b => score >= b.min && score <= b.max) || BANDS[3];

  const getBenchmark = (score) => {
    if (!portfolioAvg) return null;
    if (score >= portfolioAvg + 10) return { text:`Above portfolio avg (${portfolioAvg}/100)`, color:"text-emerald-600" };
    if (score <= portfolioAvg - 10) return { text:`Below portfolio avg (${portfolioAvg}/100)`, color:"text-red-500" };
    return { text:`Near portfolio avg (${portfolioAvg}/100)`, color:"text-slate-500" };
  };

  const band = result ? getBand(result.total_score) : null;

  return (
    <div className="space-y-4" data-testid="section-signal-score">
      {/* Step indicator */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl">
        <span className="text-[10px] font-bold bg-slate-900 text-white px-2 py-0.5 rounded">Step 3 of 3</span>
        <div>
          <p className="text-xs font-bold text-slate-800">Prioritization — How strong is this deal as an investment?</p>
          <p className="text-[10px] text-slate-500">Score determines routing to Credit Engine. Score drives decision — not a dead-end calculator.</p>
        </div>
      </div>

      {/* Scope notice */}
      <div className="flex items-start gap-2 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-xl">
        <Info size={12} className="text-blue-500 flex-shrink-0 mt-0.5"/>
        <p className="text-[11px] text-blue-700">
          <strong>Prioritization tool only.</strong> Run Eligibility Engine (Step 2) before scoring.
          A high score does not override eligibility requirements. Score drives Credit Engine routing.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-5">
        {/* Input form */}
        <div className="space-y-3">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Deal Parameters
            {selectedDeal && <span className="ml-2 text-[9px] bg-teal-100 text-teal-700 font-bold px-1.5 py-0.5 rounded normal-case">Auto-filled · {_parseName(selectedDeal).split(" ")[0]}</span>}
          </p>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Auto-populate from deal</label>
            <select value={selectedDeal?.deal_id||""} onChange={e=>handleDealSelect(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none"
              data-testid="signal-score-deal-select">
              <option value="">Manual entry</option>
              {deals.map(d=><option key={d.deal_id} value={d.deal_id}>{d.deal_id} — {_parseName(d)}</option>)}
            </select>
          </div>
          {Object.keys(manualOverrides).length > 0 && (
            <p className="text-[10px] text-amber-600 flex items-center gap-1"><AlertTriangle size={9}/> {Object.keys(manualOverrides).length} field{Object.keys(manualOverrides).length>1?"s":""} overridden — audit flagged</p>
          )}
          {[
            { key:"annual_reduction_tco2e", label:"Annual Reduction (tCO₂e/yr)", type:"number" },
            { key:"baseline_tco2e",         label:"Baseline Emissions (tCO₂e)",  type:"number" },
            { key:"confidence_pct",         label:"Data Confidence (%)",          type:"number" },
            { key:"amount_usd",             label:"Finance Amount (USD)",          type:"number" },
          ].map(f => (
            <div key={f.key}>
              <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center gap-1.5">
                {f.label}
                {manualOverrides[f.key] && <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 rounded">Override</span>}
              </label>
              <input type={f.type} value={form[f.key]} onChange={e=>setField(f.key, e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:border-teal-400 ${manualOverrides[f.key]?"border-amber-300 bg-amber-50":"border-slate-200"}`}/>
            </div>
          ))}
          <div className="grid grid-cols-2 gap-2">
            {[{key:"sector",opts:Object.keys(SECTOR_CARBON),label:"Sector"},{key:"taxonomy_eligible",opts:["eligible","conditional","not_eligible"],label:"Taxonomy"}].map(f=>(
              <div key={f.key}>
                <label className="block text-xs font-medium text-slate-600 mb-1">{f.label}</label>
                <select value={form[f.key]} onChange={e=>setField(f.key,e.target.value)} className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl">
                  {f.opts.map(o=><option key={o} value={o}>{o.replace(/_/g," ")}</option>)}
                </select>
              </div>
            ))}
          </div>
          <button onClick={run} disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-bold rounded-xl hover:bg-slate-800"
            data-testid="run-signal-score">
            {loading?<RefreshCw size={13} className="animate-spin"/>:<Target size={13}/>}
            Calculate Investment Score
          </button>
        </div>

        {/* Results */}
        <div>
          {result && band ? (
            <div className="space-y-3">
              {/* Decision band — primary output */}
              <div className={`rounded-xl border-2 p-5 ${band.bg}`}>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-2">Investment Signal</p>
                <div className="flex items-end gap-3 mb-2">
                  <p className={`text-3xl font-bold tabular-nums leading-none ${band.color}`}>
                    {result.total_score}
                  </p>
                  <p className="text-slate-400 text-sm mb-0.5">/100</p>
                </div>
                <p className={`text-sm font-bold ${band.color}`}>{band.label}</p>
                <p className="text-xs text-slate-600 mt-1">{band.rec}</p>
                {getBenchmark(result.total_score) && (
                  <p className={`text-[11px] mt-1.5 font-medium ${getBenchmark(result.total_score).color}`}>
                    {getBenchmark(result.total_score).text}
                  </p>
                )}
              </div>

              {/* Decision band table — visible logic */}
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider px-4 py-2 bg-slate-50 border-b">Decision Routing Logic</p>
                {BANDS.map(b => (
                  <div key={b.key} className={`flex items-center gap-3 px-4 py-2 text-xs border-b border-slate-50 last:border-0 ${band.key===b.key?`${b.bg} font-semibold`:""}`}>
                    <span className={`w-16 flex-shrink-0 font-mono text-[11px] ${band.key===b.key?b.color:"text-slate-400"}`}>{b.min}–{b.max}</span>
                    <span className={`flex-1 ${band.key===b.key?b.color:"text-slate-500"}`}>{b.label}</span>
                    {band.key===b.key && <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${b.bg} ${b.color}`}>CURRENT</span>}
                  </div>
                ))}
              </div>

              {/* Score components */}
              {result.score_table?.length > 0 && (
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider px-4 py-2 bg-slate-50 border-b">Score Components</p>
                  {result.score_table.map(c => (
                    <div key={c.component} className="px-4 py-2.5 border-b border-slate-50 last:border-0">
                      <div className="flex justify-between mb-1">
                        <p className="text-xs font-medium text-slate-700">{c.component}</p>
                        <span className="text-xs font-bold tabular-nums">{c.score}/{c.max}</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${c.pct>=75?"bg-emerald-500":c.pct>=50?"bg-teal-500":c.pct>=30?"bg-amber-500":"bg-red-400"}`} style={{width:`${c.pct}%`}}/>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── CTA: Route to Credit Engine ── */}
              <div className="border-2 border-slate-900 rounded-xl p-4 bg-slate-900 text-white">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Next Step</p>
                <p className="text-xs text-slate-300 mb-3">
                  {band.key === "fast_track"  ? "Score qualifies for direct Credit Engine submission." :
                   band.key === "conditional" ? "Score passes with conditions. Proceed with flags noted." :
                   band.key === "improve"     ? "Resolve flagged gaps before Credit Engine submission." :
                   "Score is insufficient for Credit Engine. Reject or park for resubmission."}
                </p>
                {band.key !== "park" ? (
                  <button
                    onClick={() => {
                      // Navigate to Credit Engine → Deal Actions with context
                      if (typeof window !== "undefined") {
                        window.history.pushState({}, "", `/fi/workspace?m=credit&s=decision`);
                        window.dispatchEvent(new PopStateEvent("popstate"));
                      }
                      toast.info(`Score ${result.total_score}/100 — proceeding to Credit Engine with flags.`);
                    }}
                    className={`w-full flex items-center justify-center gap-2 py-2.5 text-white text-sm font-bold rounded-xl transition-colors ${band.ctaColor}`}
                    data-testid="proceed-to-credit">
                    <ArrowRight size={13}/> Proceed to Credit Engine
                  </button>
                ) : (
                  <p className="text-xs text-red-400 font-semibold">Reject or park — score insufficient for Credit Engine</p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full min-h-[280px]">
              <div className="text-center">
                <Target size={32} className="mx-auto mb-3 text-slate-200"/>
                <p className="text-sm text-slate-400">Select a deal and calculate</p>
                <p className="text-xs text-slate-300 mt-1">Score will determine Credit Engine routing</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── CARBON DESK: Carbon Opportunity & Readiness (replaces basic Carbon Analysis) ──
// Answers: Which deals can generate carbon revenue, through which pathway, and what next?
function SectionCarbonStructuring({ navigate }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workflow, setWorkflow] = useState(null); // { deal, type }
  const [workflowReason, setWorkflowReason] = useState("");
  // Methodology Engine state
  const [methEngine, setMethEngine] = useState(null); // { deal, mode: "vcm"|"art6" }
  const [methSelection, setMethSelection] = useState(null); // API selection result
  const [methInputs, setMethInputs] = useState({}); // user parameter overrides
  const [methTrace, setMethTrace] = useState(null); // computed trace
  const [methLoading, setMethLoading] = useState(false);
  const [methComputing, setMethComputing] = useState(false);
  const [methExporting, setMethExporting] = useState(false);
  const [methFA, setMethFA]               = useState(null);
  const [methFALoading, setMethFALoading] = useState(false);
  const [methCarbonPrice, setMethCarbonPrice] = useState(15);
  const [methShowInputs, setMethShowInputs]   = useState(false);   // collapsed by default
  const [overriddenParams, setOverriddenParams] = useState(new Set()); // locked by default
  // Phase 6: Art.6 tracker
  const [art6Panel, setArt6Panel]   = useState(null);
  const [art6Loading, setArt6Loading] = useState(false);
  const [art6Updating, setArt6Updating] = useState(null);
  const [methTechType, setMethTechType] = useState("");
  const [methBoundary, setMethBoundary] = useState("");
  const [methOutput, setMethOutput] = useState("");

  // Per-sector carbon pricing (realistic, by project type)
  const PRICING = {
    energy:       { low:10, base:18, high:30, art6_low:20, art6_base:40, art6_high:60 },
    biogas:       { low:10, base:18, high:30, art6_low:20, art6_base:40, art6_high:60 },
    cement:       { low:8,  base:15, high:25, art6_low:15, art6_base:30, art6_high:50 },
    waste:        { low:8,  base:15, high:25, art6_low:15, art6_base:35, art6_high:50 },
    agriculture:  { low:6,  base:12, high:20, art6_low:12, art6_base:25, art6_high:40 },
    transport:    { low:5,  base:10, high:18, art6_low:10, art6_base:20, art6_high:35 },
    manufacturing:{ low:6,  base:12, high:20, art6_low:12, art6_base:25, art6_high:40 },
  };

  useEffect(() => {
    client.get("/fi/decisions/deals").then(r => setDeals(r.data.deals||[])).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  // ── Sector-based auto-estimates for primary activity parameter ─────────────
  const buildAutoInputs = (deal, schema) => {
    const defaults = {};
    (schema || []).forEach(p => {
      if (p.default !== null && p.default !== undefined) defaults[p.key] = p.default;
    });
    // Build set of all schema keys so we can fill required params even if they have no default
    const schemaKeys = new Set((schema || []).map(p => p.key));
    const amount = deal.deployed_amount_usd || 5000000;
    // Estimate primary activity parameter from facility size + sector unit economics
    const activityEstimates = {
      waste_input_t_yr:             Math.round(amount / 350),
      electricity_generated_kwh_yr: Math.round(amount / 1500 * 8760 * 0.22 * 1000),
      clinker_production_t_yr:      Math.round(amount / 35),
      waste_diverted_t_yr:          Math.round(amount / 250),
      land_area_ha:                 Math.round(amount / 3000),
      vehicle_km_yr:                Math.round(amount / 0.15),
      production_output_units_yr:   Math.round(amount / 50),
    };
    Object.entries(activityEstimates).forEach(([key, val]) => {
      if (schemaKeys.has(key)) defaults[key] = Math.max(val, 100);
    });
    return defaults;
  };

  // ── Auto-compute flow: select + compute + FA all on modal open ─────────────
  const openMethEngine = async (deal, mode) => {
    setMethEngine({ deal, mode });
    setMethSelection(null); setMethTrace(null); setMethFA(null);
    setMethTechType(""); setMethBoundary(""); setMethOutput("");
    setMethShowInputs(false); setOverriddenParams(new Set());
    setMethLoading(true);
    try {
      const selResp = await client.post(`/fi/methodology/select/${deal.deal_id}`, {});
      setMethSelection(selResp.data);
      const autoInputs = buildAutoInputs(deal, selResp.data.input_schema);
      setMethInputs(autoInputs);
      const methodKey = selResp.data.selection.primary_methodology;
      const compResp = await client.post(`/fi/methodology/compute/${deal.deal_id}`, {
        methodology_key: methodKey, inputs: autoInputs,
      });
      setMethTrace(compResp.data);
      setMethFALoading(true);
      client.post(`/fi/methodology/financial-additionality/${deal.deal_id}`, {
        methodology_key: methodKey,
        carbon_reduction_t_yr: compResp.data.results?.net_reduction_t_yr,
        confidence_pct: compResp.data.confidence?.overall_pct,
        tenor_years: compResp.data.results?.crediting_period_yr,
      }).then(r => setMethFA(r.data)).catch(() => {}).finally(() => setMethFALoading(false));
    } catch { toast.error("Auto-calculation failed — try adjusting assumptions."); }
    setMethLoading(false);
  };

  // ── Recompute after assumption override (debounced) ────────────────────────
  const recomputeWithInputs = async (newInputs) => {
    if (!methSelection || !methEngine) return;
    const methodKey = methSelection.selection.primary_methodology;
    try {
      const compResp = await client.post(`/fi/methodology/compute/${methEngine.deal.deal_id}`, {
        methodology_key: methodKey, inputs: newInputs,
      });
      setMethTrace(compResp.data);
      setMethFALoading(true);
      client.post(`/fi/methodology/financial-additionality/${methEngine.deal.deal_id}`, {
        methodology_key: methodKey,
        carbon_reduction_t_yr: compResp.data.results?.net_reduction_t_yr,
        confidence_pct: compResp.data.confidence?.overall_pct,
        tenor_years: compResp.data.results?.crediting_period_yr,
      }).then(r => setMethFA(r.data)).catch(() => {}).finally(() => setMethFALoading(false));
    } catch { /* silent — show stale result */ }
  };

  const refineSelection = async () => {
    if (!methEngine) return;
    setMethLoading(true);
    try {
      const resp = await client.post(`/fi/methodology/select/${methEngine.deal.deal_id}`, {
        technology_type: methTechType || undefined,
        project_boundary: methBoundary || undefined,
        primary_output: methOutput || undefined,
      });
      setMethSelection(resp.data);
      setMethTrace(null);
      const defaults = {};
      (resp.data.input_schema || []).forEach(p => { if (p.default !== null && p.default !== undefined) defaults[p.key] = p.default; });
      setMethInputs(prev => ({ ...defaults, ...prev }));
    } catch { toast.error("Refinement failed."); }
    setMethLoading(false);
  };

  const computeTrace = async () => {
    if (!methSelection || !methEngine) return;
    setMethComputing(true);
    setMethFA(null);
    try {
      const methodKey = methSelection.selection.primary_methodology;
      const resp = await client.post(`/fi/methodology/compute/${methEngine.deal.deal_id}`, { methodology_key: methodKey, inputs: methInputs });
      setMethTrace(resp.data);
      // Phase 5: Auto-compute financial additionality using methodology results
      const carbonTYr = resp.data.results?.net_reduction_t_yr;
      const confPct = resp.data.confidence?.overall_pct;
      const credYrs = resp.data.results?.crediting_period_yr;
      setMethFALoading(true);
      client.post(`/fi/methodology/financial-additionality/${methEngine.deal.deal_id}`, {
        methodology_key: methodKey,
        carbon_reduction_t_yr: carbonTYr,
        confidence_pct: confPct,
        tenor_years: credYrs,
      }).then(r => { setMethFA(r.data); }).catch(() => {}).finally(() => setMethFALoading(false));
      toast.success(`${methodKey} computed — financial additionality calculating...`);
    } catch { toast.error("Calculation failed."); }
    setMethComputing(false);
  };

  // Phase 5: Recompute FA with updated carbon price
  const recomputeFA = async (priceOverride) => {
    if (!methEngine || !methTrace) return;
    setMethFALoading(true);
    try {
      const methodKey = methSelection?.selection?.primary_methodology;
      const r = await client.post(`/fi/methodology/financial-additionality/${methEngine.deal.deal_id}`, {
        methodology_key: methodKey,
        carbon_reduction_t_yr: methTrace.results?.net_reduction_t_yr,
        confidence_pct: methTrace.confidence?.overall_pct,
        tenor_years: methTrace.results?.crediting_period_yr,
        carbon_price_usd_t: priceOverride ?? methCarbonPrice,
      });
      setMethFA(r.data);
    } catch { toast.error("Financial additionality recompute failed."); }
    setMethFALoading(false);
  };


  // Phase 6: Art.6 Tracker
  const openArt6Tracker = async (deal) => {
    setArt6Panel({ deal, tracker: null, readiness: null });
    setArt6Loading(true);
    try {
      const resp = await client.get(`/fi/art6/tracker/${deal.deal_id}`);
      setArt6Panel({ deal, ...resp.data });
    } catch { toast.error("Failed to load Art.6 tracker."); }
    setArt6Loading(false);
  };

  const updateArt6Stage = async (stageId, newStatus, owner, nextAction) => {
    if (!art6Panel) return;
    setArt6Updating(stageId);
    try {
      await client.post(`/fi/art6/tracker/${art6Panel.deal.deal_id}/stage`, {
        stage_id: stageId, status: newStatus,
        owner: owner || undefined, next_action: nextAction || undefined,
      });
      // Reload tracker
      const resp = await client.get(`/fi/art6/tracker/${art6Panel.deal.deal_id}`);
      setArt6Panel(prev => ({ ...prev, ...resp.data }));
      toast.success("Stage updated.");
    } catch { toast.error("Failed to update stage."); }
    setArt6Updating(null);
  };


  const generatePDDWithTrace = async () => {
    if (!methEngine) return;
    setMethExporting(true);
    try {
      const resp = await client.post(`/fi/pdf/vcm-pdd/${methEngine.deal.deal_id}`, {}, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `DeCarbonIQ_VCM_${methEngine.deal.deal_id}_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success("VCM Project Document exported with methodology-anchored calculations.");
      setMethEngine(null);
    } catch { toast.error("PDF generation failed."); }
    setMethExporting(false);
  };



  const nonRejected = deals.filter(d => d.status !== "rejected");

  // Carbon readiness per deal
  const getCarbonReadiness = (deal) => {
    const ev     = deal.evidence_readiness_pct || 0;
    const elig   = _parseElig(deal);
    const notes  = (deal.notes||"").toLowerCase();
    const sc     = SECTOR_CARBON[deal.sector];
    const vcmOk  = sc?.vcm === "Yes";
    const art6Ok = sc?.art6 && sc.art6 !== "No" && sc.art6 !== "TBD";

    let readiness;
    if (ev >= 70 && elig !== "blocked" && vcmOk) readiness = "Ready for PDD";
    else if (ev >= 30 || elig === "conditional" || notes.includes("mrv")) readiness = "Pre-Feasibility";
    else readiness = "Not Ready";

    const dataGaps = [];
    if (!deal.deployed_amount_usd) dataGaps.push("Facility size");
    if (ev < 40) dataGaps.push("Evidence quality");
    if (!notes.includes("mrv") && !notes.includes("monitoring")) dataGaps.push("MRV plan");
    if (elig === null) dataGaps.push("Eligibility assessment");

    const sc_data = SECTOR_CARBON[deal.sector] || { tco2e: 5000 };
    const tco2e   = sc_data.tco2e;
    const pricing = PRICING[deal.sector] || { low:8, base:15, high:30 };
    const baseRevenue = tco2e * pricing.base;

    return { readiness, vcmOk, art6Ok, tco2e, pricing, baseRevenue, dataGaps, elig };
  };

  const dealData = nonRejected.map(d => ({ deal: d, carbon: getCarbonReadiness(d) }));

  // Portfolio summary
  const totalTco2e     = dealData.reduce((s,d)=>s+d.carbon.tco2e, 0);
  const totalBase      = dealData.reduce((s,d)=>s+d.carbon.baseRevenue, 0);
  const vcmReadyCount  = dealData.filter(d=>d.carbon.vcmOk).length;
  const art6ReadyCount = dealData.filter(d=>d.carbon.art6Ok).length;
  const missingData    = dealData.filter(d=>d.carbon.dataGaps.length >= 2).length;
  const pddReadyCount  = dealData.filter(d=>d.carbon.readiness==="Ready for PDD").length;

  // Insights
  const INSIGHTS = [];
  INSIGHTS.push(`${vcmReadyCount} of ${nonRejected.length} deal${nonRejected.length!==1?"s":""} are VCM-eligible`);
  INSIGHTS.push(`${art6ReadyCount} deal${art6ReadyCount!==1?"s":""} potentially suitable for Article 6`);
  const biogasDeals = dealData.filter(d=>d.deal.sector==="biogas");
  if (biogasDeals.length > 0) {
    const biogas_pct = Math.round(biogasDeals.reduce((s,d)=>s+d.carbon.baseRevenue,0)/totalBase*100);
    INSIGHTS.push(`Biogas projects represent ${biogas_pct}% of portfolio carbon revenue potential`);
  }
  const cementDeals = dealData.filter(d=>d.deal.sector==="cement");
  if (cementDeals.length > 0) INSIGHTS.push("Cement deals require additionality validation before VCM submission");
  if (missingData > 0) INSIGHTS.push(`${missingData} deal${missingData!==1?"s":""} require additional data before carbon structuring`);

  // Workflow modal content
  const WORKFLOW_CONTENT = {
    vcm: (deal, carbon) => ({
      title: "Prepare for VCM",
      steps: [
        { label:"Data Completeness Check", status: carbon.readiness==="Ready for PDD"?"complete":"pending", items:["GHG baseline certified","Additionality documentation","Monitoring plan (MRV)","Project boundary defined","Technology specification"] },
        { label:"Methodology Selection", status:"complete", items:[`Selected: ${SECTOR_CARBON[deal.sector]?.methodology||"TBD"}`, `Standard: ${deal.sector==="biogas"?"Gold Standard":deal.sector==="cement"?"Verra VCS":"Gold Standard / Verra"}`, `Expected tCO₂e/yr: ${(SECTOR_CARBON[deal.sector]?.tco2e||5000).toLocaleString()}`] },
        { label:"PDD Template Structure", status:"pending", items:["A1: Project Description","A2: Baseline Scenario","A3: Project Activities","A4: Monitoring Plan","A5: Environmental Impacts","A6: Stakeholder Consultation","A7: Additionality Justification"] },
        { label:"MRV Checklist", status:"pending", items:["Monitoring equipment specified","Reporting templates prepared","Third-party verifier identified","Crediting period defined (7yr renewable)","Registry account opened"] },
      ],
    }),
    art6: (deal, carbon) => ({
      title: "Structure for Article 6",
      steps: [
        { label:"Host Country Eligibility", status:"pending", items:["Host country: Nigeria (NG) — Art 6.2 bilateral framework emerging","NDC corridor alignment required","Sovereign Letter of Authorization (LoA) needed from DNA","Check: https://unfccc.int/process/the-paris-agreement/nationally-determined-contributions"] },
        { label:"DNA Pathway", status:"pending", items:["Designated National Authority (DNA) engagement","NDC corridor eligibility assessment","Corresponding adjustment process","Bilateral agreement with purchasing country"] },
        { label:"Additionality & Authorization", status:"pending", items:["Additionality demonstration (financial + regulatory)","Baseline scenario documentation","Authorization for ITMO issuance","Monitoring and verification under Art 6.4 SB"] },
      ],
    }),
    poa: (deal, carbon) => ({
      title: "Add to Programme of Activities (PoA)",
      steps: [
        { label:"CPA Structure", status:"pending", items:[`Add as CPA: ${_parseName(deal).split(" ")[0]} component`, `Contribution: ${carbon.tco2e.toLocaleString()} tCO₂e/yr`, `Cumulative programme total: growing`] },
        { label:"Volume Aggregation", status:"pending", items:["Assign unique CPA reference code","Update programme volume register","Recalculate pooled revenue estimate","Confirm methodology coverage includes this site"] },
        { label:"Programme Benefits", status:"complete", items:["Single PDD covers all CPAs — 60-80% MRV cost reduction","Pooled verification — economies of scale","Portfolio carbon strategy aligned","Aggregated institutional reporting enabled"] },
      ],
    }),
    request_data: (deal, carbon) => ({
      title: "Request Data",
      steps: [
        { label:"Data Gaps Identified", status:"pending", items: carbon.dataGaps.length > 0 ? carbon.dataGaps.map(g=>`⚠ ${g} — required before carbon structuring`) : ["No critical gaps identified"] },
        { label:"Data Request Template", status:"pending", items:["GHG emissions baseline (certified third-party)","Annual emissions monitoring records (last 3 years)","Project description and technology specification","Legal entity documentation","Evidence of additionality (regulatory/financial)"] },
      ],
    }),
  };

  const READINESS_STYLE = {
    "Ready for PDD":   "bg-emerald-100 text-emerald-700 border-emerald-300",
    "Pre-Feasibility": "bg-amber-100 text-amber-700 border-amber-300",
    "Not Ready":       "bg-red-100 text-red-600 border-red-200",
  };

  if (loading) return <div className="flex items-center justify-center py-12"><RefreshCw size={14} className="animate-spin text-teal-500"/></div>;

  // ── Prioritised sort: Ready for PDD → High value + low effort → Blocked ──
  const blockedDeals    = dealData.filter(d => d.carbon.readiness === "Not Ready");
  const readyDeals      = dealData.filter(d => d.carbon.readiness === "Ready for PDD");
  const conditionalDeals= dealData.filter(d => d.carbon.readiness === "Pre-Feasibility");
  const sortedDeals = [
    ...readyDeals.sort((a,b) => b.carbon.baseRevenue - a.carbon.baseRevenue),
    ...conditionalDeals.sort((a,b) => b.carbon.baseRevenue - a.carbon.baseRevenue),
    ...blockedDeals.sort((a,b) => b.carbon.baseRevenue - a.carbon.baseRevenue),
  ];

  // ── Single context-aware CTA per deal ──────────────────────────────────────
  const getPrimaryCTA = (deal, carbon) => {
    if (carbon.readiness === "Ready for PDD" && carbon.vcmOk)
      return { label: "Generate VCM Document", fn: () => openMethEngine(deal, "vcm"), style: "bg-teal-600 text-white hover:bg-teal-700", tid: `vcm-pdf-${deal.deal_id}` };
    if (carbon.art6Ok)
      return { label: "Art.6 Tracker", fn: () => openArt6Tracker(deal), style: "bg-blue-600 text-white hover:bg-blue-700", tid: `art6-pdf-${deal.deal_id}` };
    if (carbon.dataGaps.length > 0)
      return { label: "Request Data", fn: () => setWorkflow({deal, carbon, type:"request_data"}), style: "bg-amber-100 text-amber-700 hover:bg-amber-200", tid: `req-data-${deal.deal_id}` };
    if (carbon.vcmOk)
      return { label: "Structure VCM", fn: () => setWorkflow({deal, carbon, type:"vcm"}), style: "bg-slate-100 text-slate-700 hover:bg-slate-200", tid: `vcm-${deal.deal_id}` };
    return { label: "View Status", fn: () => {}, style: "bg-slate-50 text-slate-400 cursor-default", tid: `view-${deal.deal_id}` };
  };

  const getBlocker = (carbon) => {
    if (carbon.readiness === "Ready for PDD") return null;
    if (carbon.dataGaps.length > 0) return carbon.dataGaps.slice(0,2).join(" · ");
    if (carbon.elig === "blocked") return "Eligibility blocked";
    return "Insufficient evidence";
  };

  // ── Action Insights ────────────────────────────────────────────────────────
  const immediateActions = [];
  if (blockedDeals.length > 0)
    immediateActions.push({ text: `${blockedDeals.length} deal${blockedDeals.length>1?"s":""} blocked — insufficient data or eligibility gap`, action: "Request data to unblock", color: "red" });
  const dataGapDeals = conditionalDeals.filter(d => d.carbon.dataGaps.length >= 2);
  if (dataGapDeals.length > 0)
    immediateActions.push({ text: `${dataGapDeals.length} deal${dataGapDeals.length>1?"s":""} in Pre-Feasibility with 2+ data gaps`, action: "Request updated data", color: "amber" });
  const art6NeedingTracker = dealData.filter(d => d.carbon.art6Ok && !d.carbon.readiness.includes("Blocked"));
  if (art6NeedingTracker.length > 0)
    immediateActions.push({ text: `${art6NeedingTracker.length} deal${art6NeedingTracker.length>1?"s":""} eligible for Article 6 — tracker not started`, action: "Open Art.6 tracker", color: "amber" });

  const structuringOpps = [];
  if (readyDeals.length > 0)
    structuringOpps.push({ text: `${readyDeals.length} deal${readyDeals.length>1?"s":""} ready for VCM Project Document — methodology anchored`, action: "Generate VCM Document", color: "green" });
  const highValueCond = conditionalDeals.filter(d => d.carbon.baseRevenue >= 200000);
  if (highValueCond.length > 0)
    structuringOpps.push({ text: `${highValueCond.length} high-value deal${highValueCond.length>1?"s":""} near PDD threshold — >$200K/yr carbon potential`, action: "Close data gaps to advance", color: "teal" });
  if (art6NeedingTracker.length > 0)
    structuringOpps.push({ text: `Art.6 bilateral pathway open for ${art6NeedingTracker.length} deal${art6NeedingTracker.length>1?"s":""} — initiate DNA engagement`, action: "Start Art.6 Tracker", color: "blue" });

  return (
    <div className="space-y-4" data-testid="section-carbon-analysis">

      {/* Disclaimer — concise, not repeated */}
      <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
        <AlertTriangle size={11} className="text-amber-500 flex-shrink-0 mt-0.5"/>
        <p className="text-[10px] text-amber-700">Carbon revenue estimates are indicative — excluded from DSCR. Not bankable until MRV-verified and registry-linked.</p>
      </div>

      {/* ── 3 KPIs: signal, not noise ──────────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#0F172A] rounded-xl p-4">
          <p className="text-[9px] text-white/60 uppercase tracking-wider mb-1">Carbon Revenue Potential</p>
          <p className="text-2xl font-bold text-teal-400">${(totalBase/1000).toFixed(0)}K<span className="text-sm text-white/60 font-normal">/yr</span></p>
          <p className="text-[10px] text-white/70 mt-1">{(totalTco2e/1000).toFixed(0)}K tCO₂e/yr across {nonRejected.length} deals</p>
        </div>
        <div className={`rounded-xl p-4 border-2 ${readyDeals.length > 0 ? "bg-emerald-50 border-emerald-300" : "bg-slate-50 border-slate-200"}`}>
          <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Ready for Action</p>
          <p className={`text-2xl font-bold ${readyDeals.length > 0 ? "text-emerald-700" : "text-slate-400"}`}>{readyDeals.length}<span className="text-sm font-normal text-slate-500"> of {nonRejected.length}</span></p>
          <p className="text-[10px] text-slate-600 mt-1">{readyDeals.length > 0 ? "Ready for PDD — generate documents now" : "No deals at PDD threshold yet"}</p>
        </div>
        <div className={`rounded-xl p-4 border-2 ${blockedDeals.length > 0 ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`}>
          <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Blocked</p>
          <p className={`text-2xl font-bold ${blockedDeals.length > 0 ? "text-red-600" : "text-slate-400"}`}>{blockedDeals.length}<span className="text-sm font-normal text-slate-500"> of {nonRejected.length}</span></p>
          <p className="text-[10px] text-slate-600 mt-1">{blockedDeals.length > 0 ? "Insufficient data or eligibility gap" : "No blocked deals"}</p>
        </div>
      </div>

      {/* ── Action Insights: Immediate + Structuring ───────────────────────── */}
      {(immediateActions.length > 0 || structuringOpps.length > 0) && (
        <div>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Action Insights</p>
          <div className="grid grid-cols-2 gap-3">
          {/* Immediate Actions Required */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-2.5">Immediate Actions Required</p>
            {immediateActions.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No immediate actions required.</p>
            ) : immediateActions.map((ins, i) => (
              <div key={i} className={`flex items-start gap-2 p-2.5 rounded-lg mb-1.5 last:mb-0 ${ins.color==="red"?"bg-red-50":ins.color==="amber"?"bg-amber-50":"bg-slate-50"}`}>
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5 ${ins.color==="red"?"bg-red-500":ins.color==="amber"?"bg-amber-500":"bg-teal-500"}`}/>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-slate-700">{ins.text}</p>
                  <p className={`text-[9px] font-bold mt-0.5 ${ins.color==="red"?"text-red-600":ins.color==="amber"?"text-amber-600":"text-teal-600"}`}>→ {ins.action}</p>
                </div>
              </div>
            ))}
          </div>
          {/* Structuring Opportunities */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-2.5">Structuring Opportunities</p>
            {structuringOpps.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No structuring opportunities identified yet.</p>
            ) : structuringOpps.map((opp, i) => (
              <div key={i} className={`flex items-start gap-2 p-2.5 rounded-lg mb-1.5 last:mb-0 ${opp.color==="green"?"bg-emerald-50":opp.color==="teal"?"bg-teal-50":opp.color==="blue"?"bg-blue-50":"bg-slate-50"}`}>
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5 ${opp.color==="green"?"bg-emerald-500":opp.color==="teal"?"bg-teal-500":opp.color==="blue"?"bg-blue-500":"bg-slate-400"}`}/>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-slate-700">{opp.text}</p>
                  <p className={`text-[9px] font-bold mt-0.5 ${opp.color==="green"?"text-emerald-600":opp.color==="teal"?"text-teal-600":opp.color==="blue"?"text-blue-600":"text-slate-500"}`}>→ {opp.action}</p>
                </div>
              </div>
            ))}
          </div>
          </div>
        </div>
      )}
      <div>
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Deals — Prioritised by Readiness</p>
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs" style={{tableLayout:"fixed"}}>
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500">
                <th className="text-left px-4 py-2.5 font-semibold" style={{width:"22%"}}>Deal</th>
                <th className="text-right px-3 py-2.5 font-semibold" style={{width:"13%"}}>Carbon Value</th>
                <th className="text-center px-3 py-2.5 font-semibold" style={{width:"13%"}}>Readiness</th>
                <th className="text-left px-3 py-2.5 font-semibold" style={{width:"22%"}}>Blocker</th>
                <th className="text-left px-3 py-2.5 font-semibold" style={{width:"18%"}}>Recommended Action</th>
                <th className="text-right px-4 py-2.5 font-semibold" style={{width:"12%"}}></th>
              </tr>
            </thead>
            <tbody>
              {sortedDeals.map(({ deal, carbon }) => {
                const cta = getPrimaryCTA(deal, carbon);
                const blocker = getBlocker(carbon);
                const readyColor = carbon.readiness === "Ready for PDD" ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
                  : carbon.readiness === "Pre-Feasibility" ? "bg-amber-100 text-amber-700 border border-amber-300"
                  : "bg-red-100 text-red-600 border border-red-200";
                const recommended = carbon.readiness === "Ready for PDD" ? "Generate VCM Document"
                  : carbon.art6Ok ? "Advance to Art.6"
                  : carbon.dataGaps.length > 0 ? "Fill data gaps first"
                  : carbon.vcmOk ? "Initiate VCM structuring"
                  : "Assess eligibility";

                return (
                  <tr key={deal.deal_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors" data-testid={`carbon-row-${deal.deal_id}`}>
                    {/* Deal */}
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900 truncate">{_parseName(deal).split(" ").slice(0,3).join(" ")}</p>
                      <p className="text-[10px] text-slate-400 capitalize mt-0.5">{deal.sector} · ${((deal.deployed_amount_usd||0)/1e6).toFixed(1)}M</p>
                    </td>
                    {/* Carbon Value */}
                    <td className="px-3 py-3 text-right">
                      <p className="font-bold tabular-nums text-teal-700">${(carbon.baseRevenue/1000).toFixed(0)}K/yr</p>
                      <p className="text-[9px] text-slate-400 mt-0.5">{carbon.tco2e.toLocaleString()} tCO₂</p>
                    </td>
                    {/* Readiness */}
                    <td className="px-3 py-3 text-center">
                      <span className={`text-[9px] font-bold px-2 py-0.5 rounded whitespace-nowrap ${readyColor}`}>{carbon.readiness}</span>
                    </td>
                    {/* Blocker */}
                    <td className="px-3 py-3">
                      {blocker ? (
                        <p className="text-[10px] text-slate-500 truncate">{blocker}</p>
                      ) : (
                        <p className="text-[10px] text-emerald-600 font-medium">No blockers</p>
                      )}
                    </td>
                    {/* Recommended Action */}
                    <td className="px-3 py-3">
                      <p className="text-[10px] font-medium text-slate-700">{recommended}</p>
                    </td>
                    {/* Single CTA */}
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={cta.fn}
                        className={`text-[9px] font-bold px-2.5 py-1.5 rounded-lg whitespace-nowrap transition-colors ${cta.style}`}
                        data-testid={cta.tid}>
                        {cta.label}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Art.6 Tracker Modal (Phase 6) ──────────────────────────────────── */}
      {art6Panel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="art6-tracker-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-[720px] max-h-[95vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-[#0F172A] px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div>
                <p className="text-[9px] text-blue-400 font-bold uppercase tracking-[0.15em]">Article 6.2 Execution Tracker — Phase 6</p>
                <p className="text-sm font-bold text-white mt-0.5">{art6Panel.deal?.deal_id} · {art6Panel.deal?.sector?.toUpperCase()}</p>
              </div>
              <div className="flex items-center gap-3">
                {art6Panel.readiness && (
                  <span className={`text-[9px] font-bold px-2 py-1 rounded ${art6Panel.readiness.level==="SUBMISSION_READY"?"bg-emerald-500/20 text-emerald-300":art6Panel.readiness.level==="STRUCTURING_READY"?"bg-teal-500/20 text-teal-300":art6Panel.readiness.level==="IN_PROGRESS"?"bg-amber-500/20 text-amber-300":"bg-red-500/20 text-red-300"}`}>
                    {art6Panel.readiness.label}
                  </span>
                )}
                <button onClick={() => setArt6Panel(null)} className="text-slate-500 hover:text-white transition-colors"><X size={16}/></button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 p-5 space-y-3 bg-slate-50">
              {art6Loading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center"><RefreshCw size={14} className="animate-spin"/>Loading tracker...</div>
              ) : art6Panel.stages && (
                <>
                  {/* Readiness summary */}
                  {art6Panel.readiness && (
                    <div className={`rounded-xl p-4 border ${art6Panel.readiness.level==="SUBMISSION_READY"?"bg-emerald-50 border-emerald-200":art6Panel.readiness.level==="STRUCTURING_READY"?"bg-teal-50 border-teal-200":art6Panel.readiness.level==="IN_PROGRESS"?"bg-amber-50 border-amber-200":"bg-red-50 border-red-200"}`} data-testid="art6-readiness-summary">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className={`text-xs font-bold mb-1 ${art6Panel.readiness.level==="SUBMISSION_READY"?"text-emerald-700":art6Panel.readiness.level==="STRUCTURING_READY"?"text-teal-700":art6Panel.readiness.level==="IN_PROGRESS"?"text-amber-700":"text-red-700"}`}>
                            {art6Panel.readiness.label}
                          </p>
                          <p className="text-[10px] text-slate-600">{art6Panel.readiness.description}</p>
                          {art6Panel.readiness.priority_stage && (
                            <p className="text-[10px] text-slate-700 mt-1.5">
                              <span className="font-bold">Priority: </span>{art6Panel.readiness.priority_stage.label} — {art6Panel.readiness.priority_stage.next_action?.slice(0,100)}
                            </p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-[9px] text-slate-400">Progress</p>
                          <p className="text-lg font-bold text-slate-900">{art6Panel.readiness.completed_count}/{art6Panel.readiness.total_stages}</p>
                          <p className="text-[9px] text-slate-400">stages</p>
                        </div>
                      </div>
                      {art6Panel.readiness.risk_flags?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-current/20">
                          {art6Panel.readiness.risk_flags.map((f,i) => (
                            <p key={i} className="text-[9px] text-amber-700 flex gap-1"><span>⚠</span><span>{f}</span></p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Stage tracker */}
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
                      <p className="text-[9px] font-bold text-slate-600 uppercase tracking-wider">6-Stage Execution Tracker</p>
                    </div>
                    <div className="divide-y divide-slate-50">
                      {art6Panel.stages.map(stage => {
                        const statusColors = { completed:"bg-emerald-100 text-emerald-700", in_progress:"bg-amber-100 text-amber-700", blocked:"bg-red-100 text-red-700", not_started:"bg-slate-100 text-slate-500" };
                        const sc = statusColors[stage.status] || statusColors.not_started;
                        return (
                          <div key={stage.id} className="px-4 py-3" data-testid={`art6-stage-${stage.id}`}>
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 mt-0.5">
                                <span className={`text-[9px] font-bold px-2 py-1 rounded ${sc}`}>{stage.status_label}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-bold text-slate-900">{stage.step}. {stage.label}</p>
                                <p className="text-[10px] text-slate-500 mt-0.5">{stage.description}</p>
                                <p className="text-[9px] text-slate-600 mt-1">
                                  <span className="font-bold">Owner:</span> {stage.owner} · <span className="font-bold">Duration:</span> {stage.typical_duration}
                                </p>
                                {stage.status !== "completed" && (
                                  <p className="text-[9px] text-blue-700 mt-1">
                                    <span className="font-bold">Next action:</span> {stage.next_action?.slice(0,120)}
                                  </p>
                                )}
                              </div>
                              {/* Status update buttons */}
                              <div className="flex-shrink-0 flex gap-1">
                                {["in_progress","completed","blocked"].map(newSt => (
                                  stage.status !== newSt && (
                                    <button
                                      key={newSt}
                                      onClick={() => updateArt6Stage(stage.id, newSt, stage.owner, stage.next_action)}
                                      disabled={art6Updating === stage.id}
                                      className={`text-[8px] font-bold px-1.5 py-1 rounded border transition-colors ${newSt==="completed"?"border-emerald-300 text-emerald-700 hover:bg-emerald-50":newSt==="in_progress"?"border-amber-300 text-amber-700 hover:bg-amber-50":"border-red-300 text-red-600 hover:bg-red-50"}`}>
                                      {art6Updating === stage.id ? "..." : newSt==="completed"?"✓ Done":newSt==="in_progress"?"→ Start":"✗ Block"}
                                    </button>
                                  )
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-between bg-white flex-shrink-0">
              <button onClick={() => setArt6Panel(null)} className="text-xs text-slate-500 hover:text-slate-700">Close</button>
              <button
                onClick={async () => {
                  if (!art6Panel) return;
                  toast.loading("Generating Article 6 Pack with current tracker status…", { id: "art6-pdf", duration: 20000 });
                  try {
                    const resp = await client.post(`/fi/pdf/article6-pack/${art6Panel.deal.deal_id}`, {}, { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
                    const a = document.createElement("a"); a.href = url;
                    a.download = `DeCarbonIQ_Article6_Pack_${art6Panel.deal.deal_id}_${new Date().toISOString().slice(0,10)}.pdf`;
                    a.click(); window.URL.revokeObjectURL(url);
                    toast.success("Article 6 Readiness Pack generated with tracker status embedded.", { id: "art6-pdf" });
                  } catch(e) { toast.error(`Article 6 Pack generation failed: ${e?.response?.data?.detail || e?.message || "Server error — Chromium may be initialising, retry in 30 seconds"}`, { id: "art6-pdf" }); }
                }}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-xs font-bold rounded-xl hover:bg-blue-700"
                data-testid="art6-generate-pdf">
                Generate Article 6 Pack (PDF)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Methodology Engine Modal (Phase 1+2) ──────────────────────────────── */}
      {methEngine && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="methodology-engine-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-[760px] max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-[#0F172A] px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div>
                <p className="text-[9px] text-teal-400 font-bold uppercase tracking-[0.15em]">Methodology Engine — Phase 1+2</p>
                <p className="text-sm font-bold text-white mt-0.5">{methEngine.deal.deal_id} · {methEngine.deal.sector?.toUpperCase()}</p>
              </div>
              <button onClick={() => setMethEngine(null)} className="text-slate-500 hover:text-white transition-colors"><X size={16}/></button>
            </div>

            <div className="overflow-y-auto flex-1 p-5 space-y-4 bg-slate-50">

              {/* ── Loading state ──────────────────────────────────────────── */}
              {(methLoading || methComputing) && (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <RefreshCw size={20} className="animate-spin text-teal-500"/>
                  <p className="text-sm font-bold text-slate-700">Auto-calculating...</p>
                  <p className="text-[10px] text-slate-400">Selecting methodology and computing emission reduction</p>
                </div>
              )}

              {/* ── RESULTS FIRST: shown immediately on open ───────────────── */}
              {!methLoading && !methComputing && methTrace && (
                <>
                  {/* Primary result card */}
                  <div className="bg-[#0F172A] rounded-xl p-4" data-testid="computation-trace">
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-3">{methTrace.methodology} · {methTrace.methodology_version?.split(" — ")[0]}</p>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-[9px] text-slate-500 mb-0.5">Annual Net Reduction</p>
                        <p className="text-2xl font-bold text-teal-400">{methTrace.results.net_reduction_t_yr.toLocaleString()}</p>
                        <p className="text-[10px] text-slate-500">tCO₂e/yr</p>
                      </div>
                      <div>
                        <p className="text-[9px] text-slate-500 mb-0.5">Crediting Total ({methTrace.results.crediting_period_yr}yr)</p>
                        <p className="text-2xl font-bold text-white">{methTrace.results.total_net_t.toLocaleString()}</p>
                        <p className="text-[10px] text-slate-500">tCO₂e total</p>
                      </div>
                      <div>
                        <p className="text-[9px] text-slate-500 mb-0.5">Calculation Confidence</p>
                        <p className={`text-2xl font-bold ${methTrace.confidence?.overall_pct >= 70 ? "text-emerald-400" : methTrace.confidence?.overall_pct >= 40 ? "text-amber-400" : "text-red-400"}`}>
                          {methTrace.confidence?.overall_pct?.toFixed(0)}%
                        </p>
                        <p className="text-[10px] text-slate-500">{methTrace.confidence?.label?.split(" (")[0]}</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2">
                      <p className="text-[8.5px] font-mono text-slate-400 truncate flex-1">{methTrace.formula_summary}</p>
                      {methTrace.confidence_gate && (
                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded flex-shrink-0 ${methTrace.confidence_gate.gate==="clear"?"bg-emerald-500/20 text-emerald-300":methTrace.confidence_gate.gate==="conditional"?"bg-amber-500/20 text-amber-300":"bg-red-500/20 text-red-300"}`}>
                          {methTrace.confidence_gate.gate.toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Revenue + Financial Additionality */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-xl border border-slate-200 p-3">
                      <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-2">Revenue Scenarios</p>
                      {[["$8/t", methTrace.results.revenue_estimates?.low_8_usd], ["$15/t", methTrace.results.revenue_estimates?.base_15_usd], ["$30/t", methTrace.results.revenue_estimates?.premium_30_usd]].map(([p,v]) => (
                        <div key={p} className="flex justify-between items-baseline mb-1">
                          <span className="text-[9px] text-slate-500">{p}</span>
                          <span className="text-xs font-bold text-teal-700">${v?.toLocaleString()}K</span>
                        </div>
                      ))}
                    </div>
                    {methFA && !methFALoading && (
                      <div className={`rounded-xl border-2 p-3 ${methFA.additionality.verdict==="Justified"?"bg-emerald-50 border-emerald-300":"bg-amber-50 border-amber-300"}`}>
                        <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Financial Additionality</p>
                        <p className={`text-sm font-bold mb-1 ${methFA.additionality.verdict==="Justified"?"text-emerald-700":"text-amber-700"}`}>
                          {methFA.additionality.verdict.toUpperCase()}
                        </p>
                        <p className="text-[9px] text-slate-600">{methFA.irr.irr_without_carbon_pct}% IRR vs {methFA.irr.hurdle_rate_pct}% hurdle</p>
                        <p className="text-[9px] text-teal-600 font-medium">+{methFA.irr.delta_irr_pct}pp from carbon</p>
                      </div>
                    )}
                    {methFALoading && (
                      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center justify-center">
                        <RefreshCw size={12} className="animate-spin text-teal-500"/>
                        <span className="text-[10px] text-slate-400 ml-2">Computing additionality...</span>
                      </div>
                    )}
                  </div>

                  {/* ── Adjust Assumptions (collapsed by default) ───────────── */}
                  <div className="bg-white rounded-xl border border-slate-200">
                    <button
                      onClick={() => setMethShowInputs(p => !p)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition-colors rounded-xl"
                      data-testid="adjust-assumptions-toggle">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-700">Adjust Assumptions</span>
                        {methTrace.confidence?.tier3_count > 0 && (
                          <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">
                            {methTrace.confidence.tier3_count} default param{methTrace.confidence.tier3_count>1?"s":""}
                          </span>
                        )}
                      </div>
                      <span className="text-slate-400 text-xs">{methShowInputs ? "▲ Collapse" : "▼ Show parameters"}</span>
                    </button>
                    {methShowInputs && (
                      <div className="px-4 pb-4 space-y-3 border-t border-slate-100">
                        {/* Methodology refinement */}
                        <div className="pt-3">
                          <p className="text-[9px] text-slate-500 font-bold uppercase tracking-wider mb-2">Methodology: {methSelection?.selection?.primary_methodology}</p>
                          <div className="grid grid-cols-3 gap-2">
                            {[["Tech type", methTechType, setMethTechType, ["","solar","wind","anaerobic_digestion","fuel_switch","material_recovery","bev","efficiency_retrofit"]],
                              ["Boundary",   methBoundary, setMethBoundary, ["","grid_connected","off_grid","on_site"]],
                              ["Output",     methOutput,   setMethOutput,   ["","electricity","biomethane","waste_diversion","soil_carbon"]]
                            ].map(([label, val, setVal, opts]) => (
                              <div key={label}>
                                <p className="text-[8.5px] text-slate-400 mb-0.5">{label}</p>
                                <select value={val} onChange={e => { setVal(e.target.value); refineSelection(); }}
                                  className="w-full text-[9px] px-1.5 py-1 border border-slate-200 rounded-lg">
                                  {opts.map(o => <option key={o} value={o}>{o || "Auto"}</option>)}
                                </select>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Input parameters — locked by default, individual override */}
                        <div className="pt-2">
                          <p className="text-[9px] text-slate-500 font-bold uppercase tracking-wider mb-2">Parameters</p>
                          <div className="space-y-1.5">
                            {(methSelection?.input_schema || []).map(p => {
                              const isOverridden = overriddenParams.has(p.key);
                              const currentVal = methInputs[p.key] ?? p.default;
                              return (
                                <div key={p.key} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg ${isOverridden ? "bg-amber-50 border border-amber-200" : "bg-slate-50"}`}>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-[9px] font-medium text-slate-700 truncate">{p.label}</p>
                                    <p className="text-[8.5px] text-slate-400 truncate">{p.source}</p>
                                  </div>
                                  {isOverridden ? (
                                    <input type="number" value={currentVal}
                                      onChange={e => {
                                        const v = parseFloat(e.target.value) || 0;
                                        const updated = {...methInputs, [p.key]: v};
                                        setMethInputs(updated);
                                        clearTimeout(window._recompDebounce);
                                        window._recompDebounce = setTimeout(() => recomputeWithInputs(updated), 800);
                                      }}
                                      className="w-20 text-[9px] text-right px-1.5 py-1 border border-amber-300 rounded bg-white focus:outline-none"
                                    />
                                  ) : (
                                    <span className="text-[9px] font-bold text-slate-600 tabular-nums">{typeof currentVal === 'number' ? currentVal.toLocaleString() : currentVal}</span>
                                  )}
                                  <span className="text-[8.5px] text-slate-400 w-10 flex-shrink-0">{p.unit}</span>
                                  <button
                                    onClick={() => {
                                      setOverriddenParams(prev => {
                                        const next = new Set(prev);
                                        if (isOverridden) next.delete(p.key); else next.add(p.key);
                                        return next;
                                      });
                                    }}
                                    className={`text-[8px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 transition-colors ${isOverridden ? "bg-amber-500 text-white" : "bg-slate-200 text-slate-500 hover:bg-slate-300"}`}>
                                    {isOverridden ? "Lock" : "Edit"}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Confidence gate warning (conditional/blocked) */}
              {!methLoading && methTrace?.confidence_gate?.gate !== "clear" && methTrace?.confidence_gate?.flag_text && (
                <div className={`rounded-lg p-3 text-[10px] ${methTrace.confidence_gate.gate==="blocked"?"bg-red-50 border border-red-200 text-red-700":"bg-amber-50 border border-amber-200 text-amber-700"}`}>
                  {methTrace.confidence_gate.flag_text}
                </div>
              )}

            </div>

            {/* ── Footer: frictionless flow ──────────────────────────────── */}
            <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-between bg-white flex-shrink-0">
              <div className="flex items-center gap-3">
                <button onClick={() => setMethEngine(null)} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
                {methTrace?.confidence_gate && (
                  <div className="flex items-center gap-1.5">
                    <div className={`w-2 h-2 rounded-full ${methTrace.confidence_gate.gate==="clear"?"bg-emerald-500":methTrace.confidence_gate.gate==="conditional"?"bg-amber-500":"bg-red-500"}`}/>
                    <span className={`text-[9px] font-bold ${methTrace.confidence_gate.gate==="clear"?"text-emerald-700":methTrace.confidence_gate.gate==="conditional"?"text-amber-700":"text-red-700"}`}>
                      {methTrace.confidence?.overall_pct?.toFixed(0)}% confidence
                    </span>
                  </div>
                )}
              </div>
              {methTrace?.confidence_gate?.gate === "blocked" ? (
                <div className="flex items-center gap-2">
                  <p className="text-[9px] text-red-600 font-bold">Confidence too low — adjust assumptions</p>
                  <button className="px-4 py-2 bg-red-100 text-red-400 text-xs font-bold rounded-xl cursor-not-allowed" disabled>Blocked</button>
                </div>
              ) : (
                <button onClick={generatePDDWithTrace} disabled={methExporting || !methTrace || methLoading}
                  className={`flex items-center gap-2 px-5 py-2.5 text-white text-xs font-bold rounded-xl disabled:opacity-40 ${methTrace?.confidence_gate?.gate==="conditional"?"bg-amber-600 hover:bg-amber-700":"bg-teal-600 hover:bg-teal-700"}`}
                  data-testid="generate-pdd-from-engine">
                  {methExporting ? <RefreshCw size={12} className="animate-spin"/> : null}
                  {methExporting ? "Generating..." : methLoading ? "Calculating..." : methTrace?.confidence_gate?.gate==="conditional" ? "Generate PDD (Conditional)" : "Generate VCM Document"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Workflow Modal */}
      {workflow && (() => {
        const wf = WORKFLOW_CONTENT[workflow.type]?.(workflow.deal, workflow.carbon);
        if (!wf) return null;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden" data-testid="carbon-workflow-modal">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-slate-50">
                <div>
                  <p className="text-sm font-bold text-slate-900">{wf.title}</p>
                  <p className="text-[10px] text-slate-500">{_parseName(workflow.deal)} · {workflow.deal.sector}</p>
                </div>
                <button onClick={()=>setWorkflow(null)} className="text-slate-400 hover:text-slate-700"><X size={16}/></button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {wf.steps.map((step, i) => (
                  <div key={i}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${step.status==="complete"?"bg-emerald-100 text-emerald-700":"bg-amber-100 text-amber-700"}`}>
                        {step.status==="complete"?"Complete":"Action Required"}
                      </span>
                      <p className="text-xs font-bold text-slate-800">{step.label}</p>
                    </div>
                    <div className="space-y-1">
                      {step.items.map((item,j) => (
                        <div key={j} className="flex items-start gap-2 text-xs text-slate-600 py-1 border-b border-slate-50 last:border-0">
                          <span className={`flex-shrink-0 mt-0.5 ${step.status==="complete"?"text-emerald-500":"text-amber-500"}`}>{step.status==="complete"?"✓":"→"}</span>
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-200 px-5 py-3 bg-white flex gap-2">
                <button onClick={()=>{toast.success(`${wf.title} workflow initiated for ${_parseName(workflow.deal).split(" ")[0]}`); setWorkflow(null);}}
                  className="flex-1 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700">
                  Initiate Workflow
                </button>
                <button onClick={()=>setWorkflow(null)} className="px-4 py-2 border border-slate-200 text-xs text-slate-600 rounded-xl">Close</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── SECTION: Climate Risk ──────────────────────────────────────────────────
// ── CLIMATE RISK & SCENARIO ANALYSIS ─────────────────────────────────────

// Section 1: Climate Risk — Bank Implication Layer
function SectionClimateRisk() {
  const [scenario, setScenario] = useState("netzero");
  const [scenarioData, setScenarioData] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dealsLoading, setDealsLoading] = useState(true);
  const [actioning, setActioning] = useState(null);

  // Sector transition risk per scenario
  const SCENARIO_RISK = {
    netzero: { cement:"high", transport:"high", manufacturing:"high", energy:"medium", waste:"medium", biogas:"low", agriculture:"low" },
    delayed: { cement:"medium", transport:"medium", manufacturing:"medium", energy:"low", waste:"low", biogas:"low", agriculture:"low" },
    "2deg":  { cement:"medium", transport:"medium", manufacturing:"medium", energy:"low", waste:"low", biogas:"low", agriculture:"low" },
  };

  // Repricing pressure per risk level per scenario
  const REPRICING_PRESSURE = {
    netzero: { high:"+0.75–1.25%", medium:"+0.25–0.50%", low:"Minimal" },
    delayed: { high:"+0.25–0.50%", medium:"+0.10–0.25%", low:"None" },
    "2deg":  { high:"+0.25–0.50%", medium:"+0.10%", low:"None" },
  };

  useEffect(() => {
    client.get("/fi/decisions/deals")
      .then(r => setDeals(r.data.deals || []))
      .catch(() => {}).finally(() => setDealsLoading(false));
  }, []);

  const loadScenario = (sc) => {
    setLoading(true); setScenario(sc);
    client.get(`/fi/workspace/scenario/${sc}`)
      .then(r => setScenarioData(r.data)).catch(() => setScenarioData(null)).finally(() => setLoading(false));
  };
  useEffect(() => { loadScenario("netzero"); }, []);

  // Bank implication computation from live portfolio
  const sRisk = SCENARIO_RISK[scenario] || SCENARIO_RISK.netzero;
  const activeDeals = deals.filter(d => !["rejected"].includes(d.status));
  const highRiskDeals   = activeDeals.filter(d => sRisk[d.sector] === "high");
  const medRiskDeals    = activeDeals.filter(d => sRisk[d.sector] === "medium");
  const covenantDeals   = highRiskDeals.filter(d => ["credit_committee","term_sheet","financial_close"].includes(d.status));
  const repricingDeals  = highRiskDeals.filter(d => ["due_diligence","credit_committee","term_sheet"].includes(d.status));
  const monitoringDeals = highRiskDeals.filter(d => d.status === "financial_close");
  const topSectors      = Object.entries(sRisk).filter(([,v])=>v==="high").map(([s])=>s).filter(s=>activeDeals.some(d=>d.sector===s));
  const totalExposure   = activeDeals.reduce((s,d)=>s+(d.deployed_amount_usd||0),0);

  const DEAL_ACTIONS = (deal) => {
    const risk = sRisk[deal.sector] || "low";
    return [
      risk === "high" ? { label:"Review Pricing",    icon:"💰", section:"financing",  module:"credit"    } : null,
      risk === "high" && ["credit_committee","term_sheet","financial_close"].includes(deal.status) ? { label:"Covenant Review",    icon:"📋", section:"decision",   module:"credit"    } : null,
      deal.status === "financial_close" ? { label:"Enhanced Monitoring",icon:"📡", section:"monitoring", module:"portfolio"  } : null,
      risk === "high" ? { label:"Risk Review",       icon:"⚠️", section:"risk",      module:"climate_risk"} : null,
    ].filter(Boolean);
  };

  return (
    <div className="space-y-5" data-testid="section-climate-risk">
      {/* Scenario selector */}
      <div className="flex items-center gap-3">
        <p className="text-xs font-semibold text-slate-500">Scenario:</p>
        <div className="flex rounded-xl border border-slate-200 overflow-hidden">
          {[["netzero","Net Zero 2050"],["delayed","Delayed Transition"],["2deg","Below 2°C"]].map(([v,l]) => (
            <button key={v} onClick={() => loadScenario(v)}
              className={`px-4 py-2 text-xs font-semibold transition-all ${scenario===v?"bg-[#0F172A] text-white":"bg-white text-slate-500 hover:bg-slate-50"}`}
              data-testid={`scenario-${v}`}>{l}</button>
          ))}
        </div>
        {loading && <RefreshCw size={13} className="animate-spin text-teal-500"/>}
      </div>

      {/* Bank Implication Summary */}
      <div className="bg-[#0F172A] rounded-xl p-5">
        <p className="text-[10px] text-teal-400 font-bold uppercase tracking-[0.15em] mb-3">
          Bank Implication Summary — {scenario === "netzero" ? "Net Zero 2050" : scenario === "delayed" ? "Delayed Transition" : "Below 2°C"}
        </p>
        {dealsLoading ? (
          <RefreshCw size={13} className="animate-spin text-teal-500"/>
        ) : (
          <div className="space-y-2">
            {/* Explicit "so what" statements */}
            {[
              highRiskDeals.length > 0 && { sev:"H", text:`${highRiskDeals.length} deal${highRiskDeals.length>1?"s":""} in high transition-risk sectors (${topSectors.join(", ")}) under this scenario` },
              covenantDeals.length > 0  && { sev:"H", text:`${covenantDeals.length} deal${covenantDeals.length>1?"s":""} require${covenantDeals.length===1?"s":""} covenant review — in active or deployed stage` },
              repricingDeals.length > 0 && { sev:"M", text:`${repricingDeals.length} deal${repricingDeals.length>1?"s":""} in high-risk sectors may need repricing review` },
              monitoringDeals.length > 0 && { sev:"M", text:`${monitoringDeals.length} deployed deal${monitoringDeals.length>1?"s":""} require${monitoringDeals.length===1?"s":""} enhanced monitoring frequency` },
              highRiskDeals.length === 0 && { sev:"L", text:"No immediate high-risk deals identified under this scenario — standard monitoring applies" },
              medRiskDeals.length > 0   && { sev:"L", text:`${medRiskDeals.length} deal${medRiskDeals.length>1?"s":""} in medium transition-risk sectors — monitor quarterly` },
            ].filter(Boolean).map((item, i) => (
              <div key={i} className="flex items-start gap-2.5 text-xs">
                <div className={`w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0 ${item.sev==="H"?"bg-red-400":item.sev==="M"?"bg-amber-400":"bg-emerald-400"}`}/>
                <span className={item.sev==="H"?"text-red-300":item.sev==="M"?"text-amber-300":"text-slate-400"}>{item.text}</span>
              </div>
            ))}
            {/* Repricing context */}
            <div className="mt-3 pt-3 border-t border-white/10 grid grid-cols-3 gap-3 text-xs">
              <div><p className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">Repricing Pressure</p><p className="text-white font-bold">{REPRICING_PRESSURE[scenario]?.high || "+0.75–1.25%"}</p><p className="text-slate-500 text-[10px]">High-risk sectors</p></div>
              <div><p className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">Exposure at Risk</p><p className={`font-bold ${highRiskDeals.length>0?"text-red-400":"text-emerald-400"}`}>${(highRiskDeals.reduce((s,d)=>s+(d.deployed_amount_usd||0),0)/1e6).toFixed(1)}M</p><p className="text-slate-500 text-[10px]">In high-risk sectors</p></div>
              <div><p className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">Policy Status</p><p className={`font-bold ${highRiskDeals.length/Math.max(1,activeDeals.length)>0.5?"text-red-400":"text-emerald-400"}`}>{highRiskDeals.length/Math.max(1,activeDeals.length)>0.5?"Exceeds threshold":"Within policy limits"}</p></div>
            </div>
          </div>
        )}
      </div>

      {/* Affected Deals — with deal-level actions */}
      {!dealsLoading && activeDeals.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
            Affected Deals — Direct Actions Required
          </p>
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold">
                <th className="text-left px-4 py-2.5">Deal</th>
                <th className="text-left px-4 py-2.5">Sector</th>
                <th className="text-center px-3 py-2.5">Transition Risk</th>
                <th className="text-right px-3 py-2.5">Repricing Pressure</th>
                <th className="text-left px-4 py-2.5">Bank Actions</th>
              </tr></thead>
              <tbody>
                {activeDeals.filter(d => sRisk[d.sector]).map((deal, i) => {
                  const risk = sRisk[deal.sector] || "low";
                  const actions = DEAL_ACTIONS(deal);
                  const RISK_STYLE = { high:"text-red-700 bg-red-50", medium:"text-amber-700 bg-amber-50", low:"text-emerald-700 bg-emerald-50" };
                  return (
                    <tr key={deal.deal_id} className={`border-b border-slate-100 last:border-0 ${i%2===1?"bg-slate-50/30":""}`}>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-slate-900 truncate max-w-[150px]">{_parseName(deal)}</p>
                        <p className="text-[10px] text-slate-400 font-mono">{deal.deal_id}</p>
                      </td>
                      <td className="px-4 py-3 capitalize text-slate-600">{deal.sector||"—"}</td>
                      <td className="px-3 py-3 text-center">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded capitalize ${RISK_STYLE[risk]}`}>{risk}</span>
                      </td>
                      <td className={`px-3 py-3 text-right text-[11px] font-semibold ${risk==="high"?"text-red-600":risk==="medium"?"text-amber-600":"text-emerald-600"}`}>
                        {REPRICING_PRESSURE[scenario]?.[risk] || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {actions.slice(0,2).map(a => (
                            <button key={a.label}
                              className="text-[9px] font-bold px-2 py-1 bg-slate-100 text-slate-700 rounded hover:bg-teal-50 hover:text-teal-700 transition-colors whitespace-nowrap"
                              data-testid={`risk-action-${deal.deal_id}`}>
                              {a.label}
                            </button>
                          ))}
                          {actions.length === 0 && <span className="text-[10px] text-emerald-600">No immediate action</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Scenario narrative */}
      {scenarioData?.description && (
        <div className="border border-slate-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-slate-700 mb-1">{scenarioData.label}</p>
          <p className="text-xs text-slate-500 leading-relaxed">{scenarioData.description}</p>
          {scenarioData.implications?.map((imp,i)=>(
            <div key={i} className="flex items-start gap-2 text-xs text-slate-600 mt-1.5">
              <span className="text-teal-500 font-bold flex-shrink-0">—</span>{imp}
            </div>
          ))}
          {scenarioData.disclaimer && <p className="text-[10px] text-slate-400 italic mt-2">{scenarioData.disclaimer}</p>}
        </div>
      )}
    </div>
  );
}

// ── SECTION: Financing Structuring ────────────────────────────────────────
// ── CREDIT ENGINE: Financial Structuring ────────────────────────────────
// Connected to upstream: auto-fills from eligibility + signal score + deal data.
// NOT a standalone form — continuation of the decision logic from Eligibility & Screening.
function SectionFinancingStructure() {
  const [deals,    setDeals]    = useState([]);
  const [selected, setSelected] = useState(null);
  const [form,     setForm]     = useState({
    instrument: "green_loan", amount_usd: "", tenor_years: "7",
    grace_months: "12", rate_pct: "", carbon_revenue_share_pct: "0",
    notes: "",
  });
  const [saving,   setSaving]   = useState(false);
  const [result,   setResult]   = useState(null);

  useEffect(() => {
    client.get("/fi/decisions/deals").then(r => {
      const ds = (r.data.deals || []).filter(d => !["rejected"].includes(d.status));
      setDeals(ds);
      if (ds.length > 0) populateFromDeal(ds[0]);
    }).catch(() => {});
  }, []);

  // ── Auto-prefill from upstream data ──────────────────────────────────
  const populateFromDeal = (deal) => {
    if (!deal) return;
    const elig = _parseElig(deal);
    const size = deal.deployed_amount_usd || 0;
    const sector = deal.sector || "energy";

    // Instrument from eligibility
    const INSTRUMENT_MAP = {
      energy:       "green_loan",
      biogas:       elig === "eligible" ? "carbon_project" : "green_loan",
      cement:       "sll",
      waste:        elig === "eligible" ? "carbon_project" : "green_loan",
      agriculture:  "green_loan",
      transport:    "sll",
      manufacturing:"sll",
    };

    // Tenor suggestion from sector
    const TENOR_MAP = { energy:7, biogas:8, cement:7, waste:6, agriculture:10, transport:5, manufacturing:7 };

    setForm(f => ({
      ...f,
      instrument: INSTRUMENT_MAP[sector] || "green_loan",
      amount_usd: size > 0 ? String(size) : f.amount_usd,
      tenor_years: String(TENOR_MAP[sector] || 7),
      grace_months: sector === "agriculture" ? "18" : "12",
    }));
    setSelected(deal);
    setResult(null);
  };

  // ── Structuring guidance derived from upstream ─────────────────────────
  const getGuidance = (deal) => {
    if (!deal) return null;
    const elig  = _parseElig(deal);
    const score = _parseScore(deal);
    const sc    = SECTOR_CARBON[deal.sector] || { tco2e: 5000 };
    const size  = deal.deployed_amount_usd || 0;

    const INSTRUMENT_LABELS = {
      green_loan:"Green Loan", sll:"Sustainability-Linked Loan",
      project_finance:"Project Finance", blended_finance:"Blended Finance",
      carbon_project:"Carbon Project Finance",
    };

    const recommendation =
      elig === "eligible"   && score >= 65 ? "Green Loan (Clear path — full eligibility)" :
      elig === "conditional" && score >= 55 ? "Green Loan with Conditions" :
      elig === "conditional" && score >= 35 ? "Blended Finance recommended — signal below threshold" :
      score && score < 35                   ? "Reject or restructure — insufficient investment signal" :
      "Requires eligibility clearance before structuring";

    const riskFlags = [];
    if (!score) riskFlags.push("Signal Score not computed — credit quality unverified");
    if (elig === "conditional") riskFlags.push("Conditional eligibility — conditions must be satisfied");
    if (elig === "blocked") riskFlags.push("Eligibility blocked — structuring not recommended yet");
    if (size === 0) riskFlags.push("Facility amount not set — carried from deal intake");

    return { elig, score, recommendation, riskFlags, sectorBenchmark: sc.tco2e };
  };

  const handleDealSelect = (dealId) => {
    const deal = deals.find(d => d.deal_id === dealId) || null;
    populateFromDeal(deal);
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await client.post(`/fi/decisions/deals/${selected.deal_id}/action`, {
        action: "mark_data_received",
        notes: `Financing structure: ${form.instrument} | USD ${parseFloat(form.amount_usd||0).toLocaleString()} | ${form.tenor_years}yr | Rate: ${form.rate_pct||"TBD"}% | ${form.notes}`,
      });
      toast.success("Financing structure saved.");
      setResult({ saved: true, deal: selected.deal_id });
    } catch { toast.error("Save failed — deal may not be at data_request stage."); }
    setSaving(false);
  };

  const guidance = selected ? getGuidance(selected) : null;
  const INSTRUMENT_OPTS = [
    { v:"green_loan",       l:"Green Loan"               },
    { v:"sll",              l:"Sustainability-Linked Loan"},
    { v:"project_finance",  l:"Project Finance"          },
    { v:"blended_finance",  l:"Blended Finance"          },
    { v:"carbon_project",   l:"Carbon Project Finance"   },
  ];

  return (
    <div className="space-y-4" data-testid="section-financing">
      {/* Linear flow context */}
      <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl">
        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex-shrink-0">Credit Engine:</p>
        {[["Financial Structuring","text-teal-700 bg-teal-50 border-teal-200"],["Due Diligence","text-slate-500 bg-slate-50 border-slate-200"],["Deal Actions","text-slate-500 bg-slate-50 border-slate-200"],["Credit Memo","text-slate-500 bg-slate-50 border-slate-200"]].map(([s,c],i)=>(
          <React.Fragment key={s}>
            {i>0&&<ArrowRight size={9} className="text-slate-300 flex-shrink-0"/>}
            <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold whitespace-nowrap ${c} ${i===0?"ring-1 ring-teal-400":""}`}>{s}</span>
          </React.Fragment>
        ))}
      </div>

      {/* Deal selector — required, not optional */}
      <div>
        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Select Deal to Structure</label>
        <select value={selected?.deal_id||""}
          onChange={e => handleDealSelect(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400"
          data-testid="financing-deal-select">
          <option value="">— Select a deal —</option>
          {deals.map(d=><option key={d.deal_id} value={d.deal_id}>{d.deal_id} — {_parseName(d)} ({d.stage_label||d.status})</option>)}
        </select>
      </div>

      {!selected ? (
        <div className="border border-dashed border-slate-200 rounded-xl p-8 text-center">
          <DollarSign size={24} className="mx-auto mb-2 text-slate-200"/>
          <p className="text-sm text-slate-400">Select a deal to auto-populate structuring parameters</p>
          <p className="text-xs text-slate-300 mt-1">Fields are pre-filled from eligibility, signal score, and deal data</p>
        </div>
      ) : (
        <div className="grid grid-cols-[1fr_300px] gap-5">
          {/* Left: Structuring form (auto-prefilled) */}
          <div className="space-y-4">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
              Financing Structure
              <span className="ml-2 text-[9px] bg-teal-100 text-teal-700 font-bold px-1.5 py-0.5 rounded normal-case">Auto-prefilled from deal data</span>
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Instrument Type</label>
                <select value={form.instrument} onChange={e=>setForm({...form,instrument:e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none">
                  {INSTRUMENT_OPTS.map(i=><option key={i.v} value={i.v}>{i.l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Tenor (years)</label>
                <input type="number" value={form.tenor_years} onChange={e=>setForm({...form,tenor_years:e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Facility Amount (USD)</label>
                <input type="number" value={form.amount_usd} onChange={e=>setForm({...form,amount_usd:e.target.value})}
                  placeholder={selected.deployed_amount_usd ? `${selected.deployed_amount_usd}` : "Enter amount"}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none"/>
                {!selected.deployed_amount_usd && <p className="text-[10px] text-amber-600 mt-0.5">Not yet set in deal record</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Grace Period (months)</label>
                <input type="number" value={form.grace_months} onChange={e=>setForm({...form,grace_months:e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Indicative Rate (% p.a.)</label>
                <input type="number" step="0.1" value={form.rate_pct} onChange={e=>setForm({...form,rate_pct:e.target.value})}
                  placeholder="Auto-calculated in Credit Memo"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Carbon Revenue Share (%)</label>
                <input type="number" step="1" value={form.carbon_revenue_share_pct} onChange={e=>setForm({...form,carbon_revenue_share_pct:e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none"/>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Structuring Notes</label>
              <textarea value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} rows={2}
                placeholder="Any special conditions, covenants, or structuring rationale..."
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl resize-none focus:outline-none"/>
            </div>
            <div className="flex gap-3">
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700 disabled:opacity-40"
                data-testid="save-financing-btn">
                {saving?<RefreshCw size={13} className="animate-spin"/>:<CheckCircle2 size={13}/>}
                {saving?"Saving...":"Save Structure"}
              </button>
              <button onClick={()=>{
                if (typeof window !== "undefined") {
                  window.history.pushState({}, "", `/fi/workspace?m=credit&s=data_import`);
                  window.dispatchEvent(new PopStateEvent("popstate"));
                }
              }} className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-sm text-slate-600 rounded-xl hover:bg-slate-50">
                <ArrowRight size={13}/> Next: Due Diligence
              </button>
            </div>
            {result?.saved && (
              <div className="flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 px-3 py-2 rounded-xl border border-emerald-200">
                <CheckCircle2 size={12}/> Structure saved for {result.deal}
              </div>
            )}
          </div>

          {/* Right: Structuring Guidance Panel (from upstream) */}
          {guidance && (
            <div className="space-y-3">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Structuring Guidance</p>
              <div className="bg-[#0F172A] rounded-xl p-4 text-white space-y-3">
                {/* Eligibility */}
                <div>
                  <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Eligibility</p>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                    guidance.elig==="eligible"?"bg-emerald-500/20 text-emerald-300":
                    guidance.elig==="conditional"?"bg-amber-500/20 text-amber-300":
                    guidance.elig==="blocked"?"bg-red-500/20 text-red-400":"bg-slate-700 text-slate-400"}`}>
                    {guidance.elig==="eligible"?"Eligible":guidance.elig==="conditional"?"Conditionally Eligible":guidance.elig==="blocked"?"Blocked":"Not Assessed"}
                  </span>
                </div>
                {/* Signal Score */}
                <div>
                  <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Signal Score</p>
                  {guidance.score ? (
                    <span className={`text-xs font-bold ${guidance.score>=65?"text-emerald-400":guidance.score>=45?"text-amber-400":"text-red-400"}`}>
                      {guidance.score}/100
                    </span>
                  ) : (
                    <span className="text-xs text-slate-600 italic">Not computed</span>
                  )}
                </div>
                {/* Recommended structure */}
                <div>
                  <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Recommended Structure</p>
                  <p className="text-xs text-white">{guidance.recommendation}</p>
                </div>
              </div>
              {/* Risk flags */}
              {guidance.riskFlags.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Risk Flags</p>
                  {guidance.riskFlags.map((f,i)=>(
                    <div key={i} className="flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1.5 rounded-lg">
                      <AlertTriangle size={10} className="flex-shrink-0 mt-0.5"/>{f}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function SectionPortfolioIntelligence() {
  const [data, setData]       = useState(null);
  const [govConfig, setGov]   = useState(null);
  const [deals, setDeals]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      client.get("/fi/portfolio-intelligence/dashboard"),
      client.get("/fi/governance-config/current").catch(() => ({ data: null })),
      client.get("/fi/decisions/deals").catch(() => ({ data: { deals: [] } })),
    ]).then(([pr, gr, dr]) => {
      setData(pr.data);
      setGov(gr.data?.config || null);
      setDeals(dr.data.deals || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-12"><RefreshCw size={16} className="animate-spin text-teal-500" /></div>;
  if (!data) return null;

  const STAGE_COLORS = { screening:"bg-slate-200", data_request:"bg-blue-300", due_diligence:"bg-amber-400", credit_committee:"bg-orange-500", term_sheet:"bg-teal-500", financial_close:"bg-emerald-600", rejected:"bg-red-300" };

  // ── Risk-weighted exposure by stage ────────────────────────────────────────
  const RISK_WEIGHTS = { screening:1.00, data_request:0.85, due_diligence:0.70, credit_committee:0.60, term_sheet:0.50, financial_close:0.30, rejected:0 };
  const stageFunnel = data.stage_funnel || [];
  const totalPipeline = data.total_pipeline_usd || 1;
  const riskWeighted = stageFunnel.reduce((sum, s) => sum + (s.amount_usd||0) * (RISK_WEIGHTS[s.stage]||0.5), 0);

  // ── Diversification score (HHI-based on sector) ────────────────────────────
  const sectors = data.sector_exposure?.breakdown || [];
  const hhi = sectors.reduce((sum, s) => { const w = (s.amount_usd||0) / totalPipeline; return sum + w * w; }, 0);
  const diversScore = Math.round(Math.max(0, (1 - hhi)) * 100);
  const diversLabel = diversScore >= 75 ? "Well Diversified" : diversScore >= 50 ? "Moderately Diversified" : "Concentrated";
  const diversColor = diversScore >= 75 ? "text-emerald-600" : diversScore >= 50 ? "text-amber-600" : "text-red-600";

  // ── Portfolio Health Score (transparent, component-based) ──────────────────
  const avgSignal = data.carbon_portfolio?.avg_signal_score || 0;
  const maxSectorPct = sectors[0]?.concentration_pct || 0;
  const countryPct = 100; // NG dominates current portfolio
  const dealsWithScore = deals.filter(d => {
    const m = (d.notes||"").match(/Decision:.*?score[\s:]*(\d+)\/100/i)||(d.notes||"").match(/\bscore[\s:]+(\d+)\/100/i);
    return !!m;
  }).length;
  const totalActive = data.active_deals || 1;
  const bottlenecks = (data.concentration_alerts || []).filter(a => a.severity === "critical").length;

  const HEALTH_COMPONENTS = [
    {
      label: "Credit Quality",
      weight: 25,
      desc: "Average Signal Score across scored deals",
      score: Math.round(Math.min(avgSignal / 100, 1) * 25),
      max: 25,
      detail: avgSignal > 0 ? `${avgSignal.toFixed(0)}/100 avg signal` : "No scores computed",
    },
    {
      label: "Concentration Risk",
      weight: 25,
      desc: "Sector and country within governance limits",
      score: Math.round(Math.max(0, 1 - Math.max(maxSectorPct, countryPct) / 100) * 25),
      max: 25,
      detail: `Max sector: ${maxSectorPct}% | Country: ${countryPct}%`,
    },
    {
      label: "Data Completeness",
      weight: 25,
      desc: "% of active deals with credit assessment",
      score: Math.round(Math.min(1, dealsWithScore / totalActive) * 25),
      max: 25,
      detail: `${dealsWithScore} of ${totalActive} deals assessed`,
    },
    {
      label: "Pipeline Health",
      weight: 25,
      desc: "Deals progressing without critical bottlenecks",
      score: Math.round(Math.max(0, 1 - bottlenecks * 0.25) * 25),
      max: 25,
      detail: bottlenecks > 0 ? `${bottlenecks} critical alert${bottlenecks>1?"s":""}` : "No critical alerts",
    },
  ];
  const totalHealth = HEALTH_COMPONENTS.reduce((s, c) => s + c.score, 0);

  // ── Downgrade triggers (explicit, visible rules) ────────────────────────────
  const concentrationBreach = maxSectorPct > (govConfig?.sector_concentration_warning_pct || 40) || countryPct > 50;
  const creditQualityWeak   = HEALTH_COMPONENTS[0].score < Math.round(0.4 * 25); // < 40% of max
  const DOWNGRADE_TRIGGERS  = [];
  if (concentrationBreach) DOWNGRADE_TRIGGERS.push({ rule:"Concentration breach active — portfolio grade capped at C", applied: true });
  if (creditQualityWeak)   DOWNGRADE_TRIGGERS.push({ rule:"Credit quality below 40% — grade downgraded by one level", applied: true });

  // Apply downgrade rules
  let rawGrade = totalHealth >= 80 ? "A" : totalHealth >= 65 ? "B" : totalHealth >= 50 ? "C" : "D";
  if (concentrationBreach && rawGrade === "A") rawGrade = "B"; // cap at B if breach (not C, C is for severe)
  if (concentrationBreach && rawGrade === "B") rawGrade = "C"; // cap at C if breach
  if (creditQualityWeak && rawGrade === "B")   rawGrade = "C";
  if (creditQualityWeak && rawGrade === "A")   rawGrade = "B";
  const grade = rawGrade;
  const gradeColor = grade === "A" ? "text-emerald-600" : grade === "B" ? "text-teal-600" : grade === "C" ? "text-amber-600" : "text-red-600";
  const gradeLabel = { A:"Institutional Grade", B:"Satisfactory", C:"Needs Improvement", D:"At Risk" }[grade];

  // ── Recommended Actions (rule-based, specific) ───────────────────────────────
  const RECOMMENDED_ACTIONS = [];
  if (maxSectorPct > (govConfig?.sector_concentration_warning_pct || 40)) {
    const topSec = sectors[0]?.label || "dominant sector";
    RECOMMENDED_ACTIONS.push({ priority:"H", action:`Reduce ${topSec} exposure — target below ${govConfig?.sector_concentration_warning_pct||40}% by diversifying into waste or biogas sector deals` });
  }
  if (countryPct > 50) RECOMMENDED_ACTIONS.push({ priority:"H", action:"Diversify geography — add deals in GH, KE, or ZA to reduce single-country exposure below 50%" });
  const topDealPct = data.stage_funnel ? Math.round((data.stage_funnel[0]?.amount_usd||0)/totalPipeline*100) : 0;
  if (topDealPct > (govConfig?.single_deal_max_portfolio_pct||25)) RECOMMENDED_ACTIONS.push({ priority:"H", action:"Avoid large single-ticket deals until portfolio diversification improves — single-name concentration is above policy limit" });
  if (avgSignal > 0 && avgSignal < 50) RECOMMENDED_ACTIONS.push({ priority:"M", action:"Strengthen credit assessment pipeline — prioritise Signal Score completion for unscored deals before new approvals" });
  if (dealsWithScore < totalActive * 0.6) RECOMMENDED_ACTIONS.push({ priority:"M", action:"Run Signal Score for all active deals — credit quality unverified for most of the portfolio" });
  RECOMMENDED_ACTIONS.push({ priority:"L", action:"Strengthen MRV validation before next disbursement cycle — conditional MRV status may affect carbon revenue credibility" });

  // ── Exposure vs governance limits ──────────────────────────────────────────
  const govLimits = govConfig ? {
    sectorBlock: govConfig.sector_concentration_block_pct || 60,
    countryAlert: govConfig.country_concentration_alert_pct || 50,
    singleDeal: govConfig.single_deal_max_portfolio_pct || 25,
    minSignal: govConfig.minimum_signal_score || 55,
  } : null;

  return (
    <SectionFrame icon={Activity} title="Portfolio Intelligence" desc="Exposure analysis, risk concentration, diversification metrics, and portfolio health score.">

      {/* ── Portfolio Health Score (explicit weights + downgrade triggers) ── */}
      <div className="bg-[#0F172A] rounded-xl p-5 mb-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] text-slate-400 uppercase tracking-[0.15em] font-bold mb-1">Portfolio Health Score</p>
            <p className="text-xs text-slate-500">4 components, 25% weight each. Downgrade rules applied where triggered.</p>
          </div>
          <div className="text-right">
            <p className={`text-4xl font-bold tabular-nums ${gradeColor}`}>{grade}</p>
            <p className="text-[10px] text-slate-400">{totalHealth}/100 · {gradeLabel}</p>
            {DOWNGRADE_TRIGGERS.filter(t=>t.applied).length > 0 && (
              <p className="text-[9px] text-amber-400 mt-0.5">Downgrade rules applied</p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {HEALTH_COMPONENTS.map(c => {
            const pct = Math.round(c.score / c.max * 100);
            return (
              <div key={c.label} className="bg-white/5 rounded-lg p-3">
                <div className="flex justify-between items-center mb-1.5">
                  <div>
                    <p className="text-[10px] font-bold text-white">{c.label}</p>
                    <p className="text-[9px] text-slate-600">{c.weight}% weight</p>
                  </div>
                  <span className={`text-xs font-bold tabular-nums ${c.score >= c.max*0.8?"text-emerald-400":c.score >= c.max*0.5?"text-amber-400":"text-red-400"}`}>{c.score}/{c.max}</span>
                </div>
                <div className="h-1 bg-white/10 rounded-full overflow-hidden mb-1.5">
                  <div className={`h-full rounded-full ${pct>=80?"bg-emerald-500":pct>=50?"bg-amber-500":"bg-red-500"}`} style={{width:`${pct}%`}}/>
                </div>
                <p className="text-[9px] text-slate-400">{c.desc}</p>
                <p className="text-[10px] text-slate-300 mt-0.5 font-medium">{c.detail}</p>
              </div>
            );
          })}
        </div>
        {/* Downgrade triggers — visible rules */}
        {DOWNGRADE_TRIGGERS.some(t=>t.applied) && (
          <div className="mt-3 pt-3 border-t border-white/10 space-y-1">
            <p className="text-[9px] text-amber-400 font-bold uppercase tracking-wider">Active Downgrade Rules</p>
            {DOWNGRADE_TRIGGERS.filter(t=>t.applied).map((t,i)=>(
              <p key={i} className="text-[10px] text-amber-300">→ {t.rule}</p>
            ))}
          </div>
        )}
      </div>

      {/* ── KPI strip ── */}
      <div className="grid grid-cols-4 gap-4 mb-5">
        <MetricCard label="Risk-Weighted Exposure" value={`$${(riskWeighted/1e6).toFixed(1)}M`} color="text-slate-900" />
        <MetricCard label="Diversification Score" value={`${diversScore}/100`} color={diversColor} />
        <MetricCard label="Portfolio Grade" value={`${avgSignal > 0 ? avgSignal.toFixed(0) : "—"}/100`} color={avgSignal >= 65 ? "text-emerald-700" : avgSignal >= 45 ? "text-amber-600" : "text-red-600"} />
        <MetricCard label="Concentration Alerts" value={data.alert_count} color={data.alert_count > 0 ? "text-amber-600" : "text-emerald-700"} />
      </div>

      <div className="grid grid-cols-2 gap-5 mb-5">
        {/* Sector exposure vs governance limits */}
        <SectionCard title="Sector Exposure vs Internal Limits" noPadding>
          {govLimits && (
            <div className="px-5 py-2 bg-slate-50 border-b border-slate-100 flex gap-4 text-[10px] text-slate-500">
              <span>Warning: &gt;{govLimits.sectorWarn}%</span>
              <span>Block: &gt;{govLimits.sectorBlock}%</span>
            </div>
          )}
          {sectors.slice(0, 6).map(s => {
            const pct = s.concentration_pct || 0;
            const atWarn = govLimits && pct > govLimits.sectorWarn;
            const atBlock = govLimits && pct > govLimits.sectorBlock;
            return (
              <div key={s.label} className="flex items-center gap-3 px-5 py-3 border-b border-slate-50 last:border-0">
                <p className="text-xs text-slate-700 flex-1 capitalize">{s.label}</p>
                <div className="flex items-center gap-2">
                  <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden relative">
                    <div className={`h-full rounded-full ${atBlock?"bg-red-500":atWarn?"bg-amber-500":"bg-teal-500"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                    {govLimits && (
                      <div className="absolute top-0 h-full border-r-2 border-amber-400 opacity-60" style={{left:`${govLimits.sectorWarn}%`}}/>
                    )}
                  </div>
                  <span className={`text-xs font-semibold w-10 text-right ${atBlock?"text-red-600":atWarn?"text-amber-600":"text-slate-700"}`}>{pct}%</span>
                  {atBlock && <AlertTriangle size={11} className="text-red-500" />}
                  {atWarn && !atBlock && <AlertTriangle size={11} className="text-amber-500" />}
                </div>
              </div>
            );
          })}
        </SectionCard>

        {/* Risk-weighted stage view */}
        <SectionCard title="Risk-Weighted Exposure by Stage" noPadding>
          <div className="px-5 py-2 bg-slate-50 border-b border-slate-100 text-[10px] text-slate-500">
            Lower weight = higher credit quality (financial close = 0.30×)
          </div>
          {stageFunnel.filter(s => s.count > 0 && s.stage !== "rejected").map(s => {
            const rw = s.amount_usd * (RISK_WEIGHTS[s.stage] || 0.5);
            const rwPct = totalPipeline > 0 ? Math.round(rw / totalPipeline * 100) : 0;
            return (
              <div key={s.stage} className="flex items-center gap-3 px-5 py-3 border-b border-slate-50 last:border-0">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${STAGE_COLORS[s.stage] || "bg-slate-300"}`} />
                <p className="text-xs text-slate-700 flex-1">{s.label}</p>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-400">{(RISK_WEIGHTS[s.stage]||0.5).toFixed(2)}×</span>
                  <span className="text-xs font-semibold text-slate-700">${(rw/1e6).toFixed(1)}M</span>
                  <span className="text-[10px] text-slate-400">({rwPct}%)</span>
                </div>
              </div>
            );
          })}
          <div className="px-5 py-3 bg-slate-50 flex justify-between text-xs">
            <span className="font-bold text-slate-700">Total Risk-Weighted</span>
            <span className="font-bold text-slate-900">${(riskWeighted/1e6).toFixed(1)}M</span>
          </div>
        </SectionCard>
      </div>

      {/* ── Diversification score ── */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="col-span-1 bg-white border border-slate-200 rounded-xl p-4 text-center">
          <p className="text-[10px] text-slate-400 uppercase tracking-[0.12em] font-bold mb-2">Diversification Score</p>
          <p className={`text-4xl font-bold ${diversColor}`}>{diversScore}</p>
          <p className="text-xs text-slate-400 mt-1">{diversLabel}</p>
          <p className="text-[10px] text-slate-400 mt-2">Herfindahl-Hirschman Index based on sector allocation. 0=concentrated, 100=fully diversified.</p>
        </div>
        <div className="col-span-2">
          {/* Concentration alerts */}
          <div className="space-y-2">
            {(data.concentration_alerts||[]).length === 0 ? (
              <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                <CheckCircle2 size={13} className="text-emerald-600 flex-shrink-0"/>
                <p className="text-xs text-emerald-700">All concentration metrics within governance limits</p>
              </div>
            ) : (data.concentration_alerts||[]).map((a,i) => (
              <div key={i} className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${a.severity==="critical"?"bg-red-50 border-red-200":a.severity==="warning"?"bg-amber-50 border-amber-200":"bg-slate-50 border-slate-200"}`}>
                <AlertTriangle size={13} className={`flex-shrink-0 mt-0.5 ${a.severity==="critical"?"text-red-600":"text-amber-600"}`}/>
                <div>
                  <p className="text-xs font-semibold text-slate-700">{a.type}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{a.message}</p>
                  {a.recommendation && <p className="text-xs text-teal-600 mt-1">{a.recommendation}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Sharper portfolio insight ── */}
      <div className="border border-slate-200 rounded-xl p-4 mb-5">
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Portfolio Assessment</p>
        {(() => {
          const parts = [];
          const topSec = sectors[0]?.label;
          const topPct = sectors[0]?.concentration_pct || 0;
          if (topPct > 40) parts.push(`Portfolio is overexposed to ${topSec} (${topPct}%)`);
          if (countryPct > 50) parts.push("single-country concentration risk is high (Nigeria 100%)");
          if (diversScore < 40) parts.push("portfolio diversification is weak");
          if (avgSignal > 0 && avgSignal < 50) parts.push("credit quality is below threshold");
          if (DOWNGRADE_TRIGGERS.some(t=>t.applied)) parts.push("downgrade triggers are active");
          const verdict = parts.length > 0
            ? `${parts.join(", ")}. Immediate action required to improve portfolio quality.`
            : "Portfolio is within policy limits. Standard monitoring applies.";
          return <p className="text-sm font-semibold text-slate-800">{verdict.charAt(0).toUpperCase()+verdict.slice(1)}</p>;
        })()}
        <p className="text-xs text-slate-400 mt-2 tabular-nums">
          Grade {grade} ({totalHealth}/100) · Risk-weighted: ${(riskWeighted/1e6).toFixed(1)}M · Diversification: {diversScore}/100 · Avg signal: {avgSignal>0?`${avgSignal.toFixed(0)}/100`:"—"}
        </p>
      </div>

      {/* ── Recommended Actions ── */}
      <div>
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Recommended Actions</p>
        <div className="space-y-2">
          {RECOMMENDED_ACTIONS.map((a,i) => (
            <div key={i} className={`flex items-start gap-2.5 border rounded-xl px-4 py-3 ${
              a.priority==="H"?"bg-red-50 border-red-200":a.priority==="M"?"bg-amber-50 border-amber-200":"bg-slate-50 border-slate-200"}`}>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5 ${
                a.priority==="H"?"bg-red-100 text-red-700":a.priority==="M"?"bg-amber-100 text-amber-700":"bg-slate-100 text-slate-500"}`}>
                {a.priority==="H"?"HIGH":a.priority==="M"?"MEDIUM":"LOW"}
              </span>
              <p className="text-xs text-slate-700">{a.action}</p>
            </div>
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

// ── CONDITIONS TRACKER ─────────────────────────────────────────────────────


// ── PORTFOLIO CONTROL SUMMARY (replaces basic SectionAnalytics) ────────────
// Question: What in this portfolio needs attention right now?
function SectionAnalytics() {
  const [deals,  setDeals]  = useState([]);
  const [stats,  setStats]  = useState(null);
  const [gov,    setGov]    = useState(null);
  const [loading,setLoading]= useState(true);

  useEffect(() => {
    Promise.all([
      client.get("/fi/decisions/deals"),
      client.get("/fi/workspace/pipeline-stats"),
      client.get("/fi/governance-config/current").catch(()=>({data:null})),
    ]).then(([dr, sr, gr]) => {
      setDeals(dr.data.deals || []);
      setStats(sr.data);
      setGov(gr.data?.config || null);
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-12"><RefreshCw size={14} className="animate-spin text-teal-500"/></div>;

  const active    = deals.filter(d => !["rejected"].includes(d.status));
  const screening = active.filter(d => ["screening","data_request"].includes(d.status));
  const inProcess = active.filter(d => ["due_diligence","credit_committee","term_sheet"].includes(d.status));
  const deployed  = active.filter(d => d.status === "financial_close");
  const totalAmt  = active.reduce((s,d)=>s+(d.deployed_amount_usd||0),0) || 1;
  const scores    = deals.map(_parseScore).filter(s=>s!==null);
  const avgScore  = scores.length ? Math.round(scores.reduce((a,b)=>a+b,0)/scores.length) : null;

  // Sector concentration
  const sectorMap = {};
  active.forEach(d => { sectorMap[d.sector||"Unknown"]=(sectorMap[d.sector||"Unknown"]||0)+(d.deployed_amount_usd||0); });
  const sortedSectors = Object.entries(sectorMap).sort((a,b)=>b[1]-a[1]);
  const topSectors = sortedSectors.slice(0,3);

  // Top deals by size
  const topDeals = [...active].sort((a,b)=>(b.deployed_amount_usd||0)-(a.deployed_amount_usd||0)).slice(0,3);

  // MRV classification
  const getMRVLabel = (deal) => {
    const notes = (deal.notes||"").toLowerCase();
    const ev = deal.evidence_readiness_pct || 0;
    if ((deal.notes||"").length < 20 && ev === 0) return "not_ready";
    if (notes.includes("verified") && ev >= 80) return "ready";
    return "conditional";
  };

  // Governance thresholds
  const sectorWarn  = gov?.sector_concentration_warning_pct  || 40;
  const countryWarn = gov?.country_concentration_alert_pct   || 50;
  const singleDeal  = gov?.single_deal_max_portfolio_pct     || 25;
  const minSignal   = gov?.minimum_signal_score              || 45;

  // Portfolio Alerts
  const ALERTS = [];
  topSectors.forEach(([sec, amt]) => {
    const pct = Math.round(amt/totalAmt*100);
    if (pct > sectorWarn) ALERTS.push({ sev:"H", text:`${sec.charAt(0).toUpperCase()+sec.slice(1)} exposure = ${pct}% (above ${sectorWarn}% threshold)` });
  });
  const topDeal = topDeals[0];
  if (topDeal) {
    const topPct = Math.round((topDeal.deployed_amount_usd||0)/totalAmt*100);
    if (topPct > singleDeal) ALERTS.push({ sev:"H", text:`Single deal = ${topPct}% of total portfolio (above ${singleDeal}% limit)` });
  }
  // Stale deals: in screening/data_request with no recent history
  const staleCount = screening.filter(d => {
    const history = d.stage_history || [];
    if (history.length === 0) return true;
    const last = history[history.length-1];
    if (!last.timestamp) return true;
    const days = (Date.now() - new Date(last.timestamp).getTime()) / (1000*60*60*24);
    return days > 14;
  }).length;
  if (staleCount > 0) ALERTS.push({ sev:"M", text:`${staleCount} deal${staleCount>1?"s":""} pending data for >14 days — follow up required` });
  if (!stats?.new_this_week) ALERTS.push({ sev:"L", text:"No new deals added this week — origination pipeline may need attention" });
  if (avgScore !== null && avgScore < minSignal) ALERTS.push({ sev:"M", text:`Avg signal score ${avgScore}/100 — below ${minSignal} minimum threshold` });

  // Capital at Risk
  const atRisk = active.filter(d =>
    getMRVLabel(d) === "not_ready" ||
    (_parseElig(d) === "blocked") ||
    (_parseScore(d) !== null && _parseScore(d) < 35)
  );
  const watchlist = active.filter(d =>
    _parseScore(d) !== null && _parseScore(d) >= 35 && _parseScore(d) < 45 ||
    _parseElig(d) === "conditional" ||
    getMRVLabel(d) === "conditional"
  );

  // Pipeline movement (from stage history)
  const now = Date.now();
  const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
  const progressedThisWeek = active.filter(d => {
    const h = d.stage_history || [];
    return h.some(e => e.timestamp && (now - new Date(e.timestamp).getTime()) < WEEK_MS &&
      ["advance_to_data_request","advance_to_due_diligence","approve_for_committee","issue_term_sheet","mark_financial_close"].includes(e.action));
  }).length;
  const stalledDeals = active.filter(d => {
    const h = d.stage_history || [];
    if (!h.length) return false;
    const last = h[h.length-1];
    return last.timestamp && (now - new Date(last.timestamp).getTime()) > 14*24*60*60*1000 &&
      !["financial_close"].includes(d.status);
  }).length;

  // Conversion rate: approved (closed+active DD+) / total entered
  const conversionPct = deals.length > 0 ? Math.round(
    deals.filter(d=>!["screening","data_request","rejected"].includes(d.status)).length / Math.max(1,deals.length) * 100
  ) : 0;

  const SEV_STYLE = { H:"bg-red-50 border-red-200 text-red-700", M:"bg-amber-50 border-amber-200 text-amber-700", L:"bg-slate-50 border-slate-200 text-slate-500" };
  const SEV_DOT   = { H:"bg-red-500", M:"bg-amber-400", L:"bg-slate-400" };

  return (
    <div className="space-y-5" data-testid="section-portfolio-control">

      {/* Section header */}
      <div>
        <h2 className="text-base font-bold text-slate-900">Portfolio Control Summary</h2>
        <p className="text-xs text-slate-500 mt-0.5">Live view of portfolio composition, alerts, pipeline movement, and capital at risk.</p>
      </div>

      {/* Portfolio Snapshot */}
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Portfolio Snapshot</p>
        <div className="grid grid-cols-3 gap-3 mb-3">
          {[
            { label:"Total Pipeline",     value:`$${(totalAmt/1e6).toFixed(1)}M`, sub:`${active.length} active deals`,       color:"text-slate-900" },
            { label:"Avg Signal Score",   value:avgScore!==null?`${avgScore}/100`:"—", sub:avgScore!==null?(avgScore>=65?"Investable range":avgScore>=45?"Conditional":"Below threshold"):"No scores yet", color:avgScore!==null?(avgScore>=65?"text-emerald-600":avgScore>=45?"text-amber-600":"text-red-500"):"text-slate-400" },
            { label:"Capital Deployed",   value:`$${(deals.filter(d=>d.status==="financial_close").reduce((s,d)=>s+(d.deployed_amount_usd||0),0)/1e6).toFixed(1)}M`, sub:`${deployed.length} deals at financial close`, color:"text-teal-600" },
          ].map(m=>(
            <div key={m.label} className="bg-white border border-slate-200 rounded-xl p-3.5">
              <p className="text-xs text-slate-400 mb-1">{m.label}</p>
              <p className={`text-xl font-bold tabular-nums ${m.color}`}>{m.value}</p>
              <p className="text-xs text-slate-400 mt-1">{m.sub}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {/* Top 3 sectors */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Top 3 Sectors by Exposure</p>
            <div className="space-y-2">
              {topSectors.map(([sec, amt]) => {
                const pct = Math.round(amt/totalAmt*100);
                const alert = pct > sectorWarn;
                return (
                  <div key={sec}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className="text-slate-700 capitalize">{sec}</span>
                      <span className={`font-bold tabular-nums ${alert?"text-amber-700":"text-slate-900"}`}>{pct}%</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${alert?"bg-amber-400":"bg-teal-500"}`} style={{width:`${pct}%`}}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Top 3 deals */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Top 3 Deals by Size</p>
            <div className="space-y-2">
              {topDeals.map((d, i) => {
                const pct = Math.round((d.deployed_amount_usd||0)/totalAmt*100);
                const alert = pct > singleDeal;
                return (
                  <div key={d.deal_id} className="flex items-center gap-2.5">
                    <span className="text-[10px] text-slate-400 font-mono w-4">{i+1}.</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-700 truncate">{_parseName(d).split(" ").slice(0,3).join(" ")}</span>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                          {alert&&<AlertTriangle size={9} className="text-amber-500"/>}
                          <span className={`font-bold tabular-nums ${alert?"text-amber-700":"text-slate-900"}`}>${((d.deployed_amount_usd||0)/1e6).toFixed(1)}M</span>
                        </div>
                      </div>
                      <div className="h-1 bg-slate-100 rounded-full overflow-hidden mt-0.5">
                        <div className={`h-full rounded-full ${alert?"bg-amber-400":"bg-teal-500"}`} style={{width:`${Math.min(pct,100)}%`}}/>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Portfolio Alerts */}
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Portfolio Alerts</p>
        {ALERTS.length === 0 ? (
          <div className="flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 px-4 py-3 rounded-xl">
            <CheckCircle2 size={12}/> No active alerts — portfolio within all policy thresholds
          </div>
        ) : (
          <div className="space-y-2">
            {ALERTS.map((a, i) => (
              <div key={i} className={`flex items-start gap-2.5 border rounded-xl px-4 py-2.5 ${SEV_STYLE[a.sev]}`}>
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1 ${SEV_DOT[a.sev]}`}/>
                <p className="text-xs">⚠ {a.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pipeline Movement */}
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Pipeline Movement</p>
        <div className="grid grid-cols-4 gap-3">
          {[
            { label:"New This Week",   value:stats?.new_this_week||0, color:"text-teal-600",   good:true   },
            { label:"Progressed",      value:progressedThisWeek,      color:"text-emerald-600", good:true   },
            { label:"Stalled (>14d)",  value:stalledDeals,             color:stalledDeals>0?"text-amber-600":"text-emerald-600", good:stalledDeals===0 },
            { label:"Conversion Rate", value:`${conversionPct}%`,     color:conversionPct>=50?"text-teal-600":"text-amber-600", good:conversionPct>=50 },
          ].map(m=>(
            <div key={m.label} className={`border rounded-xl p-3.5 text-center ${m.good===false?"border-amber-200 bg-amber-50":"border-slate-200 bg-white"}`}>
              <p className={`text-2xl font-bold tabular-nums ${m.color}`}>{m.value}</p>
              <p className="text-[10px] text-slate-400 mt-1">{m.label}</p>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-teal-500 inline-block"></span>Screening: {screening.length}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block"></span>In Process: {inProcess.length}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>Deployed: {deployed.length}</span>
        </div>
      </div>

      {/* Capital at Risk */}
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Capital at Risk / Watchlist</p>
        {atRisk.length === 0 && watchlist.length === 0 ? (
          <div className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 px-4 py-3 rounded-xl flex items-center gap-2">
            <CheckCircle2 size={12}/> No deals flagged for capital risk — standard monitoring applies
          </div>
        ) : (
          <div className="space-y-2">
            {atRisk.map(d => (
              <div key={d.deal_id} className="flex items-center gap-3 border border-red-200 bg-red-50 rounded-xl px-4 py-2.5">
                <span className="text-[10px] font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded">AT RISK</span>
                <p className="text-xs font-medium text-slate-900 flex-1 truncate">{_parseName(d)}</p>
                <p className="text-[10px] text-red-600">
                  {getMRVLabel(d)==="not_ready"?"MRV Not Ready":_parseElig(d)==="blocked"?"Eligibility Blocked":`Score ${_parseScore(d)}/100`}
                </p>
              </div>
            ))}
            {watchlist.filter(d=>!atRisk.includes(d)).slice(0,4).map(d => (
              <div key={d.deal_id} className="flex items-center gap-3 border border-amber-200 bg-amber-50 rounded-xl px-4 py-2.5">
                <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded">WATCH</span>
                <p className="text-xs font-medium text-slate-900 flex-1 truncate">{_parseName(d)}</p>
                <p className="text-[10px] text-amber-600">
                  {_parseElig(d)==="conditional"?"Conditional eligibility":getMRVLabel(d)==="conditional"?"MRV Conditional":`Score ${_parseScore(d)||"—"}/100`}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── SECTION: Deal Screening (fast triage) ─────────────────────────────────
// ── SECTION: Deal Screening ────────────────────────────────────────────────

// Section 2: Portfolio Exposure — Decision Insight Layer
function SectionPortfolioExposure() {
  const [deals,     setDeals]     = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [govConfig, setGovConfig] = useState(null);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([
      client.get("/fi/decisions/deals"),
      client.get("/fi/workspace/portfolio-exposure").catch(()=>({data:null})),
      client.get("/fi/governance-config/current").catch(()=>({data:null})),
    ]).then(([dr, pr, gr]) => {
      setDeals(dr.data.deals || []);
      setPortfolio(pr.data);
      setGovConfig(gr.data?.config || null);
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-12"><RefreshCw size={14} className="animate-spin text-teal-500"/></div>;

  const active = deals.filter(d => !["rejected"].includes(d.status));
  const totalAmt = active.reduce((s,d)=>s+(d.deployed_amount_usd||0), 0) || 1;
  const deployed = deals.filter(d=>d.status==="financial_close").reduce((s,d)=>s+(d.deployed_amount_usd||0),0);

  // Sector concentration
  const sectorMap = {};
  active.forEach(d => {
    const amt = d.deployed_amount_usd || 0;
    sectorMap[d.sector||"Unknown"] = (sectorMap[d.sector||"Unknown"]||0) + amt;
  });

  // Country concentration
  const countryMap = {};
  active.forEach(d => {
    const c = _parseCountry(d);
    countryMap[c] = (countryMap[c]||0) + (d.deployed_amount_usd||1);
  });

  // Single-name concentration
  const sortedByAmt = [...active].sort((a,b)=>(b.deployed_amount_usd||0)-(a.deployed_amount_usd||0));
  const topDeal     = sortedByAmt[0];
  const topDealPct  = topDeal ? Math.round((topDeal.deployed_amount_usd||0)/totalAmt*100) : 0;

  // Financed emissions and reduction (using SECTOR_CARBON benchmarks)
  const dealsWithEmissions = active.map(d => ({
    deal: d,
    name: _parseName(d),
    financed_tco2e: (SECTOR_CARBON[d.sector]?.tco2e || 5000) * (d.deployed_amount_usd||0) / totalAmt * 1000,
    reduction_tco2e: (SECTOR_CARBON[d.sector]?.tco2e || 5000) * 0.30 * (d.deployed_amount_usd||0) / totalAmt * 1000,
  })).sort((a,b)=>b.financed_tco2e-a.financed_tco2e);

  // Concentration alerts
  const govSectorWarn = govConfig?.sector_concentration_warning_pct || 40;
  const govCountryWarn = govConfig?.country_concentration_alert_pct || 50;
  const govSingleDeal  = govConfig?.single_deal_max_portfolio_pct || 25;

  const ALERTS = [];
  Object.entries(sectorMap).forEach(([sec, amt]) => {
    const pct = Math.round(amt/totalAmt*100);
    if (pct > govSectorWarn) ALERTS.push({ sev:"H", text:`${sec.charAt(0).toUpperCase()+sec.slice(1)} exposure at ${pct}% — exceeds ${govSectorWarn}% sector concentration threshold` });
  });
  Object.entries(countryMap).forEach(([country, amt]) => {
    const pct = Math.round(amt/totalAmt*100);
    if (pct > govCountryWarn) ALERTS.push({ sev:"H", text:`${country} accounts for ${pct}% of current climate portfolio — exceeds ${govCountryWarn}% country threshold` });
  });
  if (topDealPct > govSingleDeal) ALERTS.push({ sev:"M", text:`${_parseName(topDeal)} accounts for ${topDealPct}% of total pipeline — single-name concentration above policy limit` });
  if (Object.keys(sectorMap).length <= 2) ALERTS.push({ sev:"M", text:"Portfolio diversification remains weak — fewer than 3 distinct sectors represented" });
  if (ALERTS.length === 0) ALERTS.push({ sev:"L", text:"All concentration metrics within governance policy limits" });

  const SEV_STYLE = { H:"bg-red-50 border-red-200 text-red-700", M:"bg-amber-50 border-amber-200 text-amber-700", L:"bg-emerald-50 border-emerald-200 text-emerald-700" };
  const SEV_DOT   = { H:"bg-red-500", M:"bg-amber-400", L:"bg-emerald-400" };

  return (
    <div className="space-y-5" data-testid="section-portfolio-exposure">

      {/* Summary answer strip */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label:"Total Pipeline",  value:`$${(totalAmt/1e6).toFixed(1)}M`, sub:`${active.length} deals`, color:"text-slate-900" },
          { label:"Capital Deployed",value:`$${(deployed/1e6).toFixed(1)}M`, sub:`${totalAmt>0?Math.round(deployed/totalAmt*100):0}% utilisation`, color:"text-teal-600" },
          { label:"Top Sector",      value:Object.entries(sectorMap).sort((a,b)=>b[1]-a[1])[0]?.[0]||"—", sub:`${Math.round((Object.values(sectorMap).sort((a,b)=>b-a)[0]||0)/totalAmt*100)}% of pipeline`, color:"text-slate-900" },
          { label:"Concentration Alerts", value:ALERTS.filter(a=>a.sev!=="L").length, sub:ALERTS.filter(a=>a.sev==="H").length>0?"Action required":"Within policy", color:ALERTS.filter(a=>a.sev==="H").length>0?"text-red-600":"text-emerald-600" },
        ].map(m=>(
          <div key={m.label} className="bg-white border border-slate-200 rounded-xl p-3.5">
            <p className="text-xs text-slate-400 mb-1">{m.label}</p>
            <p className={`text-xl font-bold tabular-nums leading-none ${m.color}`}>{m.value}</p>
            <p className="text-xs text-slate-400 mt-1">{m.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* 1. Sector Exposure */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Sector Exposure (by pipeline value)</p>
          <div className="space-y-2.5">
            {Object.entries(sectorMap).sort((a,b)=>b[1]-a[1]).map(([sec, amt]) => {
              const pct = Math.round(amt/totalAmt*100);
              const alert = pct > govSectorWarn;
              return (
                <div key={sec}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-700 capitalize">{sec}</span>
                    <div className="flex items-center gap-1.5">
                      {alert && <AlertTriangle size={9} className="text-amber-500"/>}
                      <span className={`font-bold tabular-nums ${alert?"text-amber-700":"text-slate-900"}`}>{pct}%</span>
                      <span className="text-slate-400">${(amt/1e6).toFixed(1)}M</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${alert?"bg-amber-400":"bg-teal-500"}`} style={{width:`${pct}%`}}/>
                  </div>
                  {alert && <p className="text-[9px] text-amber-600 mt-0.5">Exceeds {govSectorWarn}% sector threshold</p>}
                </div>
              );
            })}
          </div>
        </div>

        {/* 2. Country / Geography Exposure */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Country / Geography Exposure</p>
          <div className="space-y-2.5">
            {Object.entries(countryMap).sort((a,b)=>b[1]-a[1]).map(([country, amt]) => {
              const pct = Math.round(amt/totalAmt*100);
              const alert = pct > govCountryWarn;
              const COUNTRY_NAMES = { NG:"Nigeria", GH:"Ghana", KE:"Kenya", ZA:"South Africa", ET:"Ethiopia", SN:"Senegal" };
              return (
                <div key={country}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-700">{COUNTRY_NAMES[country]||country}</span>
                    <div className="flex items-center gap-1.5">
                      {alert&&<AlertTriangle size={9} className="text-amber-500"/>}
                      <span className={`font-bold tabular-nums ${alert?"text-amber-700":"text-slate-900"}`}>{pct}%</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${alert?"bg-amber-400":"bg-teal-500"}`} style={{width:`${pct}%`}}/>
                  </div>
                  {alert && <p className="text-[9px] text-amber-600 mt-0.5">Single-country concentration above policy</p>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 3. Single-Name Concentration */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Largest Single-Name Exposures</p>
        <div className="space-y-2">
          {sortedByAmt.slice(0,5).map((deal, i) => {
            const pct = Math.round((deal.deployed_amount_usd||0)/totalAmt*100);
            const alert = pct > govSingleDeal;
            return (
              <div key={deal.deal_id} className="flex items-center gap-3">
                <span className="text-[10px] text-slate-400 font-mono w-5">{i+1}.</span>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center mb-0.5">
                    <span className="text-xs font-medium text-slate-800 truncate">{_parseName(deal)}</span>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      {alert&&<AlertTriangle size={9} className="text-amber-500"/>}
                      <span className={`text-xs font-bold tabular-nums ${alert?"text-amber-700":"text-slate-700"}`}>{pct}%</span>
                      <span className="text-xs text-slate-400">${(deal.deployed_amount_usd||0)/1e6 < 0.1 ? "—" : `${((deal.deployed_amount_usd||0)/1e6).toFixed(1)}M`}</span>
                    </div>
                  </div>
                  <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${alert?"bg-amber-400":i===0?"bg-slate-600":"bg-teal-500"}`} style={{width:`${Math.min(pct,100)}%`}}/>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* 4. Top deals driving financed emissions */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Top Deals — Financed Emissions</p>
          <div className="space-y-2">
            {dealsWithEmissions.slice(0,5).map((item, i) => (
              <div key={item.deal.deal_id} className="flex items-center justify-between gap-2 text-xs">
                <span className="text-slate-400 text-[10px] font-mono w-4">{i+1}.</span>
                <span className="flex-1 truncate text-slate-700">{item.name.split(" ").slice(0,3).join(" ")}</span>
                <span className="font-bold tabular-nums text-slate-900 flex-shrink-0">{item.financed_tco2e.toFixed(0)}t</span>
              </div>
            ))}
          </div>
          <p className="text-[9px] text-slate-400 italic mt-2">Estimated financed emissions — proportional to pipeline share</p>
        </div>

        {/* 5. Top deals driving emissions reduction */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Top Deals — Emissions Reduction</p>
          <div className="space-y-2">
            {[...dealsWithEmissions].sort((a,b)=>b.reduction_tco2e-a.reduction_tco2e).slice(0,5).map((item, i) => (
              <div key={item.deal.deal_id} className="flex items-center justify-between gap-2 text-xs">
                <span className="text-slate-400 text-[10px] font-mono w-4">{i+1}.</span>
                <span className="flex-1 truncate text-slate-700">{item.name.split(" ").slice(0,3).join(" ")}</span>
                <span className="font-bold tabular-nums text-teal-600 flex-shrink-0">{item.reduction_tco2e.toFixed(0)}t/yr</span>
              </div>
            ))}
          </div>
          <p className="text-[9px] text-slate-400 italic mt-2">Estimated annual reduction contribution — indicative, pre-verification</p>
        </div>
      </div>

      {/* 6. Concentration Alerts with explicit interpretation */}
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Concentration Alerts & Portfolio Assessment</p>
        <div className="space-y-2">
          {ALERTS.map((a, i) => (
            <div key={i} className={`flex items-start gap-2.5 border rounded-xl px-4 py-3 ${SEV_STYLE[a.sev]}`}>
              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1 ${SEV_DOT[a.sev]}`}/>
              <p className="text-xs">{a.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── DEAL ACTIONS — Stage-Aware Workflow Engine ──────────────────────────────
// Replaces Decision Panel. Enforces lifecycle integrity: only valid actions
// per stage are shown. No cross-stage decisions. No premature credit approvals.
function SectionDealActions() {
  const [deals,    setDeals]    = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [acting,   setActing]   = useState(false);
  const [action,   setAction]   = useState(null);  // selected action
  const [reason,   setReason]   = useState("");
  const [done,     setDone]     = useState(null);  // result
  const [compliance, setCompliance]   = useState(null);
  const [loadingComp, setLoadingComp] = useState(false);

  const load = () => {
    setLoading(true);
    client.get("/fi/decisions/deals").then(r => {
      const ds = r.data.deals || [];
      setDeals(ds);
      if (ds.length > 0 && !selected) setSelected(ds[0]);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Load compliance gate when deal changes
  useEffect(() => {
    if (!selected?.deal_id) return;
    setLoadingComp(true);
    setCompliance(null);
    client.get(`/fi/compliance/assessment/${selected.deal_id}`)
      .then(r => setCompliance(r.data))
      .catch(() => setCompliance(null))
      .finally(() => setLoadingComp(false));
  }, [selected?.deal_id]);

  // ── Stage-aware action config (lifecycle integrity enforced) ──────────────
  const STAGE_ACTIONS = {
    screening: {
      group: "Screening",
      desc: "Initial deal review. Does this deal meet minimum criteria for FI engagement?",
      color: "border-slate-300",
      bg: "bg-slate-50",
      actions: [
        { key: "advance_to_data_request", label: "Proceed to Eligibility",  variant: "green",  desc: "Move to eligibility assessment stage", needsReason: false },
        { key: "request_more_info",       label: "Request More Data",        variant: "amber",  desc: "Request documentation before proceeding", needsReason: true,  reasonLabel: "Specify what data is required from the counterparty" },
        { key: "not_suitable",            label: "Mark as Not Suitable",     variant: "red",    desc: "Deal does not meet minimum criteria for further engagement", needsReason: true,  reasonLabel: "Why does this deal not meet minimum criteria?" },
      ],
    },
    data_request: {
      group: "Eligibility",
      desc: "Data collection and eligibility check. Does the deal pass the 4-gate eligibility framework?",
      color: "border-blue-300",
      bg: "bg-blue-50",
      actions: [
        { key: "advance_to_due_diligence", label: "Mark Eligible — Proceed to Due Diligence", variant: "green", desc: "Data received, eligibility confirmed, move to full DD", needsReason: false },
        { key: "request_more_info",        label: "Request More Data",                        variant: "amber", desc: "Specific gaps identified — request from counterparty", needsReason: true,  reasonLabel: "What specific data or evidence is still required?" },
        { key: "not_eligible",             label: "Not Eligible",                             variant: "red",   desc: "Deal fails eligibility criteria — cannot proceed", needsReason: true,  reasonLabel: "Specific eligibility blockers (Gate references)" },
      ],
    },
    due_diligence: {
      group: "Due Diligence",
      desc: "Full due diligence. Validate financial and climate credentials before submitting to committee.",
      color: "border-amber-300",
      bg: "bg-amber-50",
      actions: [
        { key: "approve_for_committee", label: "Submit to Credit Committee", variant: "green", desc: "DD complete — deal is ready for credit committee deliberation", needsReason: false },
        { key: "request_info",          label: "Request Additional Info",    variant: "amber", desc: "Specific gaps must be resolved before committee submission", needsReason: true,  reasonLabel: "What information is required before committee?" },
        { key: "not_eligible",          label: "Not Eligible — Reject at DD",variant: "red",   desc: "DD findings reveal eligibility issues — cannot proceed to committee", needsReason: true, reasonLabel: "DD findings that block eligibility" },
      ],
    },
    credit_committee: {
      group: "Credit Committee",
      desc: "Final credit decision. Committee has reviewed the full pack, financial analysis, and risk assessment.",
      color: "border-orange-400",
      bg: "bg-orange-50",
      actions: [
        { key: "issue_term_sheet",         label: "Approve — Issue Term Sheet",   variant: "green", desc: "Committee approves — proceed to term sheet negotiation", needsReason: false },
        { key: "defer_to_next_committee",  label: "Conditional Approval — Defer", variant: "amber", desc: "Conditions apply — defer pending resolution", needsReason: true,  reasonLabel: "Conditions that must be resolved before next committee" },
        { key: "decline",                  label: "Decline",                      variant: "red",   desc: "Credit committee decision: deal is declined. This is a formal audit record.", needsReason: true, reasonLabel: "Credit committee decline rationale (permanent audit record)" },
      ],
    },
    term_sheet: {
      group: "Term Sheet",
      desc: "Commercial terms under negotiation. Execute term sheet to move to financial close.",
      color: "border-teal-400",
      bg: "bg-teal-50",
      actions: [
        { key: "mark_financial_close", label: "Mark Financial Close", variant: "green", desc: "Term sheet signed and executed — facility is closed", needsReason: false },
        { key: "amend_terms",          label: "Amend Terms",          variant: "amber", desc: "Terms require adjustment before execution", needsReason: true, reasonLabel: "What changes are required to the terms?" },
      ],
    },
    financial_close: {
      group: "Financial Close",
      desc: "Deal is closed. Monitor covenant compliance and deployed capital.",
      color: "border-emerald-400",
      bg: "bg-emerald-50",
      actions: [
        { key: "mark_deployed", label: "Update Deployed Capital", variant: "green", desc: "Record capital disbursement event", needsReason: false },
      ],
    },
    rejected: {
      group: "Closed — Not Proceeding",
      desc: "This deal has been closed as Not Suitable, Not Eligible, or Declined. No further actions available.",
      color: "border-red-300",
      bg: "bg-red-50",
      actions: [],
    },
  };

  // Status display labels (institutional terminology)
  const STAGE_DISPLAY = {
    screening:        "Screening",
    data_request:     "Eligibility Review",
    due_diligence:    "Due Diligence",
    credit_committee: "Credit Committee",
    term_sheet:       "Term Sheet",
    financial_close:  "Financial Close",
    rejected:         "Closed",
  };

  // Rejection label from audit history
  const getRejectionLabel = (deal) => {
    const history = deal.stage_history || deal.stage_history_json || [];
    const lastAction = [...history].reverse().find(h => ["not_suitable","not_eligible","decline","reject"].includes(h.action));
    if (!lastAction) return "Closed";
    const labels = { not_suitable:"Not Suitable", not_eligible:"Not Eligible", decline:"Declined", reject:"Rejected" };
    return labels[lastAction.action] || "Closed";
  };

  const perform = async () => {
    if (!selected || !action) return;
    setActing(true);
    try {
      await client.post(`/fi/decisions/deals/${selected.deal_id}/action`, {
        action: action.key,
        reason: reason || undefined,
        notes: reason || undefined,
      });
      toast.success(`${action.label} — ${selected.deal_id} updated.`);
      setDone({ action: action.label, deal: selected.deal_id });
      setAction(null); setReason("");
      load(); // Refresh deals
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed.");
    }
    setActing(false);
  };

  const VARIANT_STYLE = {
    green: "bg-emerald-600 hover:bg-emerald-700 text-white",
    amber: "bg-amber-500 hover:bg-amber-600 text-white",
    red:   "bg-red-600 hover:bg-red-700 text-white",
  };
  const VARIANT_BORDER = {
    green: "border-emerald-200 bg-emerald-50",
    amber: "border-amber-200 bg-amber-50",
    red:   "border-red-200 bg-red-50",
  };

  const stageConfig = selected ? (STAGE_ACTIONS[selected.status] || STAGE_ACTIONS.rejected) : null;

  if (loading) return <div className="flex items-center justify-center py-10"><RefreshCw size={14} className="animate-spin text-teal-500"/></div>;

  return (
    <SectionFrame icon={CheckCircle2} title="Deal Actions" desc="Stage-aware decision panel. Shows readiness summary before presenting available actions.">

      {/* Linear flow context */}
      <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl mb-4">
        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex-shrink-0">Credit Engine:</p>
        {[["Financial Structuring","text-slate-500 bg-slate-50 border-slate-200"],["Due Diligence","text-slate-500 bg-slate-50 border-slate-200"],["Deal Actions","text-teal-700 bg-teal-50 border-teal-200"],["Credit Memo","text-slate-500 bg-slate-50 border-slate-200"]].map(([s,c],i)=>(
          <React.Fragment key={s}>
            {i>0&&<ArrowRight size={9} className="text-slate-300 flex-shrink-0"/>}
            <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold whitespace-nowrap ${c} ${i===2?"ring-1 ring-teal-400":""}`}>{s}</span>
          </React.Fragment>
        ))}
      </div>

      {/* Deal selector */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1">
          <label className="block text-xs font-medium text-slate-600 mb-1">Select Deal</label>
          <select value={selected?.deal_id||""}
            onChange={e => { setSelected(deals.find(d=>d.deal_id===e.target.value)||null); setAction(null); setReason(""); setDone(null); }}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none"
            data-testid="deal-actions-select">
            {deals.map(d => (
              <option key={d.deal_id} value={d.deal_id}>
                {d.deal_id} — {d.stage_label || STAGE_DISPLAY[d.status] || d.status}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selected && stageConfig && (
        <div className="space-y-4">

          {/* ── 1. Current Stage ── */}
          <div className={`rounded-xl border p-4 ${stageConfig.bg} ${stageConfig.color}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Current Stage</p>
                <p className="text-base font-bold text-slate-900">
                  {selected.status === "rejected" ? getRejectionLabel(selected) : stageConfig.group}
                </p>
                <p className="text-xs text-slate-500 mt-1">{stageConfig.desc}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-[10px] text-slate-400">Deal</p>
                <p className="text-xs font-mono font-bold text-slate-700">{selected.deal_id}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{_parseName(selected).split(" ").slice(0,3).join(" ")}</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          {stageConfig.actions.length === 0 ? (
            <div className="border border-dashed border-slate-200 rounded-xl p-6 text-center">
              <p className="text-sm font-semibold text-slate-500">No further actions available</p>
              <p className="text-xs text-slate-400 mt-1">This deal is in a terminal state. View the audit trail for the decision record.</p>
            </div>
          ) : (
            <>
              {/* ── 2. Readiness Summary ── */}
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Readiness Summary</p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                  {[
                    {
                      label:"Eligibility",
                      value:_parseElig(selected)==="eligible"?"Cleared":_parseElig(selected)==="conditional"?"Conditional":_parseElig(selected)==="blocked"?"Blocked":"Not Assessed",
                      color:_parseElig(selected)==="eligible"?"text-emerald-700 bg-emerald-50 border-emerald-200":_parseElig(selected)==="conditional"?"text-amber-700 bg-amber-50 border-amber-200":_parseElig(selected)==="blocked"?"text-red-700 bg-red-50 border-red-200":"text-slate-500 bg-slate-50 border-slate-200",
                    },
                    {
                      label:"Signal Score",
                      value:_parseScore(selected)?`${_parseScore(selected)}/100`:"Not computed",
                      color:_parseScore(selected)?_parseScore(selected)>=65?"text-emerald-700 bg-emerald-50 border-emerald-200":_parseScore(selected)>=45?"text-amber-700 bg-amber-50 border-amber-200":"text-red-700 bg-red-50 border-red-200":"text-slate-500 bg-slate-50 border-slate-200",
                    },
                    {
                      label:"Due Diligence",
                      value:(selected.evidence_readiness_pct||0)>=70?"Conditional":(selected.evidence_readiness_pct||0)>=40?"Partial":"Not Started",
                      color:(selected.evidence_readiness_pct||0)>=70?"text-amber-700 bg-amber-50 border-amber-200":"text-slate-500 bg-slate-50 border-slate-200",
                    },
                    {
                      label:"Conditions",
                      value:selected.stage_history?.some(h=>h.action==="defer_to_next_committee")?"Conditions set":"Pending",
                      color:"text-slate-500 bg-slate-50 border-slate-200",
                    },
                    {
                      label:"Compliance Gate",
                      value: loadingComp ? "Checking..." : compliance ? (
                        compliance.compliance_gate === "blocked" ? "BLOCKED" :
                        compliance.compliance_gate === "conditional" ? "Conditional" : "Clear"
                      ) : "—",
                      color: loadingComp ? "text-slate-500 bg-slate-50 border-slate-200" :
                        compliance?.compliance_gate === "blocked" ? "text-red-700 bg-red-50 border-red-200" :
                        compliance?.compliance_gate === "conditional" ? "text-amber-700 bg-amber-50 border-amber-200" :
                        compliance?.compliance_gate === "clear" ? "text-emerald-700 bg-emerald-50 border-emerald-200" :
                        "text-slate-500 bg-slate-50 border-slate-200",
                    },
                  ].map(item=>(
                    <div key={item.label} className={`rounded-lg border px-3 py-2 ${item.color}`}>
                      <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-0.5">{item.label}</p>
                      <p className="text-xs font-bold">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── 2b. Compliance Panel ── */}
              {compliance && !loadingComp && (
                <div className={`rounded-xl border-2 p-4 ${
                  compliance.compliance_gate === "blocked" ? "border-red-300 bg-red-50" :
                  compliance.compliance_gate === "conditional" ? "border-amber-300 bg-amber-50" :
                  "border-emerald-300 bg-emerald-50"
                }`} data-testid="compliance-gate-panel">

                  {/* Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                        compliance.compliance_gate === "blocked" ? "bg-red-500" :
                        compliance.compliance_gate === "conditional" ? "bg-amber-500" : "bg-emerald-500"
                      }`}/>
                      <p className={`text-xs font-bold uppercase tracking-wider ${
                        compliance.compliance_gate === "blocked" ? "text-red-800" :
                        compliance.compliance_gate === "conditional" ? "text-amber-800" : "text-emerald-800"
                      }`}>{compliance.gate_decision}</p>
                    </div>
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${
                      compliance.risk_level === "High" ? "bg-red-100 text-red-700" :
                      compliance.risk_level === "Medium" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
                    }`}>{compliance.risk_level} Risk</span>
                  </div>

                  {/* Framework quick-status table */}
                  <div className="bg-white rounded-lg border border-white/60 overflow-hidden mb-3">
                    <table className="w-full text-[10px]">
                      <thead><tr className="bg-slate-50 border-b border-slate-100">
                        <th className="text-left px-3 py-1.5 text-slate-500 font-bold text-[9px] uppercase tracking-wider">Framework</th>
                        <th className="text-center px-2 py-1.5 text-slate-500 font-bold text-[9px] uppercase tracking-wider">Status</th>
                        <th className="text-right px-3 py-1.5 text-slate-500 font-bold text-[9px] uppercase tracking-wider">Score</th>
                        <th className="text-left px-3 py-1.5 text-slate-500 font-bold text-[9px] uppercase tracking-wider">Detail</th>
                      </tr></thead>
                      <tbody>
                        {Object.values(compliance.assessments).map(a => (
                          <tr key={a.label} className="border-b border-slate-50 last:border-0">
                            <td className="px-3 py-1.5 font-medium text-slate-700">{a.label}</td>
                            <td className="px-2 py-1.5 text-center">
                              <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${
                                a.status === "compliant" ? "bg-emerald-100 text-emerald-700" :
                                a.status === "conditional" ? "bg-amber-100 text-amber-700" :
                                "bg-red-100 text-red-700"
                              }`}>{a.status === "not_applicable" ? "N/A" : a.risk_category ? `Cat ${a.risk_category}` : a.status?.replace("_"," ").split(" ").map(w=>w[0].toUpperCase()+w.slice(1)).join(" ")}</span>
                            </td>
                            <td className="px-3 py-1.5 text-right font-bold text-slate-900">{a.score_pct?.toFixed(0)}%</td>
                            <td className="px-3 py-1.5 text-slate-400">{a.short}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Evidence score breakdown */}
                  <div className="bg-white rounded-lg border border-white/60 p-3 mb-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">Evidence Score</p>
                      <span className={`text-xs font-bold ${
                        compliance.evidence_score.tier === "clear" ? "text-emerald-700" :
                        compliance.evidence_score.tier === "conditional" ? "text-amber-700" : "text-red-700"
                      }`}>{compliance.evidence_score.total.toFixed(0)}/100 — {compliance.evidence_score.tier_label}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.entries(compliance.evidence_score.components).map(([k, c]) => (
                        <div key={k}>
                          <div className="flex justify-between text-[9px] mb-0.5">
                            <span className="text-slate-400 capitalize">{k.replace(/_/g," ")}</span>
                            <span className="font-bold text-slate-600">{c.pts}/{c.max}</span>
                          </div>
                          <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${c.pct >= 70 ? "bg-emerald-500" : c.pct >= 40 ? "bg-amber-500" : "bg-red-400"}`} style={{width:`${c.pct}%`}}/>
                          </div>
                          <p className="text-[8.5px] text-slate-400 mt-0.5 truncate">{c.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Blocking rules */}
                  {compliance.blocking_rules.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[9px] font-bold text-red-700 uppercase tracking-wider mb-1.5">
                        {compliance.blocking_rules.length} Blocking Rule{compliance.blocking_rules.length > 1 ? "s" : ""} — Must Resolve Before Committee
                      </p>
                      {compliance.blocking_rules.map((b, i) => (
                        <div key={b.id} className="bg-red-50 border border-red-200 rounded-lg p-2.5 mb-1.5">
                          <p className="text-[9px] font-bold text-red-700 mb-0.5">{b.framework}</p>
                          <p className="text-[10px] text-red-800">{b.message}</p>
                          <p className="text-[9px] text-red-600 mt-1"><span className="font-bold">Required: </span>{b.action_required}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Warnings */}
                  {compliance.warnings.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[9px] font-bold text-amber-700 uppercase tracking-wider mb-1.5">{compliance.warnings.length} Warning{compliance.warnings.length > 1 ? "s" : ""}</p>
                      {compliance.warnings.slice(0, 3).map(w => (
                        <div key={w.id} className="flex items-start gap-2 text-[10px] text-amber-800 py-1 border-b border-amber-100 last:border-0">
                          <span className="text-amber-500 flex-shrink-0 mt-0.5">—</span>
                          <span>{w.message}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Required actions */}
                  {compliance.required_actions.length > 0 && (
                    <div>
                      <p className="text-[9px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">Required Actions</p>
                      {compliance.required_actions.slice(0, 5).map((a, i) => (
                        <div key={i} className="flex items-start gap-2 py-1 border-b border-slate-100 last:border-0">
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5 ${a.priority === "IMMEDIATE" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{a.priority}</span>
                          <div className="text-[10px] text-slate-700">
                            <p>{a.action}</p>
                            {a.evidence_needed && <p className="text-[9px] text-slate-400 mt-0.5">{a.evidence_needed.split(".")[0]}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── 3. Available Actions ── */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Available Actions</p>
                <div className="space-y-2">
                  {stageConfig.actions.map(a => {
                    const isCommitteeAction = a.key === "approve_for_committee";
                    const isGateBlocked = compliance?.compliance_gate === "blocked" && isCommitteeAction;

                    if (isGateBlocked) {
                      return (
                        <div key={a.key} className="border-2 border-red-300 bg-red-50 rounded-xl px-4 py-3"
                          data-testid={`deal-action-${a.key}-blocked`}>
                          <div className="flex items-center gap-2 mb-1">
                            <AlertTriangle size={13} className="text-red-500 flex-shrink-0"/>
                            <p className="text-sm font-bold text-red-700">{a.label} — COMPLIANCE GATE BLOCKED</p>
                          </div>
                          <p className="text-xs text-red-600 mb-2">
                            {compliance.blocking_rules.length} blocking rule{compliance.blocking_rules.length > 1 ? "s" : ""} must be resolved before this deal can be submitted to Credit Committee.
                          </p>
                          <button
                            onClick={async () => {
                              // Log the blocked attempt to audit trail
                              try {
                                await client.post(`/fi/compliance/log-attempt/${selected.deal_id}`, {
                                  attempted_action: a.key,
                                  gate_status: "blocked",
                                  blocking_rule_ids: compliance.blocking_rules.map(b => b.id),
                                });
                              } catch (err) { /* silent */ }
                              toast.error("Attempt logged. Resolve compliance blocking rules before committee submission.", { duration: 5000 });
                            }}
                            className="text-[10px] font-bold text-red-600 border border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-100"
                            data-testid={`deal-action-${a.key}-log-attempt`}>
                            Log Attempt to Audit Trail
                          </button>
                        </div>
                      );
                    }

                    return (
                      <button key={a.key}
                        onClick={() => { setAction(a.key === action?.key ? null : a); setReason(""); }}
                        className={`w-full flex items-start gap-3 px-4 py-3 rounded-xl border text-left transition-all ${
                          action?.key === a.key ? VARIANT_BORDER[a.variant] + " border-2" : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                        data-testid={`deal-action-${a.key}`}>
                        <div className={`w-3 h-3 rounded-full mt-0.5 flex-shrink-0 ${
                          a.variant==="green"?"bg-emerald-500":a.variant==="amber"?"bg-amber-500":"bg-red-500"
                        }`}/>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-slate-900">{a.label}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{a.desc}</p>
                        </div>
                        {action?.key === a.key && <Check size={14} className="text-teal-500 flex-shrink-0 mt-0.5"/>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Reason input (when required) */}
              {action && (
                <div className="space-y-3">
                  {action.needsReason && (
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        {action.reasonLabel} <span className="text-red-500">*</span>
                      </label>
                      <textarea value={reason} onChange={e=>setReason(e.target.value)} rows={3}
                        placeholder="This will be recorded in the immutable audit trail..."
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 resize-none"
                        data-testid="action-reason"/>
                    </div>
                  )}

                  {/* Confirmation banner for destructive actions */}
                  {action.variant === "red" && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                      <p className="text-xs font-bold text-red-700 mb-1">This action is permanent and auditable</p>
                      <p className="text-[10px] text-red-600">Once confirmed, this decision is recorded in the immutable audit trail and cannot be removed. The reason will be visible to all authorised users.</p>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <button onClick={perform} disabled={acting || (action.needsReason && !reason.trim())}
                      className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold rounded-xl disabled:opacity-40 transition-colors ${VARIANT_STYLE[action.variant]}`}
                      data-testid="confirm-action">
                      {acting ? <RefreshCw size={13} className="animate-spin"/> : <Check size={13}/>}
                      {acting ? "Processing..." : `Confirm: ${action.label}`}
                    </button>
                    <button onClick={() => { setAction(null); setReason(""); }}
                      className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
                      <X size={12}/> Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Success confirmation */}
              {done && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0"/>
                  <p className="text-xs text-emerald-700"><strong>{done.action}</strong> recorded for {done.deal}. Audit trail updated.</p>
                </div>
              )}
            </>
          )}

          {/* Audit trail preview */}
          {selected.stage_history && selected.stage_history.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Recent History</p>
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                {[...selected.stage_history].reverse().slice(0,3).map((h, i) => (
                  <div key={i} className={`flex items-start gap-3 px-4 py-2.5 text-xs ${i<2?"border-b border-slate-100":""}`}>
                    <span className="text-slate-400 text-[10px] font-mono flex-shrink-0 w-28 mt-0.5">{(h.timestamp||"").slice(0,16).replace("T"," ")}</span>
                    <span className="font-semibold text-slate-700 flex-shrink-0">{h.action_label || h.action}</span>
                    <span className="text-slate-500 truncate">{h.reason||h.notes||""}</span>
                    <span className="text-slate-400 text-[10px] flex-shrink-0">{h.performed_by?.split("@")[0]||""}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </SectionFrame>
  );
}

// ── CONDITIONS TRACKER ─────────────────────────────────────────────────────
function SectionConditionsTracker() {
  const [deals, setDeals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [conditions, setConditions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [updateForm, setUpdateForm] = useState({ condId: null, status: "fulfilled", note: "" });

  useEffect(() => {
    client.get("/fi/decisions/deals").then(r => {
      const active = (r.data.deals || []).filter(d => d.status !== "rejected");
      setDeals(active);
      if (active.length > 0) loadConditions(active[0].deal_id);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const loadConditions = (dealId) => {
    setSelected(dealId);
    setConditions(null);
    client.get(`/fi/conditions/deals/${dealId}/conditions`)
      .then(r => setConditions(r.data))
      .catch(() => {});
  };

  const initConditions = async (dealId, hasCarbon) => {
    try {
      const r = await client.post(`/fi/conditions/deals/${dealId}/initialize?has_carbon=${hasCarbon}`);
      setConditions(r.data);
      toast.success(`${r.data.summary?.total} conditions initialized`);
    } catch (e) {
      toast.error("Failed to initialize conditions.");
    }
  };

  const updateCondition = async () => {
    if (!updateForm.note) { toast.error("Note required."); return; }
    setUpdating(true);
    try {
      await client.post(`/fi/conditions/deals/${selected}/update-condition`, {
        condition_id: updateForm.condId,
        new_status: updateForm.status,
        note: updateForm.note,
      });
      toast.success("Condition updated.");
      setUpdateForm({ condId: null, status: "fulfilled", note: "" });
      loadConditions(selected);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed.");
    }
    setUpdating(false);
  };

  const STATUS_COLORS = {
    pending: "bg-slate-100 text-slate-600",
    fulfilled: "bg-emerald-100 text-emerald-700",
    breached: "bg-red-100 text-red-700",
    waived: "bg-slate-100 text-slate-400",
  };

  const PRIORITY_COLORS = { high: "text-red-500", medium: "text-amber-500", low: "text-slate-400" };
  const CATEGORY_LABELS = { pre_disbursement: "Pre-Disbursement", post_disbursement: "Post-Disbursement", ongoing: "Ongoing", carbon: "Carbon" };

  if (loading) return <div className="flex items-center justify-center py-12"><RefreshCw size={16} className="animate-spin text-teal-500" /></div>;

  return (
    <SectionFrame icon={CheckCircle2} title="Conditions Tracker" desc="Operational conditions management. Structured pre/post-disbursement tracking with status lifecycle.">
      {/* Deal selector */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {deals.map(d => (
          <button key={d.deal_id} onClick={() => loadConditions(d.deal_id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-xl border transition-colors ${
              selected === d.deal_id ? "bg-slate-900 text-white border-transparent" : "border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}>
            {d.deal_id} — {d.stage_label}
          </button>
        ))}
      </div>

      {conditions ? (
        <div className="space-y-4">
          {/* Summary */}
          <div className={`rounded-xl border px-5 py-4 ${
            conditions.disbursement_clearance?.status === "blocked" ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"
          }`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-slate-900">{conditions.disbursement_clearance?.label}</p>
              <span className="text-xs font-semibold text-slate-700">{conditions.summary?.completion_pct}% complete</span>
            </div>
            <div className="grid grid-cols-4 gap-3 mt-3">
              {["total","pending","fulfilled","breached"].map(k => (
                <div key={k} className="text-center">
                  <p className="text-lg font-bold tabular-nums text-slate-900">{conditions.summary?.[k]}</p>
                  <p className="text-xs text-slate-400 capitalize">{k}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Conditions by category */}
          {Object.entries(conditions.grouped || {}).map(([cat, items]) => items.length > 0 && (
            <SectionCard key={cat} title={CATEGORY_LABELS[cat] || cat} noPadding>
              {items.map(c => (
                <div key={c.id} className="px-5 py-3 border-b border-slate-50 last:border-0">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[c.status] || ""}`}>
                          {c.status}
                        </span>
                        <span className={`text-xs ${PRIORITY_COLORS[c.priority] || ""}`}>{c.priority?.toUpperCase()}</span>
                      </div>
                      <p className="text-xs font-medium text-slate-900">{c.description}</p>
                      <p className="text-xs text-slate-400 mt-0.5">Evidence: {c.evidence_required}</p>
                      {c.fulfillment_note && <p className="text-xs text-emerald-600 mt-0.5">✓ {c.fulfillment_note}</p>}
                      {c.breach_note && <p className="text-xs text-red-600 mt-0.5">⚠ {c.breach_note}</p>}
                    </div>
                    {c.status === "pending" && (
                      <button onClick={() => setUpdateForm({ condId: c.id, status: "fulfilled", note: "" })}
                        className="flex-shrink-0 text-xs text-teal-600 hover:underline font-medium">
                        Update
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </SectionCard>
          ))}

          {/* Update form */}
          {updateForm.condId && (
            <SectionCard title="Update Condition">
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">New Status</label>
                  <select value={updateForm.status} onChange={e => setUpdateForm({...updateForm, status: e.target.value})}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl">
                    {["fulfilled","waived","breached"].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Note / Evidence</label>
                  <input value={updateForm.note} onChange={e => setUpdateForm({...updateForm, note: e.target.value})}
                    placeholder="Describe evidence received..."
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl" />
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={updateCondition} disabled={updating}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-xs font-medium rounded-xl hover:bg-slate-800">
                  {updating ? <RefreshCw size={11} className="animate-spin" /> : null}
                  Save Update
                </button>
                <button onClick={() => setUpdateForm({ condId: null, status: "fulfilled", note: "" })}
                  className="px-4 py-2 border border-slate-200 text-slate-500 text-xs font-medium rounded-xl hover:bg-slate-50">
                  Cancel
                </button>
              </div>
            </SectionCard>
          )}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-sm text-slate-500 mb-3">Conditions not yet initialized for this deal</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => initConditions(selected, false)}
              className="px-4 py-2 bg-slate-900 text-white text-xs font-medium rounded-xl hover:bg-slate-800">
              Initialize (no carbon)
            </button>
            <button onClick={() => initConditions(selected, true)}
              className="px-4 py-2 border border-teal-300 text-teal-700 text-xs font-medium rounded-xl hover:bg-teal-50">
              Initialize (with carbon)
            </button>
          </div>
        </div>
      )}
    </SectionFrame>
  );
}

// ── DATA IMPORT SECTION ────────────────────────────────────────────────────
// ── CREDIT ENGINE: Due Diligence ──────────────────────────────────────────
// Structured checklist replacing the CSV-centric data import UI.
// Shows sections: Financials, Climate/MRV, Legal/KYC, Technical.
// Each section shows status + key issues + completion.
function SectionDataImport() {
  const [deals,    setDeals]    = useState([]);
  const [selected, setSelected] = useState(null);
  const [sections, setSections] = useState({
    financials: { complete:[], missing:[], flagged:[] },
    climate:    { complete:[], missing:[], flagged:[] },
    legal:      { complete:[], missing:[], flagged:[] },
    technical:  { complete:[], missing:[], flagged:[] },
  });
  const [loading, setLoading]   = useState(true);

  const DD_SECTIONS = {
    financials: {
      label:"Financials",
      items:[
        "3-year audited financial statements",
        "Revenue and EBITDA projections (5yr)",
        "Debt service coverage model (DSCR)",
        "Operating cost breakdown",
        "Existing indebtedness schedule",
      ],
    },
    climate: {
      label:"Climate & MRV",
      items:[
        "GHG emissions baseline (certified)",
        "Reduction methodology and plan",
        "MRV monitoring framework",
        "Third-party verification engagement",
        "Energy audit or technical assessment",
      ],
    },
    legal: {
      label:"Legal & KYC",
      items:[
        "Certificate of incorporation (certified)",
        "Directors and beneficial ownership register",
        "Regulatory approvals and permits",
        "Environmental and social impact assessment",
        "Board resolution authorising transaction",
      ],
    },
    technical: {
      label:"Technical",
      items:[
        "Site inspection report or feasibility study",
        "Technology specification / EPC terms",
        "Offtake agreement or demand confirmation",
        "Operations and maintenance plan",
        "Grid/infrastructure connection confirmation",
      ],
    },
  };

  // Derive DD status from section completion
  const [checked, setChecked] = useState({});

  useEffect(() => {
    client.get("/fi/decisions/deals")
      .then(r => {
        const ds = (r.data.deals||[]).filter(d=>!["rejected"].includes(d.status));
        setDeals(ds);
        if (ds.length > 0) setSelected(ds[0]);
      }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const toggleItem = (section, item) => {
    const key = `${section}:${item}`;
    setChecked(c => ({...c, [key]: !c[key]}));
  };
  const isChecked = (section, item) => !!checked[`${section}:${item}`];

  // Compute section status
  const getSectionStatus = (sectionKey) => {
    const items = DD_SECTIONS[sectionKey].items;
    const doneCount = items.filter(i => isChecked(sectionKey, i)).length;
    if (doneCount === items.length) return { label:"Complete",   color:"text-emerald-700", bg:"bg-emerald-100 border-emerald-300", pct:100 };
    if (doneCount >= Math.floor(items.length/2)) return { label:"Conditional", color:"text-amber-700",   bg:"bg-amber-100 border-amber-300",   pct: Math.round(doneCount/items.length*100) };
    return { label:"Missing",     color:"text-red-700",     bg:"bg-red-100 border-red-200",       pct: Math.round(doneCount/items.length*100) };
  };

  const allSectionStatuses = Object.keys(DD_SECTIONS).map(k => getSectionStatus(k));
  const overallComplete = allSectionStatuses.filter(s=>s.label==="Complete").length;
  const overallPct = Math.round(
    Object.keys(DD_SECTIONS).reduce((sum, k) => {
      const items = DD_SECTIONS[k].items;
      return sum + items.filter(i=>isChecked(k,i)).length;
    }, 0) / Object.keys(DD_SECTIONS).reduce((s,k)=>s+DD_SECTIONS[k].items.length,0) * 100
  );

  const getDDStatus = () => {
    if (overallComplete === 4) return { label:"Ready for Committee",  color:"text-emerald-700", bg:"bg-emerald-50 border-emerald-300" };
    if (overallPct >= 60)      return { label:"Conditional",          color:"text-amber-700",   bg:"bg-amber-50 border-amber-300"   };
    return                             { label:"Not Ready",            color:"text-red-700",     bg:"bg-red-50 border-red-200"       };
  };
  const ddStatus = getDDStatus();

  return (
    <div className="space-y-4" data-testid="section-data-import">
      {/* Linear flow context */}
      <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl">
        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex-shrink-0">Credit Engine:</p>
        {[["Financial Structuring","text-slate-500 bg-slate-50 border-slate-200"],["Due Diligence","text-teal-700 bg-teal-50 border-teal-200"],["Deal Actions","text-slate-500 bg-slate-50 border-slate-200"],["Credit Memo","text-slate-500 bg-slate-50 border-slate-200"]].map(([s,c],i)=>(
          <React.Fragment key={s}>
            {i>0&&<ArrowRight size={9} className="text-slate-300 flex-shrink-0"/>}
            <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold whitespace-nowrap ${c} ${i===1?"ring-1 ring-teal-400":""}`}>{s}</span>
          </React.Fragment>
        ))}
      </div>

      {/* Deal selector */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Deal</label>
          <select value={selected?.deal_id||""} onChange={e=>setSelected(deals.find(d=>d.deal_id===e.target.value)||null)}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none">
            <option value="">— Select deal —</option>
            {deals.map(d=><option key={d.deal_id} value={d.deal_id}>{d.deal_id} — {_parseName(d)}</option>)}
          </select>
        </div>
        {selected && (
          <div className={`mt-4 px-3 py-1.5 rounded-xl border-2 ${ddStatus.bg}`}>
            <p className="text-[9px] text-slate-400 uppercase tracking-wider">DD Status</p>
            <p className={`text-sm font-bold ${ddStatus.color}`}>{ddStatus.label}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">{overallPct}% complete</p>
          </div>
        )}
      </div>

      {selected && (
        <div className="space-y-3">
          {/* 4 structured DD sections */}
          {Object.entries(DD_SECTIONS).map(([sKey, sConf]) => {
            const status = getSectionStatus(sKey);
            const items = sConf.items;
            const doneCount = items.filter(i=>isChecked(sKey,i)).length;
            return (
              <div key={sKey} className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-bold text-slate-900">{sConf.label}</p>
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded border ${status.bg} ${status.color}`}>
                      {status.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${status.pct===100?"bg-emerald-500":status.pct>=50?"bg-amber-500":"bg-red-400"}`} style={{width:`${status.pct}%`}}/>
                    </div>
                    <span className="text-[10px] text-slate-500">{doneCount}/{items.length}</span>
                  </div>
                </div>
                <div className="divide-y divide-slate-50">
                  {items.map(item => (
                    <label key={item} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors`}>
                      <input type="checkbox" checked={isChecked(sKey,item)} onChange={()=>toggleItem(sKey,item)} className="rounded text-teal-600"/>
                      <span className={`text-xs ${isChecked(sKey,item)?"text-slate-400 line-through":"text-slate-700"}`}>{item}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}

          <div className="flex gap-3 pt-2">
            {ddStatus.label === "Ready for Committee" ? (
              <button onClick={() => {
                window.history.pushState({}, "", `/fi/workspace?m=credit&s=decision`);
                window.dispatchEvent(new PopStateEvent("popstate"));
                toast.success("DD complete — proceeding to Deal Actions");
              }} className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700">
                <ArrowRight size={13}/> Proceed to Deal Actions
              </button>
            ) : (
              <p className="text-xs text-amber-700 italic">Complete all sections to proceed to Deal Actions</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── AUDIT & PERMISSIONS — Real Audit Log ─────────────────────────────────
function SectionRBAC() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | approvals | rejections | reports

  useEffect(() => {
    client.get("/fi/decisions/deals").then(r => setDeals(r.data.deals || [])).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  // Extract all audit events from deal stage histories
  const auditEvents = [];
  deals.forEach(deal => {
    const history = deal.stage_history || [];
    history.forEach(event => {
      if (!event.timestamp) return;
      auditEvents.push({
        deal_id: deal.deal_id,
        deal_name: _parseName(deal),
        sector: deal.sector,
        timestamp: event.timestamp,
        action: event.action || "unknown",
        action_label: event.action_label || event.action,
        from_stage: event.from_stage,
        to_stage: event.to_stage,
        performed_by: event.performed_by || "system",
        reason: event.reason || event.notes || null,
        category: ["approve_for_committee","issue_term_sheet","mark_financial_close"].includes(event.action) ? "approval" :
                  ["not_suitable","not_eligible","decline","reject","withdraw"].includes(event.action) ? "rejection" : "action",
      });
    });
  });
  auditEvents.sort((a,b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  const filtered = filter === "all" ? auditEvents :
    filter === "approvals" ? auditEvents.filter(e=>e.category==="approval") :
    filter === "rejections" ? auditEvents.filter(e=>e.category==="rejection") :
    auditEvents;

  const CAT_STYLE = {
    approval:  "bg-emerald-100 text-emerald-700",
    rejection: "bg-red-100 text-red-700",
    action:    "bg-blue-100 text-blue-700",
  };

  if (loading) return <div className="flex items-center justify-center py-10"><RefreshCw size={14} className="animate-spin text-teal-500"/></div>;

  return (
    <div className="space-y-4" data-testid="section-rbac">
      <div>
        <h2 className="text-base font-bold text-slate-900">Audit Log & Permissions</h2>
        <p className="text-xs text-slate-500 mt-0.5">Immutable audit trail of all deal actions, approvals, and rejections. Sourced from deal stage histories.</p>
      </div>

      <div className="grid grid-cols-4 gap-3">
        {[
          { label:"Total Events",   value:auditEvents.length,                             color:"text-slate-900" },
          { label:"Approvals",      value:auditEvents.filter(e=>e.category==="approval").length, color:"text-emerald-600" },
          { label:"Rejections",     value:auditEvents.filter(e=>e.category==="rejection").length, color:"text-red-600" },
          { label:"Active Deals",   value:deals.filter(d=>!["rejected","financial_close"].includes(d.status)).length, color:"text-teal-600" },
        ].map(m=>(
          <div key={m.label} className="bg-white border border-slate-200 rounded-xl p-3.5 text-center">
            <p className={`text-2xl font-bold ${m.color}`}>{m.value}</p>
            <p className="text-[10px] text-slate-400 mt-1">{m.label}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl w-fit">
        {[["all","All Events"],["approvals","Approvals"],["rejections","Rejections"]].map(([k,l])=>(
          <button key={k} onClick={()=>setFilter(k)}
            className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all ${filter===k?"bg-white shadow-sm text-slate-800":"text-slate-500"}`}>{l}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-xl p-8 text-center">
          <p className="text-sm text-slate-400">No audit events found</p>
          <p className="text-xs text-slate-300 mt-1">Deal actions will appear here as deals progress through the pipeline.</p>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold">
                <th className="text-left px-4 py-2.5">Timestamp</th>
                <th className="text-left px-4 py-2.5">Deal</th>
                <th className="text-left px-4 py-2.5">Action</th>
                <th className="text-left px-4 py-2.5">Stage Transition</th>
                <th className="text-left px-4 py-2.5">Performed By</th>
                <th className="text-left px-4 py-2.5">Rationale</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0,20).map((ev, i) => (
                <tr key={i} className={`border-b border-slate-100 last:border-0 ${i%2===1?"bg-slate-50/30":""}`}>
                  <td className="px-4 py-2.5 text-[10px] font-mono text-slate-500">{ev.timestamp?.slice(0,16).replace("T"," ") || "—"}</td>
                  <td className="px-4 py-2.5">
                    <p className="font-medium text-slate-900 truncate max-w-[120px]">{ev.deal_name.split(" ").slice(0,2).join(" ")}</p>
                    <p className="text-[9px] text-slate-400 font-mono">{ev.deal_id}</p>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${CAT_STYLE[ev.category]}`}>{ev.action_label || ev.action}</span>
                  </td>
                  <td className="px-4 py-2.5 text-[10px] text-slate-600">
                    {ev.from_stage && ev.to_stage ? <>{ev.from_stage.replace(/_/g," ")} <span className="text-slate-400">→</span> {ev.to_stage.replace(/_/g," ")}</> : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-[10px] text-slate-600">{ev.performed_by?.split("@")[0] || "system"}</td>
                  <td className="px-4 py-2.5 text-[10px] text-slate-500 italic truncate max-w-[140px]">{ev.reason?.slice(0,60) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length > 20 && <p className="px-4 py-2 text-[10px] text-slate-400 bg-slate-50 border-t border-slate-200">Showing 20 of {filtered.length} events</p>}
        </div>
      )}
    </div>
  );
}

// ── REPORTING & DISCLOSURE HUB ─────────────────────────────────────────────
// Institutional-grade reporting: color-coded categories, status, completeness,
// version history, real PDF generation for all 12 report types.
function SectionRegulatoryMapping() {
  const [deals,       setDeals]       = useState([]);
  const [selected,    setSelected]    = useState(null);
  const [exporting,   setExporting]   = useState(null);
  const [metadata,    setMetadata]    = useState(null);
  const [versions,    setVersions]    = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  // Portfolio scope filters for regulatory/portfolio reports
  const [portSectorFilter, setPortSectorFilter] = useState("all");  // "all" | sector name
  const [portStatusFilter, setPortStatusFilter] = useState("active"); // "active" | "all" | "closed"
  const [allSectors, setAllSectors] = useState([]);

  // Load deals
  useEffect(() => {
    client.get("/fi/decisions/deals").then(r => {
      const d = (r.data.deals || []).filter(d => d.status !== "rejected");
      setDeals(d);
      if (d.length > 0) {
        const preferred = d.find(x => x.deal_id === "FI-LAFARGE-2026-003") || d[0];
        setSelected(preferred.deal_id);
        // Collect unique sectors
        const secs = [...new Set(d.map(x => x.sector).filter(Boolean))];
        setAllSectors(secs);
      }
    }).catch(() => {});
  }, []);

  // Load metadata + version history when deal selected (for deal-level reports)
  useEffect(() => {
    if (!selected) return;
    setLoadingMeta(true);
    Promise.all([
      client.get(`/fi/reports/metadata/${selected}`).then(r => r.data).catch(() => null),
      client.get(`/fi/reports/versions`).then(r => r.data.versions || []).catch(() => []),
    ]).then(([meta, vers]) => {
      setMetadata(meta);
      setVersions(vers);
    }).finally(() => setLoadingMeta(false));
  }, [selected]);

  const selectedDeal = deals.find(d => d.deal_id === selected) || null;

  // Portfolio scope for regulatory: how many deals included
  const portfolioDeals = deals.filter(d => {
    const stageActive = ["due_diligence","credit_committee","term_sheet","financial_close","screening","data_request"];
    const stageClosed = ["financial_close"];
    if (portStatusFilter === "active") return stageActive.includes(d.status);
    if (portStatusFilter === "closed") return stageClosed.includes(d.status);
    return true; // "all"
  }).filter(d => portSectorFilter === "all" || d.sector === portSectorFilter);

  // Build filter payload for regulatory reports
  const getPortfolioFilters = () => {
    const body = {};
    if (portSectorFilter !== "all") body.sector_filter = [portSectorFilter];
    if (portStatusFilter !== "all") {
      if (portStatusFilter === "active") body.status_filter = ["screening","data_request","due_diligence","credit_committee","term_sheet"];
      if (portStatusFilter === "closed") body.status_filter = ["financial_close"];
    }
    return body;
  };

  // Compliance engine: compute deal-level status for regulatory reports
  const getRegStatus = (reportKey) => {
    if (!selectedDeal) return "blocked";
    const ev = selectedDeal.evidence_readiness_pct || 0;
    const stage = selectedDeal.status;
    if (reportKey === "cbn-nsfp") return ev >= 40 ? "conditional" : "blocked";
    if (reportKey === "eu-taxonomy") return ev >= 50 ? "conditional" : "blocked";
    if (reportKey === "ifc-ps") return ev >= 40 ? "conditional" : "blocked";
    if (reportKey === "glp") return (ev >= 40 && stage !== "screening") ? "conditional" : "blocked";
    return "conditional";
  };

  const getMissing = (reportKey) => {
    if (!selectedDeal) return ["Select a deal first"];
    const ev = selectedDeal.evidence_readiness_pct || 0;
    const gaps = [];
    if (ev < 70) gaps.push(`Evidence readiness ${ev.toFixed(0)}% (need ≥70%)`);
    if (reportKey === "eu-taxonomy" && ev < 50) gaps.push("DNSH documentation required");
    if (reportKey === "cbn-nsfp") gaps.push("ESMS policy document");
    if (reportKey === "ifc-ps") gaps.push("E&S Impact Assessment");
    if (reportKey === "glp") gaps.push("Green Loan Framework document");
    return gaps;
  };

  // Completeness from metadata
  const getCompleteness = (reportKey) => {
    if (metadata?.reports?.[reportKey]) return metadata.reports[reportKey].completeness;
    if (!selectedDeal) return 0;
    const ev = selectedDeal.evidence_readiness_pct || 0;
    const checks = [
      selectedDeal.deployed_amount_usd != null,
      ev >= 40,
      selectedDeal.sector != null,
      (selectedDeal.status !== "screening"),
    ];
    return Math.round(checks.filter(Boolean).length / checks.length * 100);
  };

  const getLastGenerated = (reportKey) => {
    const meta = metadata?.reports?.[reportKey];
    if (!meta?.last_generated) return null;
    const d = new Date(meta.last_generated);
    const now = new Date();
    const diffMs = now - d;
    const diffHrs = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffHrs < 1) return "Just now";
    if (diffHrs < 24) return `${diffHrs}h ago`;
    return `${diffDays}d ago`;
  };

  const getVersion = (reportKey) => metadata?.reports?.[reportKey]?.version || 0;

  // Export helpers
  const exportDealReport = async (reportType, fileName, retrying = false) => {
    if (!selected) { toast.error("Select a deal first."); return; }
    if (!retrying) setExporting(reportType);
    try {
      const ev = selectedDeal?.evidence_readiness_pct || 70;
      const dscr = (selectedDeal?.notes || "").match(/DSCR base case[\s:]*([\d.]+)x/i)?.[1];
      const payload = {
        baseline_tco2e: 50000, annual_reduction_tco2e: 15000, confidence_pct: ev,
        amount_usd: selectedDeal?.deployed_amount_usd || 14200000,
        annual_dscr: dscr ? parseFloat(dscr) : 1.35,
        sector: selectedDeal?.sector || "cement", country: "NG",
      };
      const resp = await client.post(`/fi/pdf/${reportType}/${selected}`, payload, { responseType: "blob" });
      if (!resp.data || resp.data.size === 0) throw new Error("Empty PDF response");
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `DeCarbonIQ_${fileName}_${selected}_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`${fileName} exported. QR code embedded for verification.`);
      client.get(`/fi/reports/metadata/${selected}`).then(r => setMetadata(r.data)).catch(() => {});
      client.get(`/fi/reports/versions`).then(r => setVersions(r.data.versions || [])).catch(() => {});
    } catch(e) {
      if (!retrying) {
        toast.loading("Report generation failed. Retrying…", { id: `retry-${reportType}`, duration: 4000 });
        setTimeout(() => exportDealReport(reportType, fileName, true), 2500);
        return;
      }
      toast.error(`${fileName}: ${e?.response?.data?.detail || e?.message || "PDF generation failed — if this is a new deployment, wait 30 seconds for the PDF service to initialise and try again"}`);
    }
    setExporting(null);
  };

  const exportPortfolioReport = async (reportType, fileName, retrying = false) => {
    if (!retrying) setExporting(reportType);
    try {
      const resp = await client.post("/fi/pdf/portfolio-report", { report_type: reportType }, { responseType: "blob" });
      if (!resp.data || resp.data.size === 0) throw new Error("Empty PDF response");
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `DeCarbonIQ_${fileName}_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`${fileName} exported.`);
    } catch(e) {
      if (!retrying) {
        toast.loading("Report generation failed. Retrying in 5 seconds…", { id: `retry-${reportType}`, duration: 6000 });
        setTimeout(() => exportPortfolioReport(reportType, fileName, true), 5000);
        return;
      }
      toast.error(`${fileName}: ${e?.response?.data?.detail || e?.message || "PDF generation failed — if this is a new deployment, wait 30 seconds and try again"}`);
    }
    setExporting(null);
  };

  const exportRegulatoryReport = async (regType, fileName, retrying = false) => {
    if (!retrying) setExporting(regType);
    try {
      const filters = getPortfolioFilters();
      const resp = await client.post(`/fi/pdf/regulatory/${regType}`, filters, { responseType: "blob" });
      if (!resp.data || resp.data.size === 0) throw new Error("Empty PDF response");
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `DeCarbonIQ_${fileName}_Portfolio_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`${fileName} generated — Portfolio-level disclosure (${portfolioDeals.length} deals aggregated). QR verification embedded.`);
      client.get(`/fi/reports/versions`).then(r => setVersions(r.data.versions || [])).catch(() => {});
    } catch(e) {
      if (!retrying) {
        toast.loading("Report generation failed. Retrying…", { id: `retry-${regType}`, duration: 4000 });
        setTimeout(() => exportRegulatoryReport(regType, fileName, true), 2500);
        return;
      }
      toast.error(`${fileName}: ${e?.response?.data?.detail || e?.message || "Regulatory report generation failed"}`);
    }
    setExporting(null);
  };

  // Report groups config
  const DEAL_REPORTS = [
    {
      key: "credit-committee",
      label: "Credit Committee Pack",
      desc: "13-section institutional committee document",
      sources: "Signal Engine · Credit Engine · Carbon Desk",
      fn: () => exportDealReport("credit-committee", "CreditCommitteePack"),
    },
    {
      key: "solar-brief",
      label: "Climate Finance Brief",
      desc: "Investment brief: signal score, eligibility, carbon scenarios",
      sources: "Signal Engine · Eligibility Engine",
      fn: () => exportDealReport("solar-brief", "ClimateFinanceBrief"),
    },
    {
      key: "ghg-inventory",
      label: "GHG Inventory Report",
      desc: "Scope 1, 2, 3 breakdown · GHG Protocol · SHA-256 hash",
      sources: "V1 Engine · IPCC AR6 · GHG Protocol",
      fn: () => exportDealReport("ghg-inventory", "GHGInventory"),
    },
    {
      key: "term-sheet",
      label: "Term Sheet",
      desc: "Indicative commercial terms · instrument, tenor, rate, conditions",
      sources: "Credit Engine · Pricing Engine",
      fn: () => exportDealReport("term-sheet", "TermSheet"),
    },
  ];

  const PORTFOLIO_REPORTS = [
    {
      key: "ifrs_s2",
      label: "IFRS S2 Disclosure Report",
      desc: "Climate-related financial disclosures · physical + transition risk",
      sources: "Risk Engine · All Deals · Scenario Engine",
      fn: () => exportPortfolioReport("ifrs_s2", "IFRS_S2"),
    },
    {
      key: "tcfd",
      label: "TCFD Report",
      desc: "Task Force on Climate-Related Financial Disclosures · 4-pillar",
      sources: "Risk Engine · Scenario Analysis",
      fn: () => exportPortfolioReport("tcfd", "TCFD"),
    },
    {
      key: "pcaf",
      label: "PCAF Financed Emissions",
      desc: "Scope 3 Cat.15 · PCAF Standard v1.1 · Data quality scored",
      sources: "Credit Engine · Emissions Calculator",
      fn: () => exportPortfolioReport("pcaf", "PCAF"),
    },
    {
      key: "portfolio_exposure",
      label: "Portfolio Climate Exposure",
      desc: "Sector / country / deal exposure · concentration alerts",
      sources: "All Deals · Risk Engine",
      fn: () => exportPortfolioReport("portfolio_exposure", "PortfolioExposure"),
    },
  ];

  const REGULATORY_REPORTS = [
    {
      key: "cbn-nsfp",
      label: "CBN Sustainable Finance",
      desc: "9-principle NSFP mapping · compliance status · gaps · actions",
      sources: "Compliance Engine v1.0 · CBN NSFP 2022",
      fn: () => exportRegulatoryReport("cbn", "CBN_NSFP"),
    },
    {
      key: "eu-taxonomy",
      label: "EU Taxonomy Alignment",
      desc: "CCM eligibility · DNSH validation · social safeguards",
      sources: "Compliance Engine v1.0 · EU Taxonomy 2020/852",
      fn: () => exportRegulatoryReport("eu-taxonomy", "EU_Taxonomy"),
    },
    {
      key: "ifc-ps",
      label: "IFC Performance Standards",
      desc: "PS1–8 mapping · risk category (A/B/C) · ESMS requirements",
      sources: "Compliance Engine v1.0 · IFC PS 2012",
      fn: () => exportRegulatoryReport("ifc-ps", "IFC_PS"),
    },
    {
      key: "glp",
      label: "Green Loan Principles",
      desc: "LMA GLP 2021 · use of proceeds · evaluation · management · reporting",
      sources: "Compliance Engine v1.0 · LMA GLP 2021",
      fn: () => exportRegulatoryReport("glp", "GLP"),
    },
  ];

  const dealVersions = versions.filter(v => v.deal_id === selected);
  const totalReady = DEAL_REPORTS.length + PORTFOLIO_REPORTS.length;

  return (
    <div className="space-y-5" data-testid="section-reporting-hub">

      {/* Header KPI strip */}
      <div className="bg-[#0F172A] rounded-xl p-4">
        <p className="text-[10px] text-teal-400 font-bold uppercase tracking-[0.15em] mb-3">Reporting & Disclosure Hub</p>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Reports Available</p>
            <p className="text-xl font-bold text-white">12</p>
            <p className="text-[10px] text-slate-600">deal · portfolio · regulatory</p>
          </div>
          <div>
            <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Ready to Generate</p>
            <p className="text-xl font-bold text-emerald-400">{totalReady}</p>
            <p className="text-[10px] text-slate-600">with live data</p>
          </div>
          <div>
            <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Versions Generated</p>
            <p className="text-xl font-bold text-white">{dealVersions.length}</p>
            <p className="text-[10px] text-slate-600">for selected deal</p>
          </div>
          <div>
            <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">QR Verification</p>
            <p className="text-xl font-bold text-teal-400">ACTIVE</p>
            <p className="text-[10px] text-slate-600">verify.decarboniq.com — scan any exported PDF</p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-white/10 text-xs text-slate-400 space-y-0.5">
          <p>— All reports include SHA-256 hash, QR verification code, and data integrity block</p>
          <p>— Regulatory reports generated from rule-based compliance engine — not static text</p>
          <p>— Every generation tracked for version control and audit trail</p>
        </div>
      </div>

      {/* Deal selector — ONLY for Deal-Level reports */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
          Deal Snapshot — for Deal-Level Reports only (Credit Memo, GHG Inventory, Term Sheet, Climate Brief)
        </p>
        <div className="flex items-center gap-4">
          <select
            value={selected || ""}
            onChange={e => setSelected(e.target.value)}
            className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none"
            data-testid="report-deal-select"
          >
            {deals.map(d => <option key={d.deal_id} value={d.deal_id}>{d.deal_id} — {d.deal_id.slice(-6)}</option>)}
          </select>
          {selectedDeal && (
            <div className="flex gap-4 text-xs flex-shrink-0">
              {[
                ["Sector", selectedDeal.sector || "—"],
                ["Amount", selectedDeal.deployed_amount_usd ? `$${(selectedDeal.deployed_amount_usd/1e6).toFixed(1)}M` : "—"],
                ["Evidence", `${(selectedDeal.evidence_readiness_pct||0).toFixed(0)}%`],
                ["Stage", selectedDeal.stage_label || selectedDeal.status],
              ].map(([l, v]) => (
                <div key={l} className="text-center">
                  <p className="text-[9px] text-slate-400 uppercase tracking-wider">{l}</p>
                  <p className="font-bold text-slate-900 mt-0.5 capitalize">{v}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Portfolio Scope — for Portfolio Disclosure Reports */}
      <div className="bg-[#0F172A] rounded-xl p-4" data-testid="portfolio-scope-panel">
        <p className="text-[10px] text-teal-400 font-bold uppercase tracking-[0.15em] mb-3">
          Portfolio Scope — Portfolio Disclosure Reports
        </p>
        <div className="grid grid-cols-4 gap-4 mb-3">
          <div>
            <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Scope</p>
            <p className="text-sm font-bold text-white">Keystone Bank</p>
            <p className="text-[10px] text-slate-500">Climate &amp; Transition Portfolio</p>
          </div>
          <div>
            <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Deals Included</p>
            <p className="text-xl font-bold text-teal-400">{portfolioDeals.length}</p>
            <p className="text-[10px] text-slate-600">of {deals.length} total</p>
          </div>
          <div>
            <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Total Exposure</p>
            <p className="text-xl font-bold text-white">${(portfolioDeals.reduce((s,d)=>s+(d.deployed_amount_usd||0),0)/1e6).toFixed(1)}M</p>
            <p className="text-[10px] text-slate-600">across filtered deals</p>
          </div>
          <div>
            <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Report Level</p>
            <p className="text-sm font-bold text-emerald-400">PORTFOLIO</p>
            <p className="text-[10px] text-slate-600">not deal-specific</p>
          </div>
        </div>
        {/* Optional filters */}
        <div className="flex items-center gap-3 border-t border-white/10 pt-3">
          <p className="text-[9px] text-slate-500 uppercase tracking-wider flex-shrink-0">Filters (optional):</p>
          <select
            value={portSectorFilter}
            onChange={e => setPortSectorFilter(e.target.value)}
            className="px-2 py-1 text-[10px] border border-white/10 rounded-lg bg-[#1E293B] text-slate-300 focus:outline-none"
            data-testid="port-sector-filter"
          >
            <option value="all">All Sectors</option>
            {allSectors.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
          </select>
          <select
            value={portStatusFilter}
            onChange={e => setPortStatusFilter(e.target.value)}
            className="px-2 py-1 text-[10px] border border-white/10 rounded-lg bg-[#1E293B] text-slate-300 focus:outline-none"
            data-testid="port-status-filter"
          >
            <option value="active">Active Pipeline</option>
            <option value="all">All Stages</option>
            <option value="closed">Closed / Deployed</option>
          </select>
          {(portSectorFilter !== "all" || portStatusFilter !== "active") && (
            <button
              onClick={() => { setPortSectorFilter("all"); setPortStatusFilter("active"); }}
              className="text-[9px] text-slate-500 hover:text-slate-300 border border-white/10 px-2 py-1 rounded-lg"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* ── SECTION A: Deal-Level Reports (GREEN) ─────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-3 h-3 rounded-full bg-emerald-500 flex-shrink-0" />
          <p className="text-xs font-bold text-emerald-800">DEAL-LEVEL REPORTS</p>
          <span className="text-[10px] text-slate-400 ml-1">Generated per deal from pipeline, credit engine, and carbon desk</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {DEAL_REPORTS.map(r => {
            const completeness = getCompleteness(r.key);
            const lastGen = getLastGenerated(r.key);
            const version = getVersion(r.key);
            const isExporting = exporting === r.key;
            return (
              <div key={r.key} className="border border-emerald-200 bg-emerald-50/60 rounded-xl p-4" data-testid={`report-${r.key}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      <span className="text-[9px] font-bold px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">DEAL</span>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 bg-emerald-600 text-white rounded">Ready</span>
                      {version > 0 && <span className="text-[9px] text-slate-400">v{version}</span>}
                    </div>
                    <p className="text-xs font-bold text-slate-900">{r.label}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">{r.desc}</p>
                  </div>
                </div>
                {/* Completeness bar */}
                <div className="mb-2">
                  <div className="flex justify-between text-[9px] mb-0.5">
                    <span className="text-slate-400">Data completeness</span>
                    <span className="font-bold text-emerald-700">{completeness.toFixed(0)}%</span>
                  </div>
                  <div className="h-1 bg-white rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${completeness}%` }} />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-[9px] text-slate-400">
                    <p className="truncate">{r.sources}</p>
                    {lastGen && <p className="text-emerald-600 font-medium">Last: {lastGen}</p>}
                  </div>
                  <button
                    onClick={r.fn}
                    disabled={isExporting}
                    className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 text-white text-[10px] font-bold rounded-lg hover:bg-emerald-700 disabled:opacity-40 flex-shrink-0 ml-2"
                  >
                    {isExporting ? <RefreshCw size={10} className="animate-spin" /> : null}
                    {isExporting ? "..." : "Generate"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── SECTION B: Portfolio / FI Reports (BLUE) ─────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-3 h-3 rounded-full bg-blue-500 flex-shrink-0" />
          <p className="text-xs font-bold text-blue-800">PORTFOLIO / FI REPORTS</p>
          <span className="text-[10px] text-slate-400 ml-1">Generated across all active deals — institutional disclosure standards</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {PORTFOLIO_REPORTS.map(r => {
            const completeness = getCompleteness(r.key);
            const isExporting = exporting === r.key;
            return (
              <div key={r.key} className="border border-blue-200 bg-blue-50/60 rounded-xl p-4" data-testid={`report-${r.key}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      <span className="text-[9px] font-bold px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">PORTFOLIO</span>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 bg-blue-600 text-white rounded">AUTO</span>
                    </div>
                    <p className="text-xs font-bold text-slate-900">{r.label}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">{r.desc}</p>
                  </div>
                </div>
                <div className="mb-2">
                  <div className="flex justify-between text-[9px] mb-0.5">
                    <span className="text-slate-400">Data completeness</span>
                    <span className="font-bold text-blue-700">{completeness.toFixed(0)}%</span>
                  </div>
                  <div className="h-1 bg-white rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${completeness}%` }} />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-[9px] text-slate-400 truncate flex-1">{r.sources}</p>
                  <button
                    onClick={r.fn}
                    disabled={isExporting}
                    className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-600 text-white text-[10px] font-bold rounded-lg hover:bg-blue-700 disabled:opacity-40 flex-shrink-0 ml-2"
                  >
                    {isExporting ? <RefreshCw size={10} className="animate-spin" /> : null}
                    {isExporting ? "..." : "Generate"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── SECTION C: Regulatory Reports (AMBER) ────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-3 h-3 rounded-full bg-amber-500 flex-shrink-0" />
          <div>
            <p className="text-xs font-bold text-amber-800">PORTFOLIO DISCLOSURE REPORTS</p>
            <p className="text-[10px] text-slate-400">Generated across portfolio — not deal-specific · Rule-based compliance engine · {portfolioDeals.length} deals included</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {REGULATORY_REPORTS.map(r => {
            const completeness = Math.round((portfolioDeals.reduce((s,d)=>s+(d.evidence_readiness_pct||0),0)/Math.max(portfolioDeals.length,1))*0.8);
            const lastGen = getLastGenerated(r.key);
            const version = getVersion(r.key);
            const isExporting = exporting === r.key;
            return (
              <div
                key={r.key}
                className="border border-amber-200 bg-amber-50/60 rounded-xl p-4"
                data-testid={`report-${r.key}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      <span className="text-[9px] font-bold px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">PORTFOLIO</span>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-600 text-white">Disclosure</span>
                      {version > 0 && <span className="text-[9px] text-slate-400">v{version}</span>}
                    </div>
                    <p className="text-xs font-bold text-slate-900">{r.label}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">{r.desc}</p>
                  </div>
                </div>
                {/* Completeness bar */}
                <div className="mb-2">
                  <div className="flex justify-between text-[9px] mb-0.5">
                    <span className="text-slate-400">Portfolio evidence coverage</span>
                    <span className="font-bold text-amber-700">{completeness}%</span>
                  </div>
                  <div className="h-1 bg-white rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${completeness}%` }} />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-[9px] text-slate-400">
                    <p className="truncate">{r.sources}</p>
                    <p className="text-teal-700 font-medium">{portfolioDeals.length} deals · portfolio-level</p>
                    {lastGen && <p className="text-amber-600 font-medium">Last: {lastGen}</p>}
                  </div>
                  <button
                    onClick={r.fn}
                    disabled={isExporting || portfolioDeals.length === 0}
                    className="flex items-center gap-1 px-2.5 py-1.5 bg-amber-600 text-white text-[10px] font-bold rounded-lg hover:bg-amber-700 disabled:opacity-40 flex-shrink-0 ml-2"
                  >
                    {isExporting ? <RefreshCw size={10} className="animate-spin" /> : null}
                    {isExporting ? "..." : "Generate"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── VERSION HISTORY PANEL ──────────────────────────────────────────── */}
      {versions.length > 0 && (
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <p className="text-[10px] font-bold text-slate-700 uppercase tracking-wider">Version History — All Reports</p>
            <span className="text-[10px] text-slate-400">{versions.length} reports generated</span>
          </div>
          <div className="divide-y divide-slate-50 max-h-56 overflow-y-auto">
            {versions.slice(0, 20).map(v => (
              <div key={v.id} className="flex items-center gap-3 px-4 py-2.5">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center">
                  <CheckCircle2 size={11} className="text-teal-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-slate-800">{v.report_type.toUpperCase()} <span className="font-normal text-slate-400">v{v.version_num}</span></p>
                  <p className="text-[9px] text-slate-400 truncate">{v.deal_name || v.report_id}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[9px] font-bold text-slate-600">{v.completeness_pct?.toFixed(0) || "—"}% complete</p>
                  <p className="text-[9px] text-slate-400">{v.generated_at ? new Date(v.generated_at).toLocaleDateString() : "—"}</p>
                </div>
                <div className="text-[9px] font-mono text-slate-300 flex-shrink-0 w-16 truncate">
                  {v.report_hash?.slice(0, 8)}...
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ── POST-DISBURSEMENT MONITORING ─────────────────────────────────────────────
// ── PORTFOLIO MONITORING & COVENANT TRACKING ─────────────────────────────
// Question: Which deals are deviating and what action is required?
// Only applies to DISBURSED (financial_close) deals.
function SectionPostDisbursement() {
  const [deals,    setDeals]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [actioning,setActioning]= useState(null);
  const [startCycle, setStartCycle] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all"); // all | performing | watchlist | breach
  // Action modal state
  const [actionModal, setActionModal] = useState(null); // { type, deal }
  const [actionNote,  setActionNote]  = useState("");
  const [actionUrgency, setActionUrgency] = useState("standard");
  const [dataItems, setDataItems] = useState([]); // for request-data
  const [actionLoading, setActionLoading] = useState(false);

  const reload = () => {
    client.get("/fi/decisions/deals")
      .then(r => setDeals(r.data.deals || []))
      .catch(() => {});
  };

  useEffect(() => {
    client.get("/fi/decisions/deals")
      .then(r => setDeals(r.data.deals || []))
      .catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const openAction = (type, deal) => {
    setActionModal({ type, deal });
    setActionNote("");
    setActionUrgency("standard");
    setDataItems(["DSCR (latest quarter)", "MRV report", "Financial statements"]);
  };

  const submitAction = async () => {
    if (!actionModal) return;
    setActionLoading(true);
    const { type, deal } = actionModal;
    try {
      let body, endpoint;
      if (type === "covenant_review") {
        endpoint = `/fi/monitoring/deals/${deal.deal_id}/covenant-review`;
        body = { review_note: actionNote, review_type: actionUrgency === "urgent" ? "triggered" : "periodic" };
      } else if (type === "request_data") {
        endpoint = `/fi/monitoring/deals/${deal.deal_id}/request-data`;
        body = { data_needed: dataItems.filter(Boolean), urgency: actionUrgency, deadline_days: 14, note: actionNote };
      } else if (type === "escalate") {
        if (!actionNote.trim()) { toast.error("Escalation reason is required."); setActionLoading(false); return; }
        endpoint = `/fi/monitoring/deals/${deal.deal_id}/escalate`;
        body = { escalation_reason: actionNote, urgency: actionUrgency };
      }
      const resp = await client.post(endpoint, body);
      toast.success(
        type === "covenant_review" ? `Covenant review triggered for ${deal.deal_id}. Next review: ${resp.data.next_review_date}` :
        type === "request_data"    ? `Data request created — ${resp.data.items_requested?.length} item(s), due ${resp.data.deadline}` :
        `Escalated to Credit Committee. ${resp.data.recommended_action}`
      );
      setActionModal(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed. Please try again.");
    }
    setActionLoading(false);
  };

  // Only disbursed deals are monitored
  const disbursed = deals.filter(d => d.status === "financial_close");

  // MRV classification
  const getMRV = (deal) => {
    const notes = (deal.notes || "").toLowerCase();
    const ev = deal.evidence_readiness_pct || 0;
    if ((deal.notes||"").length < 20 && ev === 0) return { label:"Missing",     color:"text-red-700",     bg:"bg-red-100",     dot:"bg-red-500"   };
    if (notes.includes("verified") && ev >= 80)   return { label:"Verified",    color:"text-emerald-700", bg:"bg-emerald-100", dot:"bg-emerald-500"};
    return                                                 { label:"Pending",    color:"text-amber-700",   bg:"bg-amber-100",   dot:"bg-amber-400" };
  };

  // Generate monitoring status from deal characteristics
  const getMonitoringStatus = (deal) => {
    const sc    = _parseScore(deal);
    const elig  = _parseElig(deal);
    const mrv   = getMRV(deal);
    const dscr  = (deal.notes||"").match(/DSCR base case[:\s]*([\d.]+)x/i)?.[1] ? parseFloat((deal.notes||"").match(/DSCR base case[:\s]*([\d.]+)x/i)[1]) : null;
    const history = deal.stage_history || [];
    const lastUpdate = history.length > 0 && history[history.length-1].timestamp
      ? new Date(history[history.length-1].timestamp)
      : null;
    const daysSinceUpdate = lastUpdate ? Math.floor((Date.now()-lastUpdate.getTime()) / (1000*60*60*24)) : null;

    const ALERTS = [];
    if (mrv.label === "Missing")         ALERTS.push({ sev:"H", text:"No MRV submission — monitoring data unavailable" });
    if (elig === "conditional")          ALERTS.push({ sev:"M", text:"Conditional eligibility — conditions not yet fully resolved" });
    if (sc !== null && sc < 45)          ALERTS.push({ sev:"M", text:`Signal score ${sc}/100 — below 45 threshold. Monitor performance closely.` });
    if (dscr !== null && dscr < 1.3)     ALERTS.push({ sev:"H", text:`DSCR ${dscr}x — approaching covenant minimum (1.30x). Watchlist required.` });
    if (mrv.label === "Pending")         ALERTS.push({ sev:"L", text:"MRV pending verification — carbon claims cannot be substantiated" });
    if (daysSinceUpdate && daysSinceUpdate > 30) ALERTS.push({ sev:"M", text:`No activity for ${daysSinceUpdate} days — confirm reporting cycle is active` });

    const status = ALERTS.some(a=>a.sev==="H") ? "breach" : ALERTS.some(a=>a.sev==="M") ? "watchlist" : "performing";

    // Expected vs actual (indicative from sector benchmarks)
    const sc_sector = SECTOR_CARBON[deal.sector] || { tco2e: 5000 };
    const expectedReduction = sc_sector.tco2e * 0.30;
    const actualReductionPct = mrv.label === "Verified" ? 95 : mrv.label === "Pending" ? 70 : 0;
    const actualReduction    = Math.round(expectedReduction * actualReductionPct / 100);
    const expectedDSCR       = 1.30;

    return { status, ALERTS, mrv, expectedReduction, actualReduction, actualReductionPct, expectedDSCR, dscr, lastUpdate, daysSinceUpdate };
  };

  const monitoringData = disbursed.map(d => ({ deal: d, mon: getMonitoringStatus(d) }));
  const filtered = filterStatus === "all" ? monitoringData :
    monitoringData.filter(m => m.mon.status === filterStatus);

  const breachCount = monitoringData.filter(m=>m.mon.status==="breach").length;
  const watchCount  = monitoringData.filter(m=>m.mon.status==="watchlist").length;

  const STATUS_STYLE = {
    performing: { label:"Performing",  badge:"bg-emerald-100 text-emerald-700", border:"border-emerald-200" },
    watchlist:  { label:"Watchlist",   badge:"bg-amber-100 text-amber-700",     border:"border-amber-200"   },
    breach:     { label:"Breach Risk", badge:"bg-red-100 text-red-700",         border:"border-red-200"     },
  };

  if (loading) return <div className="flex items-center justify-center py-12"><RefreshCw size={14} className="animate-spin text-teal-500"/></div>;

  return (
    <div className="space-y-5" data-testid="section-post-disbursement">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-900">Portfolio Monitoring & Covenant Tracking</h2>
          <p className="text-xs text-slate-500 mt-0.5">Applies only to disbursed deals. Tracks performance, MRV compliance, and covenant adherence.</p>
        </div>
        {disbursed.length > 0 && (
          <div className="flex gap-1 p-1 bg-slate-100 rounded-xl">
            {[["all",`All (${disbursed.length})`],["performing","Performing"],["watchlist","Watchlist"],["breach","Breach Risk"]].map(([k,l])=>(
              <button key={k} onClick={()=>setFilterStatus(k)}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all ${filterStatus===k?"bg-white shadow-sm text-slate-800":"text-slate-500 hover:text-slate-700"}`}>
                {l}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Summary alerts */}
      {disbursed.length > 0 && (breachCount > 0 || watchCount > 0) && (
        <div className="space-y-2">
          {breachCount > 0 && <div className="flex items-center gap-2 border border-red-200 bg-red-50 rounded-xl px-4 py-3 text-xs text-red-700"><AlertTriangle size={12}/> {breachCount} deal{breachCount>1?"s":""} with breach risk — DSCR or MRV issues require immediate review</div>}
          {watchCount > 0  && <div className="flex items-center gap-2 border border-amber-200 bg-amber-50 rounded-xl px-4 py-3 text-xs text-amber-700"><AlertTriangle size={12}/> {watchCount} deal{watchCount>1?"s":""} on watchlist — conditional eligibility or pending MRV</div>}
        </div>
      )}

      {/* No disbursed deals → guided onboarding */}
      {disbursed.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-xl p-8 text-center">
          <Activity size={28} className="mx-auto mb-3 text-slate-200"/>
          <p className="text-sm font-semibold text-slate-500 mb-1">No monitoring cycle started</p>
          <p className="text-xs text-slate-400 mb-4">Portfolio Monitoring applies to disbursed deals only. Move deals to Financial Close stage to begin covenant and performance tracking.</p>
          {!startCycle ? (
            <button onClick={()=>setStartCycle(true)}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 mx-auto"
              data-testid="start-monitoring">
              <ArrowRight size={12}/> Begin Monitoring Setup
            </button>
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-left max-w-md mx-auto">
              <p className="text-xs font-bold text-slate-700 mb-2">Monitoring Setup Checklist</p>
              {["Advance at least 1 deal to Financial Close (from Deal Actions)","Ensure deal has DSCR and facility amount set","Confirm MRV monitoring plan is in place","Set reporting period (quarterly recommended)"].map((s,i)=>(
                <div key={i} className="flex items-center gap-2 text-xs text-slate-600 py-1 border-b border-slate-100 last:border-0">
                  <span className="text-slate-300 font-mono">{i+1}.</span>{s}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Deal Monitoring Cards */
        <div className="space-y-3">
          {filtered.map(({ deal, mon }) => {
            const style = STATUS_STYLE[mon.status];
            const highAlerts = mon.ALERTS.filter(a=>a.sev==="H");
            const medAlerts  = mon.ALERTS.filter(a=>a.sev==="M");
            return (
              <div key={deal.deal_id}
                className={`border-2 rounded-xl overflow-hidden ${mon.status==="breach"?"border-red-300":mon.status==="watchlist"?"border-amber-300":"border-emerald-300"}`}
                data-testid={`monitoring-${deal.deal_id}`}>

                {/* Card header */}
                <div className={`px-5 py-3 flex items-center justify-between ${mon.status==="breach"?"bg-red-50":mon.status==="watchlist"?"bg-amber-50":"bg-emerald-50"}`}>
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded ${style.badge}`}>{style.label}</span>
                    <div>
                      <p className="text-sm font-bold text-slate-900">{_parseName(deal)}</p>
                      <p className="text-[10px] text-slate-500">{deal.deal_id} · {deal.sector} · Financial Close</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-right">
                    <div>
                      <p className="text-[9px] text-slate-400 uppercase tracking-wider">MRV Status</p>
                      <span className={`text-[10px] font-bold ${mon.mrv.color}`}>{mon.mrv.label}</span>
                    </div>
                    {mon.lastUpdate && (
                      <div>
                        <p className="text-[9px] text-slate-400 uppercase tracking-wider">Last Update</p>
                        <p className="text-[10px] font-bold text-slate-700">{mon.daysSinceUpdate}d ago</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Monitoring metrics */}
                <div className="px-5 py-3 border-t border-slate-100 grid grid-cols-4 gap-4 text-xs bg-white">
                  <div>
                    <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-1">Expected CO₂ Reduction</p>
                    <p className="font-bold text-slate-900">{mon.expectedReduction.toLocaleString()} tCO₂e/yr</p>
                    <p className="text-[10px] text-slate-400">Sector benchmark</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-1">Actual CO₂ Reduction</p>
                    <p className={`font-bold ${mon.actualReductionPct < 70?"text-amber-600":"text-emerald-700"}`}>
                      {mon.mrv.label !== "Missing" ? `${mon.actualReduction.toLocaleString()} tCO₂e/yr` : "—"}
                    </p>
                    <p className="text-[10px] text-slate-400">{mon.mrv.label === "Verified" ? "MRV verified" : mon.mrv.label === "Pending" ? "Estimated, pending verification" : "MRV data missing"}</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-1">DSCR</p>
                    <p className={`font-bold ${mon.dscr && mon.dscr < 1.30?"text-amber-600":"text-emerald-700"}`}>
                      {mon.dscr ? `${mon.dscr}x (actual)` : "Not reported"}
                    </p>
                    <p className="text-[10px] text-slate-400">Min covenant: 1.30x</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-1">Next Report Due</p>
                    <p className="font-bold text-slate-900">Q2 2026</p>
                    <p className="text-[10px] text-slate-400">Quarterly reporting cycle</p>
                  </div>
                </div>

                {/* Alerts + Actions */}
                {(highAlerts.length > 0 || medAlerts.length > 0) && (
                  <div className="px-5 py-3 border-t border-slate-100 bg-white">
                    <div className="space-y-1.5 mb-3">
                      {[...highAlerts, ...medAlerts].map((a,i) => (
                        <div key={i} className={`flex items-start gap-1.5 text-[11px] ${a.sev==="H"?"text-red-700":"text-amber-700"}`}>
                          <AlertTriangle size={10} className="flex-shrink-0 mt-0.5"/>
                          <span>⚠ {a.text}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {highAlerts.length > 0 && (
                        <button
                          onClick={() => openAction("covenant_review", deal)}
                          className="text-[10px] font-bold px-2.5 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                          data-testid={`covenant-review-${deal.deal_id}`}>
                          Trigger Covenant Review
                        </button>
                      )}
                      {/* Request Updated Data — available for ALL deployed deals, proactively */}
                      <button
                        onClick={() => openAction("request_data", deal)}
                        className="text-[10px] font-bold px-2.5 py-1.5 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors"
                        data-testid={`request-data-${deal.deal_id}`}>
                        Request Updated Data
                      </button>
                      {mon.status === "watchlist" && (
                        <button className="text-[10px] font-bold px-2.5 py-1.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">
                          Move to Watchlist
                        </button>
                      )}
                      {highAlerts.length > 0 && (
                        <button
                          onClick={() => openAction("escalate", deal)}
                          className="text-[10px] font-bold px-2.5 py-1.5 bg-slate-700 text-white rounded-lg hover:bg-slate-900 transition-colors"
                          data-testid={`escalate-${deal.deal_id}`}>
                          Escalate to Credit Committee
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Action Modal — Covenant Review / Request Data / Escalate */}
      {actionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="monitoring-action-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-[480px] overflow-hidden">
            <div className="bg-[#0F172A] px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-[9px] text-teal-400 font-bold uppercase tracking-[0.15em]">Portfolio Monitoring Action</p>
                <p className="text-sm font-bold text-white mt-0.5">
                  {actionModal.type === "covenant_review" ? "Trigger Covenant Review"
                   : actionModal.type === "request_data"  ? "Request Updated Data"
                   : "Escalate to Credit Committee"}
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">{actionModal.deal.deal_id}</p>
              </div>
              <button onClick={() => setActionModal(null)} className="text-slate-500 hover:text-white transition-colors"><X size={16}/></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Urgency selector */}
              <div>
                <p className="text-[10px] font-bold text-slate-600 mb-1.5">Urgency</p>
                <div className="flex gap-2">
                  {(actionModal.type === "escalate" ? ["standard","urgent","critical"] : ["standard","urgent"]).map(u => (
                    <button key={u} onClick={() => setActionUrgency(u)}
                      className={`text-[9px] font-bold px-3 py-1.5 rounded-lg capitalize transition-colors ${actionUrgency===u ? (u==="critical"?"bg-red-600 text-white":u==="urgent"?"bg-amber-600 text-white":"bg-teal-600 text-white") : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                      {u}
                    </button>
                  ))}
                </div>
              </div>
              {/* Data items (request_data only) */}
              {actionModal.type === "request_data" && (
                <div>
                  <p className="text-[10px] font-bold text-slate-600 mb-1.5">Data Items Required</p>
                  {dataItems.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 mb-1">
                      <input value={item} onChange={e => setDataItems(prev => prev.map((v,j) => j===i ? e.target.value : v))}
                        className="flex-1 text-[10px] px-2 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-teal-400"/>
                      <button onClick={() => setDataItems(prev => prev.filter((_,j) => j!==i))} className="text-slate-400 hover:text-red-500"><X size={12}/></button>
                    </div>
                  ))}
                  <button onClick={() => setDataItems(prev => [...prev, ""])} className="text-[9px] text-teal-600 font-bold mt-1">+ Add item</button>
                </div>
              )}
              {/* Note / reason */}
              <div>
                <p className="text-[10px] font-bold text-slate-600 mb-1.5">
                  {actionModal.type === "escalate" ? "Escalation Reason (required)" : "Note (optional)"}
                </p>
                <textarea value={actionNote} onChange={e => setActionNote(e.target.value)} rows={3}
                  placeholder={actionModal.type === "escalate" ? "Describe the specific covenant breach or risk requiring committee review..." : "Add context for this action..."}
                  className="w-full text-[10px] px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 resize-none"
                  data-testid="action-note-input"/>
              </div>
            </div>
            <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-between">
              <button onClick={() => setActionModal(null)} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
              <button onClick={submitAction} disabled={actionLoading}
                className={`flex items-center gap-2 px-5 py-2 text-white text-xs font-bold rounded-xl disabled:opacity-40 transition-colors ${actionModal.type === "escalate" ? "bg-slate-800 hover:bg-slate-900" : actionModal.type === "request_data" ? "bg-amber-600 hover:bg-amber-700" : "bg-red-600 hover:bg-red-700"}`}
                data-testid="action-confirm-btn">
                {actionLoading ? <RefreshCw size={12} className="animate-spin"/> : null}
                {actionLoading ? "Processing..." :
                  actionModal.type === "covenant_review" ? "Trigger Covenant Review" :
                  actionModal.type === "request_data"    ? "Send Data Request" :
                  "Escalate to Committee"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── GOVERNANCE CONFIG SECTION ─────────────────────────────────────────────────
function SectionGovernanceConfig() {
  const [currentConfig, setCurrentConfig] = useState(null);
  const [presets, setPresets] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ preset: "dfi_conservative", rationale: "" });
  const [customForm, setCustomForm] = useState({
    sector_concentration_warning_pct: 35,
    sector_concentration_block_pct: 55,
    minimum_signal_score: 55,
    board_ic_threshold_usd: 3000000,
  });

  useEffect(() => {
    client.get("/fi/governance-config/current").then(r => setCurrentConfig(r.data)).catch(() => {});
    client.get("/fi/governance-config/presets").then(r => setPresets(r.data?.presets)).catch(() => {});
  }, []);

  const saveConfig = async (usePreset) => {
    setSaving(true);
    try {
      const payload = usePreset
        ? { preset: form.preset, rationale: form.rationale }
        : { ...customForm, rationale: form.rationale };
      const res = await client.post("/fi/governance-config/set", payload);
      setCurrentConfig({ config: res.data.config });
      toast.success("Governance config updated.");
    } catch (e) { toast.error("Failed to update config."); }
    setSaving(false);
  };

  return (
    <SectionFrame icon={SlidersHorizontal} title="Governance Configuration" desc="FI-specific risk appetite settings. Customize concentration limits, pricing premiums, and approval thresholds.">
      <div className="grid grid-cols-2 gap-5">
        {/* Current config */}
        <div className="space-y-4">
          {currentConfig?.config && (
            <SectionCard title={`Active: ${currentConfig.config.name}`} subtitle={currentConfig.config.description}>
              <div className="space-y-2">
                {[
                  { k: "sector_concentration_warning_pct", l: "Sector warning threshold" },
                  { k: "sector_concentration_block_pct", l: "Sector block threshold" },
                  { k: "country_concentration_alert_pct", l: "Country concentration limit" },
                  { k: "minimum_signal_score", l: "Minimum signal score required" },
                  { k: "minimum_mrv_readiness_pct", l: "Minimum MRV readiness" },
                ].map(({k, l}) => (
                  <div key={k} className="flex justify-between text-xs py-1 border-b border-slate-50">
                    <span className="text-slate-500">{l}</span>
                    <span className="font-semibold text-slate-900">{currentConfig.config[k]}{k.endsWith("pct") || k.endsWith("score") || k.endsWith("readiness") ? k.includes("score") ? "/100" : "%" : ""}</span>
                  </div>
                ))}
                <div className="flex justify-between text-xs py-1">
                  <span className="text-slate-500">Board/IC required above</span>
                  <span className="font-semibold text-slate-900">USD {(currentConfig.config?.approval_thresholds?.board_ic_required_above_usd || 0).toLocaleString()}</span>
                </div>
              </div>
            </SectionCard>
          )}

          <SectionCard title="Available Presets" noPadding>
            {presets && Object.entries(presets).map(([key, p]) => (
              <button key={key} onClick={() => setForm({...form, preset: key})}
                className={`w-full text-left px-5 py-3 border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors ${form.preset === key ? "bg-teal-50" : ""}`}>
                <p className="text-xs font-semibold text-slate-900">{p.name}</p>
                <div className="flex gap-3 mt-1 text-xs text-slate-400">
                  <span>Sector: {p.key_thresholds?.sector_warning}</span>
                  <span>Signal: {p.key_thresholds?.min_signal}</span>
                  <span>Board: {p.key_thresholds?.board_threshold}</span>
                </div>
              </button>
            ))}
          </SectionCard>
        </div>

        {/* Config form */}
        <div className="space-y-4">
          <SectionCard title="Apply Preset">
            <div className="mb-3">
              <label className="block text-xs font-medium text-slate-600 mb-1">Rationale (required for audit trail)</label>
              <textarea value={form.rationale} onChange={e => setForm({...form, rationale: e.target.value})}
                rows={3} placeholder="Describe why this configuration is appropriate for your institution..."
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl resize-none" />
            </div>
            <button onClick={() => saveConfig(true)} disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-xl hover:bg-slate-800">
              {saving ? <RefreshCw size={13} className="animate-spin" /> : <SlidersHorizontal size={13} />}
              Apply {presets?.[form.preset]?.name || "Preset"}
            </button>
          </SectionCard>

          <SectionCard title="Custom Overrides">
            <div className="grid grid-cols-2 gap-3 mb-3">
              {[
                { key: "sector_concentration_warning_pct", label: "Sector warning (%)" },
                { key: "sector_concentration_block_pct", label: "Sector block (%)" },
                { key: "minimum_signal_score", label: "Min signal score" },
                { key: "board_ic_threshold_usd", label: "Board required (USD)" },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-xs font-medium text-slate-600 mb-1">{f.label}</label>
                  <input type="number" value={customForm[f.key]} onChange={e => setCustomForm({...customForm, [f.key]: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl" />
                </div>
              ))}
            </div>
            <button onClick={() => saveConfig(false)} disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-slate-300 text-slate-600 text-xs font-medium rounded-xl hover:bg-slate-50">
              Apply Custom Overrides
            </button>
          </SectionCard>
        </div>
      </div>
    </SectionFrame>
  );
}

// ── MODE 2: Stress Testing ─────────────────────────────────────────────────
// Section 3: Scenario Analysis — Forward-Looking Decision Layer
function SectionStressTesting() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeScenario, setActiveScenario] = useState("netzero");

  useEffect(() => {
    client.get("/fi/decisions/deals")
      .then(r => setDeals(r.data.deals || []))
      .catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  // Sector risk per scenario (deterministic, rules-based)
  const SCENARIOS = {
    netzero: {
      key: "netzero",
      label: "Net Zero 2050",
      description: "Rapid, deep decarbonisation across all sectors by 2050. Strong carbon pricing, technology shifts, demand changes.",
      highRiskSectors: ["cement","transport","manufacturing"],
      medRiskSectors: ["energy","waste"],
      repricing: "+0.75–1.25%",
      riskLevel: "High",
      monitoring: "Enhanced — monthly or quarterly",
      opportunity: "Strong for biogas, renewables, waste-to-energy",
      action: "Review covenant packages for cement and transport deals. Enhanced monitoring for all high-risk sector deals. Consider repricing trigger clauses.",
      color: "text-red-700", bg: "bg-red-50 border-red-200",
    },
    delayed: {
      key: "delayed",
      label: "Delayed Transition",
      description: "Slower policy action, late market shifts. Short-term lower risk but longer-term stranded asset exposure.",
      highRiskSectors: [],
      medRiskSectors: ["cement","transport","manufacturing"],
      repricing: "+0.25–0.50%",
      riskLevel: "Medium",
      monitoring: "Standard — quarterly",
      opportunity: "Limited near-term disruption, but reduced green premium",
      action: "Monitor sector exposure. Prepare covenant adjustment framework. No immediate repricing required.",
      color: "text-amber-700", bg: "bg-amber-50 border-amber-200",
    },
    "2deg": {
      key: "2deg",
      label: "Below 2°C (Orderly)",
      description: "Gradual, well-managed transition. Policy and markets align. Predictable decarbonisation pathway.",
      highRiskSectors: [],
      medRiskSectors: ["cement"],
      repricing: "Minimal — <0.25%",
      riskLevel: "Low–Medium",
      monitoring: "Standard",
      opportunity: "Strong for all climate finance products — green loan demand rises",
      action: "No immediate action required. Standard monitoring. Optimise for green premium in next deal cycle.",
      color: "text-teal-700", bg: "bg-teal-50 border-teal-200",
    },
  };

  const activeDeals = deals.filter(d => !["rejected"].includes(d.status));
  const totalAmt = activeDeals.reduce((s,d)=>s+(d.deployed_amount_usd||0),0) || 1;

  // Compute affected exposure per scenario
  const getScenarioExposure = (sc) => {
    const highSectors = sc.highRiskSectors || [];
    const medSectors  = sc.medRiskSectors  || [];
    const highAmt = activeDeals.filter(d=>highSectors.includes(d.sector)).reduce((s,d)=>s+(d.deployed_amount_usd||0),0);
    const medAmt  = activeDeals.filter(d=>medSectors.includes(d.sector)).reduce((s,d)=>s+(d.deployed_amount_usd||0),0);
    return { highAmt, medAmt, highPct: Math.round(highAmt/totalAmt*100), medPct: Math.round(medAmt/totalAmt*100) };
  };

  const selected = SCENARIOS[activeScenario];

  return (
    <div className="space-y-5" data-testid="section-stress-testing">
      {/* Directional label — honest disclaimer */}
      <div className="flex items-start gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl">
        <Info size={12} className="text-blue-500 flex-shrink-0 mt-0.5"/>
        <p className="text-[11px] text-blue-700">
          <strong>Directional Scenario Analysis — Scenario-Informed Planning.</strong> This analysis provides qualitative signals for portfolio planning and committee discussion.
          For regulatory-grade stress testing (TCFD, Basel, ECB), a full quantitative climate risk model is required.
        </p>
      </div>

      {/* Scenario Comparison Table */}
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Scenario Comparison — Portfolio Impact Overview</p>
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-2.5 text-slate-500 font-semibold">Scenario</th>
                <th className="text-left px-4 py-2.5 text-slate-500 font-semibold">Most Exposed Sectors</th>
                <th className="text-center px-3 py-2.5 text-slate-500 font-semibold">Risk Level</th>
                <th className="text-left px-3 py-2.5 text-slate-500 font-semibold">Repricing Pressure</th>
                <th className="text-left px-4 py-2.5 text-slate-500 font-semibold">Monitoring</th>
                <th className="text-left px-4 py-2.5 text-slate-500 font-semibold">Recommended Action</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(SCENARIOS).map((sc, i) => {
                const exp = getScenarioExposure(sc);
                const isActive = activeScenario === sc.key;
                const RISK_STYLE = { "High":"bg-red-100 text-red-700", "Medium":"bg-amber-100 text-amber-700", "Low–Medium":"bg-teal-100 text-teal-700" };
                return (
                  <tr key={sc.key}
                    onClick={() => setActiveScenario(sc.key)}
                    className={`border-b border-slate-100 last:border-0 cursor-pointer transition-colors ${isActive?"bg-teal-50":i%2===1?"bg-slate-50/30":"hover:bg-slate-50"}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isActive && <div className="w-1.5 h-1.5 bg-teal-500 rounded-full flex-shrink-0"/>}
                        <p className="font-bold text-slate-900">{sc.label}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {[...sc.highRiskSectors,...sc.medRiskSectors].filter(s=>activeDeals.some(d=>d.sector===s)).join(", ") || "None in current portfolio"}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${RISK_STYLE[sc.riskLevel]||"bg-slate-100 text-slate-600"}`}>{sc.riskLevel}</span>
                    </td>
                    <td className={`px-3 py-3 text-[11px] font-semibold ${sc.riskLevel==="High"?"text-red-600":sc.riskLevel==="Medium"?"text-amber-600":"text-emerald-600"}`}>{sc.repricing}</td>
                    <td className="px-4 py-3 text-slate-600">{sc.monitoring}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-[200px] text-[11px]">{sc.action}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Selected Scenario Deep Dive */}
      {selected && (
        <div className={`border-2 rounded-xl p-5 ${selected.bg}`}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${selected.color}`}>{selected.label}</p>
              <p className="text-sm text-slate-700 leading-relaxed">{selected.description}</p>
            </div>
            {!loading && (
              <div className="text-right flex-shrink-0 ml-4">
                <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-1">Portfolio at Risk</p>
                <p className={`text-lg font-bold ${selected.color}`}>${(getScenarioExposure(selected).highAmt/1e6).toFixed(1)}M</p>
                <p className="text-[10px] text-slate-500">{getScenarioExposure(selected).highPct}% in high-risk sectors</p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <div><p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Strategic Opportunity</p><p className="text-xs text-slate-700">{selected.opportunity}</p></div>
            <div><p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Recommended Action</p><p className="text-xs text-slate-700">{selected.action}</p></div>
            <div><p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Repricing Pressure</p><p className={`text-xs font-bold ${selected.color}`}>{selected.repricing}</p></div>
          </div>
        </div>
      )}

      {/* Affected Deals Under Selected Scenario */}
      {!loading && activeDeals.length > 0 && selected && (
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
            Deals Affected — {selected.label}
          </p>
          <div className="space-y-2">
            {activeDeals.filter(d => [...(selected.highRiskSectors||[]),...(selected.medRiskSectors||[])].includes(d.sector)).map(deal => {
              const isHigh = selected.highRiskSectors?.includes(deal.sector);
              return (
                <div key={deal.deal_id} className={`flex items-center gap-3 border rounded-xl px-4 py-2.5 ${isHigh?"bg-red-50 border-red-200":"bg-amber-50 border-amber-200"}`}>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${isHigh?"bg-red-100 text-red-700":"bg-amber-100 text-amber-700"}`}>{isHigh?"High":"Medium"}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-900 truncate">{_parseName(deal)}</p>
                    <p className="text-[10px] text-slate-500">{deal.sector} · {deal.stage_label || deal.status}</p>
                  </div>
                  <span className="text-xs text-slate-600 flex-shrink-0">{selected.repricing}</span>
                </div>
              );
            })}
            {activeDeals.filter(d => [...(selected.highRiskSectors||[]),...(selected.medRiskSectors||[])].includes(d.sector)).length === 0 && (
              <div className="flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 px-4 py-3 rounded-xl">
                <CheckCircle2 size={12}/> No deals in affected sectors under this scenario — portfolio is well-positioned
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── MODE 2: Portfolio Strategy ────────────────────────────────────────────
function SectionPortfolioStrategy() {
  return (
    <SectionFrame icon={TrendingDown} title="Portfolio Strategy" desc="Priority sectors, reduction targets, and capital allocation guidance.">
      <div className="space-y-3">
        {[
          { sector: "Clean Energy",    action: "Increase", note: "Solar, biogas, wind — strong pipeline available",             color: "bg-emerald-50 border-emerald-200" },
          { sector: "Industrial Transition", action: "Target", note: "Cement, manufacturing — large deals, longer structuring", color: "bg-teal-50 border-teal-200"     },
          { sector: "Waste & Circular", action: "Grow",   note: "Biogas and recycling — high carbon value, SME + corporate",   color: "bg-blue-50 border-blue-200"     },
          { sector: "Transport",        action: "Explore", note: "Fleet conversion, EVs — emerging pipeline",                  color: "bg-amber-50 border-amber-200"   },
        ].map(s => (
          <div key={s.sector} className={`rounded-xl border p-4 ${s.color}`}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-bold text-slate-900">{s.sector}</p>
              <span className="text-xs font-bold text-slate-600">{s.action}</span>
            </div>
            <p className="text-xs text-slate-500">{s.note}</p>
          </div>
        ))}
        <p className="text-xs text-slate-400 italic">
          Strategy guidance is indicative based on platform pipeline data. Actual allocation decisions are made by your investment committee.
        </p>
      </div>
    </SectionFrame>
  );
}

// ── MODE 2: Compliance & Reporting ────────────────────────────────────────
// ── COMPLIANCE STATUS DASHBOARD ──────────────────────────────────────────
function SectionCompliance() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/fi/decisions/deals").then(r => setDeals(r.data.deals || [])).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-10"><RefreshCw size={14} className="animate-spin text-teal-500"/></div>;

  const active = deals.filter(d => !["rejected"].includes(d.status));
  const scored = active.filter(d => _parseScore(d) !== null);
  const eligible = active.filter(d => ["eligible","conditional"].includes(_parseElig(d)));
  const highEvidence = active.filter(d => (d.evidence_readiness_pct || 0) >= 60);
  const deployed = deals.filter(d => d.status === "financial_close");

  // Compute compliance % per framework
  const FRAMEWORKS = [
    {
      key:"ifrs_s2",
      label:"IFRS S2",
      std:"Climate-Related Disclosures (2023)",
      pct: Math.min(100, Math.round((scored.length/Math.max(1,active.length)*40) + (highEvidence.length/Math.max(1,active.length)*30) + (deployed.length > 0 ? 30 : 0))),
      gaps: [
        scored.length < active.length && `Signal Score missing for ${active.length - scored.length} deal${active.length-scored.length>1?"s":""}`,
        highEvidence.length < active.length && `MRV/evidence incomplete for ${active.length - highEvidence.length} deal${active.length-highEvidence.length>1?"s":""}`,
        !active.some(d => d.sector === "cement") && false,
      ].filter(Boolean),
      actions: ["Run Signal Score for all active deals","Strengthen MRV validation before next reporting cycle"],
    },
    {
      key:"tcfd",
      label:"TCFD",
      std:"Task Force on Climate-Related Financial Disclosures",
      pct: Math.min(100, Math.round(60 + (scored.length/Math.max(1,active.length)*20) + (active.length > 0 ? 20 : 0))),
      gaps: ["Physical risk assessment not yet quantified per deal","Forward-looking scenario analysis (FY2030 targets) not defined"],
      actions: ["Complete scenario analysis in Climate Risk module","Quantify physical risk per deal in Portfolio Exposure"],
    },
    {
      key:"pcaf",
      label:"PCAF",
      std:"Partnership for Carbon Accounting Financials (2020)",
      pct: Math.min(100, Math.round((deployed.length > 0 ? 40 : 20) + (highEvidence.length/Math.max(1,active.length)*40) + (scored.length > 0 ? 20 : 0))),
      gaps: [
        deployed.length === 0 && "No deployed capital — PCAF Scope 3 Cat.15 applies to disbursed loans only",
        highEvidence.length < active.length && `PCAF data quality limited for ${active.length-highEvidence.length} deal${active.length-highEvidence.length>1?"s":""}`,
      ].filter(Boolean),
      actions: ["Improve evidence readiness to achieve PCAF Tier 1 quality","Generate PCAF report from Portfolio Reports section"],
    },
    {
      key:"taxonomy",
      label:"EU Taxonomy",
      std:"EU Taxonomy Regulation — Climate Objectives",
      pct: Math.min(100, Math.round((eligible.length/Math.max(1,active.length)*50) + (highEvidence.length/Math.max(1,active.length)*30) + (scored.length > 0 ? 20 : 0))),
      gaps: [
        active.length - eligible.length > 0 && `${active.length - eligible.length} deal${active.length-eligible.length>1?"s":""} without clear eligibility — DNSH validation incomplete`,
        "EU Taxonomy Article 6/7/8 alignment not yet assessed per deal",
      ].filter(Boolean),
      actions: ["Complete Eligibility Engine for all active deals","Map each deal to EU Taxonomy environmental objectives (climate mitigation)"],
    },
  ];

  const getColor = (pct) => pct >= 80 ? "text-emerald-700" : pct >= 60 ? "text-amber-700" : "text-red-600";
  const getBar   = (pct) => pct >= 80 ? "bg-emerald-500" : pct >= 60 ? "bg-amber-500" : "bg-red-400";
  const getLabel = (pct) => pct >= 80 ? "Substantially Aligned" : pct >= 60 ? "Partially Aligned" : "Incomplete";

  return (
    <div className="space-y-5" data-testid="section-compliance">
      <div>
        <h2 className="text-base font-bold text-slate-900">Compliance Status Dashboard</h2>
        <p className="text-xs text-slate-500 mt-0.5">Computed from live platform data — no manual input. Completion % reflects data availability and assessment coverage.</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {FRAMEWORKS.map(fw => (
          <div key={fw.key} className={`border rounded-xl overflow-hidden ${fw.pct>=80?"border-emerald-200":fw.pct>=60?"border-amber-200":"border-red-200"}`}>
            <div className={`px-5 py-3 border-b ${fw.pct>=80?"bg-emerald-50":fw.pct>=60?"bg-amber-50":"bg-red-50"}`}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-bold text-slate-900">{fw.label}</p>
                <p className={`text-2xl font-bold tabular-nums ${getColor(fw.pct)}`}>{fw.pct}%</p>
              </div>
              <div className="h-2 bg-white/60 rounded-full overflow-hidden mb-1">
                <div className={`h-full rounded-full ${getBar(fw.pct)}`} style={{width:`${fw.pct}%`}}/>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-slate-500">{fw.std}</p>
                <span className={`text-[9px] font-bold ${getColor(fw.pct)}`}>{getLabel(fw.pct)}</span>
              </div>
            </div>
            <div className="px-5 py-3 bg-white">
              {fw.gaps.length > 0 && (
                <div className="mb-2">
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Gaps</p>
                  {fw.gaps.map((g,i)=>(
                    <div key={i} className="flex items-start gap-1.5 text-[10px] text-slate-600 mb-1">
                      <span className="text-amber-500 flex-shrink-0">→</span>{g}
                    </div>
                  ))}
                </div>
              )}
              {fw.actions.length > 0 && (
                <div>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Required Actions</p>
                  {fw.actions.map((a,i)=>(
                    <div key={i} className="flex items-start gap-1.5 text-[10px] text-teal-700 mb-1">
                      <CheckCircle2 size={9} className="flex-shrink-0 mt-0.5"/>{a}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── MAIN FI WORKSPACE ─────────────────────────────────────────────────────
export default function FIWorkspace() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeModule  = searchParams.get('m') || 'dashboard';
  const activeSection = searchParams.get('s') || 'dashboard';

  // Unified navigation — updates URL params (sidebar and content stay in sync)
  const handleNavTo = useCallback((modKey, secKey) => {
    setSearchParams({ m: modKey, s: secKey });
  }, [setSearchParams]);

  // Section-only navigation — finds and sets the parent module automatically
  const navToSection = useCallback((secKey) => {
    const mod = FI_MODULES.find(m => m.sections.some(s => s.key === secKey));
    if (mod) setSearchParams({ m: mod.key, s: secKey });
  }, [setSearchParams]);

  const renderSection = () => {
    switch (activeSection) {
      // Module 1: Executive Dashboard
      case "dashboard":     return <SectionExecutiveDashboard onNav={handleNavTo} />;
      // Module 2: Deal Origination
      case "pipeline":      return <SectionPipelineMain onSection={navToSection} />;
      case "create_deal":   return <SectionDealOrigination />;
      case "data_import":   return <SectionDataImport />;
      // Module 3: Eligibility & Screening
      case "screening":     return <SectionScreening onSection={navToSection} />;
      case "eligibility":   return <SectionEligibility onSection={navToSection} />;
      case "signal_score":  return <SectionSignalScore />;
      // Module 4: Credit Engine
      case "financing":     return <SectionFinancingStructure />;
      case "conditions":    return <SectionConditionsTracker />;
      case "decision":      return <SectionDealActions />;
      case "credit_memo":   return <SectionCreditMemo />;
      // Module 5: Portfolio & Monitoring
      case "analytics":     return <SectionAnalytics />;
      case "portfolio_intel": return <SectionPortfolioIntelligence />;
      case "monitoring":    return <SectionPostDisbursement />;
      // Module 6: Climate Risk & Scenarios
      case "risk":          return <SectionClimateRisk />;
      case "exposure":      return <SectionPortfolioExposure />;
      case "stress":        return <SectionStressTesting />;
      // Module 7: Carbon Desk
      case "carbon":        return <SectionCarbonStructuring navigate={navigate} />;
      case "vcm_desk":      return <SectionVCMDesk />;
      // Module 8: Reporting & Disclosure
      case "regulatory":    return <SectionRegulatoryMapping />;
      case "gov_config":    return <SectionGovernanceConfig />;
      case "rbac":          return <SectionRBAC />;
      case "compliance":    return <SectionCompliance />;
      // Module 9: Strategic Layer
      case "syndication":   return <SectionSyndication />;
      case "coinvestment":  return <SectionCoInvestment />;
      case "poa_desk":      return <SectionPoADesk />;
      default:              return <SectionExecutiveDashboard onNav={handleNavTo} />;
    }
  };

  // No wrapper div needed — Layout.js main handles the container
  return (
    <>
      <OnboardingOverlay workspace="fi" />
      <div className="px-6 py-6" data-testid="fi-workspace">
        {renderSection()}
      </div>
    </>
  );
}
